import { AfterViewInit, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { DatePipe, ViewportScroller } from '@angular/common';
import { InvoiceV2Service } from '../../../services/invoice-v2.service';
import { CurrencyService } from '../../../services/currency.service';
import { HttpHelpService } from '../../../Data/service/http-help.service';
import { CompaniesService } from '../../../components/alldocuments/alldocuments-companies/companies.service';
import { companyDisplayName } from '../../../shared/utils/company-display-name';
import { InvoiceSubscriptionsV2Service } from '../../../services/invoice-subscriptions-v2.service';
import {
  CreateInvoiceV2Dto,
  UpdateInvoiceV2Dto,
  CreateSoldItemV2Dto,
  PaymentMethodDto,
  InvoiceType,
  PaymentType,
  CreditCardType,
  CreditCardPaymentType,
  InvoiceStatusCode,
  MultiLangMessagePayload,
  StoredPaymentStatus,
  InvoiceFieldError,
} from '../../../models/invoice-v2.model';
import { InvoiceV2ValidationService } from '../services/invoice-v2-validation.service';
import { getDefaultDocumentLanguage } from 'src/app/shared/services/document-language-preference';
import {
  InvoiceV2LocalDraft,
  InvoiceV2LocalDraftService,
  InvoiceV2LocalDraftSummary,
} from '../services/invoice-v2-local-draft.service';
import { InvoiceV2FormCanDeactivate } from '../guards/invoice-v2-unsaved.guard';
import Swal from 'sweetalert2';
import {
  forkJoin,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  Subject,
  Subscription,
  Observable,
  switchMap,
} from 'rxjs';
import { ExchangeConvertResponseData } from '../../../models/currency.model';
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { environment } from 'src/environments/environment';
import { openCustomerWorkerNumberSwal } from '../worker-number-swal.util';
import { openTaxAuthorityLinkSwal } from '../tax-authority-link-swal.util';
import { isProcedureCycleConfirmResponse, normalizeProcedureInvoiceRow } from '../invoice-v2-procedure.util';
import {
  formatDueDateForApi,
  isPaymentTrackingInvoiceType,
  parseDueDateFromApi,
  resolvePaymentFieldsForSubmit,
} from '../invoice-v2-payment.util';
import {
  CurrencyDto,
  DEFAULT_CURRENCY_CODE,
} from '../../../models/currency.model';
import {
  buildForeignCurrencyPayload,
  buildForeignToIlsConvertRequest,
  convertForeignToIls,
  convertIlsToForeign,
  getCurrencyDisplayName,
  hasSoldItemForeignAmounts,
  isForeignCurrency,
  mapItemForSubmitWithCurrency,
  readSoldItemForeignFromApi,
  resolveForeignCurrencyFromInvoice,
  resolveDocumentCurrency,
} from '../invoice-v2-currency.util';

/**
 * Category A — sources that may be merged INTO a Category B draft:
 * offer price (0), order / مطالبة (100), mission / إرسالية (200), financial request / مطالبة مالية (332).
 * Category B targets are tax invoice (305) and invoice+receipt (320).
 */
const CREATE_MERGE_ALLOWED_SOURCE_INVOICE_TYPES = new Set<number>([
  InvoiceType.OFFER_PRICE,
  InvoiceType.ORDER,
  InvoiceType.MISSION,
  InvoiceType.FINANCIAL_REQUEST,
]);

/** Legacy guard: excluded types are never offered even if mis-tagged in API responses. */
const CREATE_MERGE_EXCLUDED_INVOICE_TYPES = new Set([
  InvoiceType.TAX_INVOICE,
  InvoiceType.INVOICE_WITH_RECEIPT,
  InvoiceType.REVERSE_INVOICE,
  InvoiceType.RECEIPT,
  InvoiceType.CANCELLED_RECEIPT,
]);

/** After merge, draft type must be one of these (وارث). */
const CREATE_MERGE_ALLOWED_TARGET_INVOICE_TYPES = new Set<number>([
  InvoiceType.TAX_INVOICE,
  InvoiceType.INVOICE_WITH_RECEIPT,
]);

@Component({
  selector: 'app-invoices-v2-form',
  templateUrl: './invoices-v2-form.component.html',
  styleUrls: ['./invoices-v2-form.component.scss'],
  providers: [DatePipe],
})
export class InvoicesV2FormComponent implements OnInit, AfterViewInit, OnDestroy, InvoiceV2FormCanDeactivate {
  curentLang: string = '';
  merchantId: number;
  user_type: number;
  isEditMode: boolean = false;
  invoiceId: number | null = null;
  /** From server when invoice was loaded; PUT allocation-number is only valid when this is 0. */
  private invoiceConfirmFromServer: 0 | 1 = 0;

  // Form
  invoiceForm: FormGroup;

  // Data
  companies: any[] = [];
  customers: any[] = [];
  products: any[] = [];
  categories: any[] = [];
  measurementUnits: any[] = [];
  /** Master list from API (minus cancelled receipt); filtered per company into `invoiceTypes`. */
  allInvoiceTypes: any[] = [];
  invoiceTypes: any[] = [];

  /**
   * When API omits companyType.invoice_types, same fallback as CompaniesService.getCompanyInvoiceTypes.
   */
  private static readonly DEFAULT_COMPANY_INVOICE_TYPE_CODES = [
    305, 400, 330, 320, 332, 100, 200, 0,
  ];

  /**
   * Company types that cannot send to the tax authority from invoice v2:
   * 3 = nonprofit (מלכ"ר / جمعية), 4 = exempt dealer (עוסק פטور / معفى).
   */
  private static readonly COMPANY_TYPE_IDS_NO_TAX_AUTHORITY = new Set([3, 4]);
  paymentMethodsList: any[] = []; // List of available payment methods from API
  banks: any[] = []; // List of banks from backend
  banksLoading = false;
  banksLoadError = false;
  bankBranchesByCode: { [bankCode: string]: any[] } = {}; // Cache of branches per bank code
  // Credit card names list (used for credit_card_name dropdown)
  creditCardTypes = [
    { value: 'Isracard', label: 'Isracard' },
    { value: 'Cal', label: 'Cal' },
    { value: 'Diners', label: 'Diners' },
    { value: 'American Express', label: 'American Express' },
    { value: 'Leumi Card', label: 'Leumi Card' },
    { value: 'Not Credit Card', label: 'Not Credit Card' },
  ];

  // Credit payment types (used for credit_type dropdown)
  creditPaymentTypes = [
    { value: CreditCardPaymentType.NORMAL, label: 'Normal' },
    { value: CreditCardPaymentType.PAYMENTS, label: 'Payments' },
    { value: CreditCardPaymentType.CREDIT, label: 'Credit' },
    { value: CreditCardPaymentType.DEFERRED_PAYMENT, label: 'Deferred Payment' },
    { value: CreditCardPaymentType.OTHERS, label: 'Others' },
  ];

  // Add customer modal (same as old invoice CRUD)
  showAddCustomerModal: boolean = false;
  @ViewChild('customerSelect') customerSelect?: NgSelectComponent;

  /** Add product modal — item row index to select the new product on save */
  showAddProductModal = false;
  private addProductItemIndex: number | null = null;

  /** Compact items accordion — only one line expanded at a time */
  expandedItemIndex: number | null = null;

  /** Row index whose product ng-select is open (stacking context; avoids CSS :has reflow). */
  productSelectOpenIndex: number | null = null;

  /** Catalog: small first page for fast open, then background pages. */
  productsLoading = false;
  private readonly productLoad$ = new Subject<{
    companyId: number;
    selectProductId?: number;
    itemIndex?: number;
    clearInvalidLineProducts: boolean;
    fallbackProduct?: any;
  }>();
  private readonly productOverflowSearch$ = new Subject<string>();
  private productLoadSub: Subscription | null = null;
  private productOverflowSub: Subscription | null = null;
  private productBackgroundFillSub: Subscription | null = null;
  private loadedProductsCompanyId: number | null = null;
  private pendingProductsCompanyId: number | null = null;
  /** True when more active products exist than we currently hold. */
  private productCatalogTruncated = false;

  /** First paint — dropdown must work without waiting for the full catalog. */
  private readonly PRODUCT_FIRST_PAGE = 80;
  /** Max products kept in memory for local filtering. */
  private readonly PRODUCT_CATALOG_LIMIT = 2000;
  /** Background page size after first paint. */
  private readonly PRODUCT_PAGE_SIZE = 200;
  /** Server matches when typed term is missing from pages already loaded. */
  private readonly PRODUCT_OVERFLOW_SEARCH_LIMIT = 50;

  /** Brief highlight on payment row after user adds a new method */
  highlightedPaymentIndex: number | null = null;
  private highlightPaymentTimer: ReturnType<typeof setTimeout> | null = null;

  /** Active validation errors from the last submit attempt */
  activeValidationErrors: InvoiceFieldError[] = [];
  activeValidationFieldPaths = new Set<string>();
  lastValidationCorrelationId: string | null = null;

  /** Footer submit buttons — prevent double-click while save/confirm is in flight */
  isSubmitting = false;
  submittingAsConfirm: boolean | null = null;

  /** Create flow: after customer select — merge confirmed orders / missions / financial requests only */
  showCreateMergeModal = false;
  createMergeLoading = false;
  createMergeInvoices: any[] = [];
  createMergeSelectedIds: number[] = [];

  /** Create-merge list row: HTML preview popup */
  showCreateMergePreviewModal = false;
  createMergePreviewLoading = false;
  createMergePreviewUrl: SafeResourceUrl | null = null;
  private createMergePreviewBlobUrl: string | null = null;
  createMergePreviewInvoiceId: number | null = null;
  createMergePreviewInvoiceLabel = '';
  /** Row currently being excluded from merge list (PATCH mark-merged). */
  createMergeExcludingId: number | null = null;

  /** Create mode: show merge button only when API finds ≥1 mergeable confirmed doc for customer+company */
  createMergeEligibleCount = 0;
  private createMergeEligibilityChecked = false;
  private createMergeEligibilityLoading = false;
  private createMergeEligibilityTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * True while before-VAT total is under yearly max — when it crosses up, we auto-enable
   * tax authority reporting once until the user goes below the max again.
   */
  private yearlyCapBelowThreshold = true;

  /**
   * True while the per-invoice before-VAT amount is at or under the legal threshold (₪5k / ₪10k).
   * When it crosses up on an eligible type (305/320), we auto-enable tax authority reporting.
   */
  private perInvoiceBelowThreshold = true;

  // CKEditor
  public Editor = ClassicEditor;
  
  // Dynamic editor config based on language
  get editorConfig() {
    const isRtl = this.curentLang === 'ar' || this.curentLang === 'he';
    return {
      toolbar: ['bold', 'italic', 'bulletedList', 'numberedList', '|', 'undo', 'redo'],
      language: {
        ui: this.curentLang || 'en',
        content: this.curentLang || 'en'
      },
      contentsLangDirection: isRtl ? 'rtl' : 'ltr',
    };
  }

  selectedCompany: any = null;
  selectedCustomer: any = null;
  predictedDocumentNumber: string = '';

  // VAT
  vatListId: number = 8; // Default VAT list ID
  vatPercentage: number = 0; // Current VAT percentage from backend

  // Payment methods
  paymentMethodsRequired: boolean = false;

  // Flag to prevent product selection during form population
  isPopulatingForm: boolean = false;

  // Collapse state for tax details section
  showTaxDetails: boolean = false;

  /** True until copy-from-invoice population finishes — avoids sequence Swal before copy data is applied. */
  private copyFlowPending = false;

  /** Create mode: prompt document type before filling the form (when URL has no ?type=). */
  showDocumentTypePicker = false;
  private pendingDocumentTypePicker = false;
  private documentTypePickerResolved = false;

  /** Debounced local draft auto-save (create mode only). */
  private localDraftSub: Subscription | null = null;
  private createFormNavSub: Subscription | null = null;
  private createFormScrollResetTimers: ReturnType<typeof setTimeout>[] = [];
  private localDraftRestoreOffered = false;
  private localDraftBypassNavigation = false;
  private skipLocalDraftSave = false;
  private localDraftRestoreDataPending = false;
  private companiesLoadedForDraftRestore = false;
  private invoiceTypesLoadedForDraftRestore = false;

  /** Debounce document sequence check (company + invoice type) — same API as V1 `document-head`. */
  private documentSequenceCheckTimer: ReturnType<typeof setTimeout> | null = null;
  private documentSequenceCheckInFlight = false;

  // Invoice type configuration flags from backend
  hasItemsForSelectedType: boolean = true;

  /**
   * Yearly max total before VAT (עוסק פטור ceiling) from GET yearly-max-amount?year=…
   * Applies to exempt / nonprofit company types: checkbox stays off until line totals reach this.
   */
  yearlyMaxAmountBeforeVat: number | null = null;
  /** Set after first yearly-max-amount response (success or error) for notice copy. */
  yearlyMaxAmountLoaded = false;

  /** Dedupes noisy sync logs while editing line items (dev only). */
  private lastTaxSyncDebugKey = '';

  // Calculated amounts
  calculatedAmounts = {
    /** Sum(line subtotals) — net after per-line discount (same as "after discount" for lines). */
    amount_before_discount: 0,
    /** Gross before any line discount = amount_after_discount + all_discount */
    gross_before_discount: 0,
    all_discount: 0,
    /** Net after discount (sum of subtotals), before VAT lines */
    amount_after_discount: 0,
    payment_amount: 0,
    all_vat_amount: 0,
    payment_amount_including_vat: 0,
    total_amount: 0,
  };

  /** Foreign-currency totals for display and submit (when currency !== ILS) */
  foreignCalculatedAmounts = {
    gross_before_discount: 0,
    all_discount: 0,
    amount_after_discount: 0,
    all_vat_amount: 0,
    payment_amount_including_vat: 0,
  };

  currencies: CurrencyDto[] = [];
  currenciesLoading = false;
  exchangeRateLoading = false;
  foreignConvertLoading = false;
  private skipExchangeRateFetch = false;
  private previousCurrencyCode = DEFAULT_CURRENCY_CODE;
  private foreignConvertTimer: ReturnType<typeof setTimeout> | null = null;
  private foreignConvertSeq = 0;
  private exchangeRateLoadSeq = 0;

  constructor(
    private fb: FormBuilder,
    private invoiceV2Service: InvoiceV2Service,
    private currencyService: CurrencyService,
    private httpHelpService: HttpHelpService,
    private companiesService: CompaniesService,
    private route: ActivatedRoute,
    private router: Router,
    private spinner: NgxSpinnerService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private datePipe: DatePipe,
    private invoiceSubscriptionsV2: InvoiceSubscriptionsV2Service,
    private sanitizer: DomSanitizer,
    private invoiceV2Validation: InvoiceV2ValidationService,
    private localDraftService: InvoiceV2LocalDraftService,
    private cdr: ChangeDetectorRef,
    private viewportScroller: ViewportScroller,
  ) {
    this.curentLang = localStorage.getItem('curentLang') || 'en';
    this.merchantId = JSON.parse(localStorage.getItem('merchant_id') || '0');
    this.user_type = JSON.parse(localStorage.getItem('user_type') || '0');

    // Initialize form
    this.invoiceForm = this.createForm();
  }

  ngOnInit(): void {
    // Check if edit mode
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode = true;
      this.invoiceId = +id;
      this.loadInvoiceForEdit(+id);
    } else {
      this.invoiceConfirmFromServer = 0;
      // Check for copy data from navigation state
      const historyState = history.state as { copyData?: any } | undefined;
      if (historyState?.copyData) {
        this.copyFlowPending = true;
        this.loadInitialData();
        this.setupFormListeners();
        this.setupLocalDraftAutoSave();
        // Wait for initial data to load, then populate copy data
        setTimeout(() => {
          this.populateCopyData(historyState.copyData);
          this.copyFlowPending = false;
          this.refreshYearlyMaxAmount();
          this.scheduleCreateFormScrollReset();
        }, 1000);
        this.subscribeCreateFormNavigationScrollReset();
        return;
      }

      // Pre-select invoice type from ?type= query param (used by merchant-info modal)
      const typeParam = this.route.snapshot.queryParamMap.get('type');
      if (typeParam != null && !isNaN(+typeParam)) {
        const type = +typeParam;
        this.invoiceForm.patchValue({ invoice_type: type }, { emitEvent: false });
        this.onInvoiceTypeChange(type);
        this.documentTypePickerResolved = true;
      } else {
        this.pendingDocumentTypePicker = true;
        this.invoiceForm.patchValue({ invoice_type: null }, { emitEvent: false });
      }

      this.loadInitialData();
      this.setupFormListeners();
      this.setupLocalDraftAutoSave();
      this.localDraftRestoreDataPending = true;
      setTimeout(() => this.refreshYearlyMaxAmount(), 0);
      this.scheduleCreateFormScrollReset();
      this.subscribeCreateFormNavigationScrollReset();
      return;
    }

    this.loadInitialData();
    this.setupFormListeners();
    setTimeout(() => this.refreshYearlyMaxAmount(), 0);
  }

  ngAfterViewInit(): void {
    this.scheduleCreateFormScrollReset();
  }

  ngOnDestroy(): void {
    if (this.createMergeEligibilityTimer) {
      clearTimeout(this.createMergeEligibilityTimer);
      this.createMergeEligibilityTimer = null;
    }
    this.localDraftSub?.unsubscribe();
    this.localDraftSub = null;
    if (this.highlightPaymentTimer) {
      clearTimeout(this.highlightPaymentTimer);
      this.highlightPaymentTimer = null;
    }
    if (this.foreignConvertTimer) {
      clearTimeout(this.foreignConvertTimer);
      this.foreignConvertTimer = null;
    }
    this.productLoadSub?.unsubscribe();
    this.productLoadSub = null;
    this.productOverflowSub?.unsubscribe();
    this.productOverflowSub = null;
    this.productBackgroundFillSub?.unsubscribe();
    this.productBackgroundFillSub = null;
    this.productLoad$.complete();
    this.productOverflowSearch$.complete();
    this.createFormNavSub?.unsubscribe();
    this.createFormNavSub = null;
    this.createFormScrollResetTimers.forEach((t) => clearTimeout(t));
    this.createFormScrollResetTimers = [];
  }

  /** New create screens always start at the top; do not leave the product row over the header. */
  private resetCreateFormScroll(): void {
    if (this.isEditMode) {
      return;
    }
    this.viewportScroller.scrollToPosition([0, 0]);
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    let node: HTMLElement | null = document.querySelector('app-invoices-v2-form');
    while (node) {
      const overflowY = getComputedStyle(node).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && node.scrollHeight > node.clientHeight) {
        node.scrollTop = 0;
      }
      node = node.parentElement;
    }
    document.querySelectorAll('.page-body').forEach((el) => {
      (el as HTMLElement).scrollTop = 0;
    });
  }

  private scheduleCreateFormScrollReset(): void {
    if (this.isEditMode) {
      return;
    }
    this.resetCreateFormScroll();
    const later = (ms: number) => {
      const id = setTimeout(() => this.resetCreateFormScroll(), ms);
      this.createFormScrollResetTimers.push(id);
    };
    later(0);
    later(50);
    later(200);
    later(400);
  }

  private subscribeCreateFormNavigationScrollReset(): void {
    if (this.createFormNavSub || this.isEditMode) {
      return;
    }
    this.createFormNavSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.scheduleCreateFormScrollReset());
  }

  canDeactivateCreateForm(): boolean | Promise<boolean> {
    if (this.isEditMode || this.localDraftBypassNavigation) {
      return true;
    }

    const raw = this.invoiceForm.getRawValue();
    if (!this.localDraftService.isFormValueMeaningful(raw)) {
      return true;
    }

    return Swal.fire({
      icon: 'warning',
      title: this.translate.instant('invoices_v2.form.local_draft_leave_title'),
      text: this.translate.instant('invoices_v2.form.local_draft_leave_text'),
      showCancelButton: true,
      confirmButtonText: this.translate.instant('invoices_v2.form.local_draft_leave_confirm'),
      cancelButtonText: this.translate.instant('invoices_v2.form.local_draft_leave_stay'),
      confirmButtonColor: '#E62E2D',
      reverseButtons: this.curentLang === 'ar' || this.curentLang === 'he',
    }).then((result) => result.isConfirmed);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.isEditMode || this.localDraftBypassNavigation) {
      return;
    }

    this.persistLocalDraftIfNeeded();

    const raw = this.invoiceForm.getRawValue();
    if (this.localDraftService.isFormValueMeaningful(raw)) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  private getUserId(): number {
    return JSON.parse(localStorage.getItem('user_id') || '0');
  }

  private setupLocalDraftAutoSave(): void {
    if (this.isEditMode) {
      return;
    }

    this.localDraftSub?.unsubscribe();
    this.localDraftSub = this.invoiceForm.valueChanges
      .pipe(
        debounceTime(1500),
        filter(() => !this.isPopulatingForm && !this.copyFlowPending && !this.skipLocalDraftSave),
      )
      .subscribe(() => this.persistLocalDraftIfNeeded());
  }

  private persistLocalDraftIfNeeded(): void {
    if (this.isEditMode || this.skipLocalDraftSave) {
      return;
    }

    const raw = this.invoiceForm.getRawValue();
    const userId = this.getUserId();

    if (!this.localDraftService.isFormValueMeaningful(raw)) {
      this.localDraftService.clear(this.merchantId, userId);
      return;
    }

    this.localDraftService.save(this.merchantId, userId, raw, {
      hasItemsForSelectedType: this.hasItemsForSelectedType,
      paymentMethodsRequired: this.paymentMethodsRequired,
    }, this.buildLocalDraftSummary(raw));
  }

  private buildLocalDraftSummary(formValue: Record<string, unknown>): InvoiceV2LocalDraftSummary {
    const companyId = this.parseCompanyId(formValue.company_id);
    const customerId = formValue.customer_id;
    const invoiceType = +formValue.invoice_type;
    const items = Array.isArray(formValue.items) ? formValue.items : [];
    const payments = Array.isArray(formValue.payment_methods) ? formValue.payment_methods : [];
    const totalRaw = Number(
      formValue.payment_amount_including_vat ??
        formValue.total_amount ??
        this.calculatedAmounts.payment_amount_including_vat ??
        this.calculatedAmounts.total_amount ??
        0,
    );
    const totalAmount = Number.isFinite(totalRaw) && totalRaw > 0 ? totalRaw : null;

    let invoiceTypeLabel = '';
    if (Number.isFinite(invoiceType) && (items.length > 0 || (customerId != null && customerId !== ''))) {
      invoiceTypeLabel = this.getInvoiceTypeLabel(invoiceType);
    }

    return {
      companyName: companyId ? this.resolveCompanyDisplayName(companyId) : '',
      customerName:
        customerId != null && customerId !== ''
          ? this.resolveCustomerDisplayName(customerId)
          : '',
      invoiceTypeLabel,
      invoiceDateIso: this.localDraftService.toIsoDate(formValue.invoice_date),
      itemCount: items.length,
      paymentMethodCount: payments.length,
      totalAmount,
      hasNote: String(formValue.invoice_note ?? '').trim().length > 0,
    };
  }

  private resolveCompanyDisplayName(companyId: number | null): string {
    if (!companyId) {
      return '';
    }
    const company = this.companies.find((c) => +c.company_id === +companyId);
    if (!company) {
      return '';
    }
    return (
      company.displayName ||
      companyDisplayName(company, this.curentLang) ||
      companyDisplayName(company.fullData, this.curentLang)
    );
  }

  private resolveCustomerDisplayName(customerId: unknown): string {
    if (customerId == null || customerId === '') {
      return '';
    }
    const customer = this.customers.find((c) => +c.customer_id === +customerId);
    return customer?.customer_name?.trim() || '';
  }

  private resolveDraftSummaryForDisplay(draft: InvoiceV2LocalDraft): InvoiceV2LocalDraftSummary {
    const formValue = draft.formValue;
    const companyId = this.parseCompanyId(formValue.company_id);
    const customerId = formValue.customer_id;
    const items = Array.isArray(formValue.items) ? formValue.items : [];
    const payments = Array.isArray(formValue.payment_methods) ? formValue.payment_methods : [];
    const totalRaw = Number(
      formValue.payment_amount_including_vat ?? formValue.total_amount ?? 0,
    );
    let totalAmount: number | null = Number.isFinite(totalRaw) && totalRaw > 0 ? totalRaw : null;
    if (!totalAmount && items.length > 0) {
      const itemSum = items.reduce((acc: number, item: any) => {
        const lineTotal = Number(item?.total_amount ?? item?.sub_total ?? 0);
        return acc + (Number.isFinite(lineTotal) ? lineTotal : 0);
      }, 0);
      if (itemSum > 0) {
        totalAmount = itemSum;
      }
    }

    let invoiceTypeLabel = '';
    const invoiceType = +formValue.invoice_type;
    if (Number.isFinite(invoiceType) && (items.length > 0 || (customerId != null && customerId !== ''))) {
      invoiceTypeLabel = this.getInvoiceTypeLabel(invoiceType);
    }

    return {
      companyName: companyId ? this.resolveCompanyDisplayName(companyId) : '',
      customerName:
        customerId != null && customerId !== ''
          ? this.resolveCustomerDisplayName(customerId)
          : '',
      invoiceTypeLabel,
      invoiceDateIso: this.localDraftService.toIsoDate(formValue.invoice_date),
      itemCount: items.length,
      paymentMethodCount: payments.length,
      totalAmount,
      hasNote: String(formValue.invoice_note ?? '').trim().length > 0,
    };
  }

  private tryOfferLocalDraftRestoreWhenReady(): void {
    if (
      !this.localDraftRestoreDataPending ||
      this.isEditMode ||
      this.copyFlowPending ||
      !this.companiesLoadedForDraftRestore ||
      !this.invoiceTypesLoadedForDraftRestore
    ) {
      return;
    }

    this.localDraftRestoreDataPending = false;
    const offeredDraft = this.maybeOfferLocalDraftRestore();
    if (!offeredDraft) {
      this.tryShowDocumentTypePickerIfNeeded();
    }
  }

  /** Localized label for an invoice type row (same as ng-select options). */
  getInvoiceTypePickerLabel(type: { name_en?: string; name_ar?: string; name_he?: string }): string {
    if (this.curentLang === 'en') {
      return String(type.name_en || type.name_ar || '').trim();
    }
    if (this.curentLang === 'ar') {
      return String(type.name_ar || type.name_en || '').trim();
    }
    return String(type.name_he || type.name_en || '').trim();
  }

  selectDocumentTypeFromPicker(typeId: number): void {
    this.invoiceForm.patchValue({ invoice_type: typeId });
    this.showDocumentTypePicker = false;
    this.documentTypePickerResolved = true;
    this.pendingDocumentTypePicker = false;
    this.scheduleDocumentSequenceCheck();
  }

  cancelDocumentTypePicker(): void {
    this.showDocumentTypePicker = false;
    this.pendingDocumentTypePicker = false;
    this.documentTypePickerResolved = true;
    this.localDraftBypassNavigation = true;
    this.router.navigate(['/dashboard/invoices-v2']);
  }

  private tryShowDocumentTypePickerIfNeeded(): void {
    if (
      this.isEditMode ||
      this.copyFlowPending ||
      !this.pendingDocumentTypePicker ||
      this.documentTypePickerResolved ||
      this.showDocumentTypePicker
    ) {
      return;
    }

    const currentType = this.invoiceForm.get('invoice_type')?.value;
    if (currentType != null && currentType !== '') {
      this.documentTypePickerResolved = true;
      this.pendingDocumentTypePicker = false;
      return;
    }

    if (!this.invoiceTypes.length) {
      return;
    }

    if (this.invoiceTypes.length === 1) {
      this.selectDocumentTypeFromPicker(+this.invoiceTypes[0].id);
      return;
    }

    this.showDocumentTypePicker = true;
  }

  private openLocalDraftRestoreDialog(draft: InvoiceV2LocalDraft): void {
    const savedLabel = this.localDraftService.formatSavedAt(draft.savedAt, this.curentLang);
    const summary = this.resolveDraftSummaryForDisplay(draft);
    const isRtl = this.curentLang === 'ar' || this.curentLang === 'he';

    Swal.fire({
      title: this.translate.instant('invoices_v2.form.local_draft_restore_title'),
      html: this.localDraftService.buildRestoreDialogHtml({
        savedAt: savedLabel,
        summary,
        isRtl,
        locale: this.curentLang,
        labels: {
          lead: this.translate.instant('invoices_v2.form.local_draft_restore_lead'),
          savedAtLabel: this.translate.instant('invoices_v2.form.local_draft_restore_saved_at'),
          companyLabel: this.translate.instant('invoices_v2.common.company'),
          customerLabel: this.translate.instant('invoices_v2.common.customer'),
          documentTypeLabel: this.translate.instant('invoices_v2.common.invoice_type'),
          documentDateLabel: this.translate.instant('invoices_v2.common.document_date'),
          itemsLabel: this.translate.instant('invoices_v2.form.local_draft_restore_items'),
          paymentsLabel: this.translate.instant('invoices_v2.form.local_draft_restore_payments'),
          totalLabel: this.translate.instant('invoices_v2.common.total_amount'),
          noteLabel: this.translate.instant('invoices_v2.form.local_draft_restore_note'),
          notePresent: this.translate.instant('invoices_v2.form.local_draft_restore_note_yes'),
          footer: this.translate.instant('invoices_v2.form.local_draft_restore_footer'),
          notSet: this.translate.instant('invoices_v2.form.local_draft_restore_not_set'),
        },
      }),
      icon: undefined,
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: this.translate.instant('invoices_v2.form.local_draft_restore_confirm'),
      cancelButtonText: this.translate.instant('invoices_v2.form.local_draft_restore_discard'),
      confirmButtonColor: '#E62E2D',
      cancelButtonColor: '#6b7280',
      reverseButtons: isRtl,
      focusCancel: false,
      width: '520px',
      padding: '1.5rem 1.25rem 1.25rem',
      customClass: {
        popup: 'iv2-draft-restore-popup',
        title: 'iv2-draft-restore-title',
        htmlContainer: 'iv2-draft-restore-html',
        confirmButton: 'iv2-draft-restore-confirm',
        cancelButton: 'iv2-draft-restore-cancel',
        closeButton: 'iv2-draft-restore-close',
      },
    }).then((result) => {
      const userId = this.getUserId();
      if (result.isConfirmed) {
        this.populateFromLocalDraft(draft);
        this.documentTypePickerResolved = true;
        this.pendingDocumentTypePicker = false;
        this.toastr.info(this.translate.instant('invoices_v2.form.local_draft_restored'));
      } else {
        if (result.dismiss === Swal.DismissReason.cancel) {
          this.localDraftService.clear(this.merchantId, userId);
        }
        this.tryShowDocumentTypePickerIfNeeded();
      }
    });
  }

  private clearLocalDraft(): void {
    this.localDraftService.clear(this.merchantId, this.getUserId());
  }

  private maybeOfferLocalDraftRestore(): boolean {
    if (this.isEditMode || this.copyFlowPending || this.localDraftRestoreOffered) {
      return false;
    }

    this.localDraftRestoreOffered = true;
    const userId = this.getUserId();
    const draft = this.localDraftService.load(this.merchantId, userId);

    if (!draft || !this.localDraftService.isFormValueMeaningful(draft.formValue)) {
      if (draft) {
        this.localDraftService.clear(this.merchantId, userId);
      }
      return false;
    }

    const companyId = this.parseCompanyId(draft.formValue.company_id);
    const customerId = draft.formValue.customer_id;
    const openDialog = () => this.openLocalDraftRestoreDialog(draft);

    if (companyId && customerId != null && customerId !== '') {
      this.httpHelpService.getCustomersByCompanyId(companyId).subscribe({
        next: (response) => {
          const customersData: any[] = Array.isArray(response) ? response : [];
          this.customers = customersData.map((customer: any) => this.mapCustomerOption(customer));
          openDialog();
        },
        error: () => openDialog(),
      });
      return true;
    }

    openDialog();
    return true;
  }

  private populateFromLocalDraft(draft: InvoiceV2LocalDraft): void {
    const data = this.localDraftService.deserializeFormValue(
      draft.formValue as Record<string, unknown>,
    );

    this.hasItemsForSelectedType = draft.uiState.hasItemsForSelectedType;
    this.paymentMethodsRequired = draft.uiState.paymentMethodsRequired;

    this.populateCopyData(data);

    setTimeout(() => {
      const patch: Record<string, unknown> = {};
      if (data.invoice_date) {
        patch.invoice_date = data.invoice_date;
      }
      if (data.payment_status) {
        patch.payment_status = data.payment_status;
      }

      const amountFields = [
        'amount_before_discount',
        'all_discount',
        'payment_amount',
        'all_vat_amount',
        'payment_amount_including_vat',
        'total_amount',
        'deduction_from_source',
      ] as const;
      amountFields.forEach((field) => {
        if (data[field] != null) {
          patch[field] = data[field];
        }
      });

      if (Object.keys(patch).length > 0) {
        this.invoiceForm.patchValue(patch, { emitEvent: false });
      }

      this.recalculateAllItemsAndTotals();
      this.getVatPercentageByVatListIdAndDate();
      this.refreshYearlyMaxAmount();
    }, 600);
  }

  /**
   * Populate form with copied invoice data
   */
  populateCopyData(copyData: any): void {
    this.isPopulatingForm = true;
    
    // Step 1: Patch basic form values (except invoice_type for now)
    const copiedCompanyId = this.parseCompanyId(copyData.company_id);
    this.invoiceForm.patchValue({
      company_id: copiedCompanyId,
      document_language: copyData.document_language,
      invoice_note: copyData.invoice_note,
    });

    const copiedCurrency = copyData.currency ?? DEFAULT_CURRENCY_CODE;
    const copiedForeign = isForeignCurrency(copiedCurrency);
    this.previousCurrencyCode = copiedCurrency;
    this.invoiceForm.patchValue(
      {
        currency: copiedCurrency,
        exchange_rate: copyData.exchange_rate ?? 1,
        exchange_rate_date: copyData.exchange_rate_date ?? '',
        exchange_rate_source: copyData.exchange_rate_source ?? 'BOI',
        exchange_rate_manually_changed: !!copyData.exchange_rate_manually_changed,
      },
      { emitEvent: false },
    );
    if (copiedForeign) {
      this.skipExchangeRateFetch = true;
    }

    if (copiedCompanyId) {
      this.applyInvoiceTypesFilterForCompany(copiedCompanyId, { silent: true });
    }

    // Step 2: Load customers for the selected company
    if (copiedCompanyId) {
      this.loadCustomersByCompany(copiedCompanyId);
      // Set customer after customers load
      setTimeout(() => {
        this.invoiceForm.patchValue({ customer_id: copyData.customer_id }, { emitEvent: false });
        this.scheduleRefreshCreateMergeEligibility();
      }, 500);
    }

    // Step 3: Set invoice type and update flags WITHOUT clearing items
    if (copyData.invoice_type) {
      const allowed = this.getAllowedInvoiceTypeCodesForCompany(copyData.company_id ?? null);
      const useType =
        allowed == null || allowed.includes(+copyData.invoice_type)
          ? copyData.invoice_type
          : this.invoiceTypes[0]
            ? +this.invoiceTypes[0].id
            : +copyData.invoice_type;
      this.invoiceForm.patchValue({ invoice_type: useType }, { emitEvent: false });

      // Update flags based on invoice type
      const selectedType =
        this.invoiceTypes.find((t) => +t.id === +useType) ||
        this.allInvoiceTypes.find((t) => +t.id === +useType);
      const hasItems = selectedType ? (selectedType.has_items === false || selectedType.has_items === 0 ? false : true) : true;
      const hasPayments = selectedType ? (selectedType.has_payments === true || selectedType.has_payments === 1) : false;
      const allowSend = this.canSendToTaxAuthority(useType, selectedType, copyData.company_id);
      let allocationFromCopy = 0;
      if (allowSend) {
        if (copyData.allocation_number !== undefined && copyData.allocation_number !== null) {
          allocationFromCopy = +copyData.allocation_number === 1 ? 1 : 0;
        }
        // else keep 0 — default do not send unless source invoice specified allocation
      }
      const subjectToVat = this.isSubjectToVatDefault(useType);

      this.hasItemsForSelectedType = hasItems;
      this.paymentMethodsRequired = hasPayments;

      this.invoiceForm.patchValue({
        allocation_number: allocationFromCopy,
        tax_status: copyData.tax_status ?? (subjectToVat ? 1 : 0),
        due_date: parseDueDateFromApi(copyData.due_date),
        payment_status: copyData.payment_status === 'unpaid' ? 'unpaid' : 'paid',
      }, { emitEvent: false });
      
      // Update items validators
      const itemsArray = this.invoiceForm.get('items') as FormArray;
      if (this.hasItemsForSelectedType) {
        itemsArray.setValidators([Validators.required, Validators.minLength(1)]);
      } else {
        itemsArray.clearValidators();
      }
      itemsArray.updateValueAndValidity();
      
      // Update payment methods validators (finalize vs draft)
      this.syncPaymentMethodValidatorsAfterTypeOrCopy();
    }

    // Step 4: Clear and add items
    this.items.clear();
    if (copyData.items && copyData.items.length > 0) {
      // Add items in original order (using push, not insert at 0)
      copyData.items.forEach((item: any) => {
        const itemForm = this.createItemFormGroup(item);
        this.items.push(itemForm);
        this.setupItemListeners(this.items.length - 1);
      });
    }

    // Step 5: Clear and add payment methods
    this.paymentMethods.clear();
    if (copyData.payment_methods && copyData.payment_methods.length > 0) {
      copyData.payment_methods.forEach((pm: any) => {
        const paymentForm = this.createPaymentMethodFormGroup(pm);
        this.paymentMethods.push(paymentForm);
        this.setupPaymentMethodListeners(this.paymentMethods.length - 1);
      });
    }
    this.syncPaymentMethodValidatorsAfterTypeOrCopy();

    // Step 6: Calculate amounts
    this.recalculateAllItemsAndTotals();

    this.loadProducts(copiedCompanyId);

    // Reset flag after form is populated
    setTimeout(() => {
      this.isPopulatingForm = false;
      this.skipExchangeRateFetch = false;
      this.loadProducts(copiedCompanyId);
      this.initYearlyCapThresholdTracking();
      this.scheduleDocumentSequenceCheck();
      }, 500);
  }

  /**
   * Create the main form
   */
  createForm(): FormGroup {
    const today = new Date();
    const defaultCompanyId = this.parseCompanyId(localStorage.getItem('companyDefault'));

    return this.fb.group({
      company_id: [defaultCompanyId, Validators.required],
      customer_id: [null, Validators.required],
      invoice_type: [null as number | null, Validators.required],
      invoice_date: [today, Validators.required],
      due_date: [null as Date | null],
      payment_status: ['paid' as StoredPaymentStatus],
      document_language: [getDefaultDocumentLanguage(), Validators.required],
      allocation_number: [0], // 1 = send to tax authority, 0 = do not send — default unchecked
      tax_status: [1], // 0 = معفي من الضريبة (tax exempt), 1 = خاضع للضريبة (subject to tax) — drives line VAT (separate from allocation)
      confirm: [false],
      invoice_note: [''],
      items: this.fb.array([]), // Validators will be set dynamically based on invoice type
      payment_methods: this.fb.array([]),
      // Optional amounts (will be auto-calculated)
      amount_before_discount: [0],
      all_discount: [0],
      payment_amount: [0],
      all_vat_amount: [0],
      payment_amount_including_vat: [0],
      total_amount: [0],
      deduction_from_source: [0],
      currency: [DEFAULT_CURRENCY_CODE],
      exchange_rate: [1],
      exchange_rate_date: [''],
      exchange_rate_source: ['BOI'],
      exchange_rate_manually_changed: [false],
    });
  }

  /**
   * Get items FormArray
   */
  get items(): FormArray {
    return this.invoiceForm.get('items') as FormArray;
  }

  /**
   * Get payment methods FormArray
   */
  get paymentMethods(): FormArray {
    return this.invoiceForm.get('payment_methods') as FormArray;
  }

  /**
   * When adding a blank line, inherit "price includes VAT" from the **last** row's checkbox
   * (the user's most recent choice). First line defaults to false.
   */
  private inheritIsVatIncludedFromLastLine(): boolean {
    if (!this.items?.length) {
      return false;
    }
    const last = this.items.at(this.items.length - 1);
    return !!last?.get('isVatIncluded')?.value;
  }

  /**
   * Create item FormGroup
   */
  createItemFormGroup(item?: CreateSoldItemV2Dto): FormGroup {
    return this.fb.group({
      index: [item?.index || this.items.length + 1],
      catalog_id: [item?.catalog_id || ''],
      category: [item?.category || ''],
      measurement_unit_id: [item?.measurement_unit_id || null],
      description: [item?.description || ''],
      item_name: [item?.item_name || '', [Validators.required, Validators.maxLength(300)]],
      measure_unit_description: [item?.measure_unit_description || ''],
      quantity: [item?.quantity || 1, [Validators.required, Validators.min(0.01)]],
      price_per_unit: [item?.price_per_unit || 0, [Validators.required, Validators.min(0)]],
      sub_total: [item?.sub_total || 0, Validators.required],
      discount: [item?.discount || 0, [Validators.required, Validators.min(0)]],
      total_amount: [item?.total_amount || 0, Validators.required],
      vat_rate: [item?.vat_rate || this.vatPercentage || 17, [Validators.required, Validators.min(0)]],
      vat_amount: [item?.vat_amount || 0, Validators.required],
      product_id: [item?.product_id || null, Validators.required],
      isPresenting: [item?.isPresenting || false],
      isVatIncluded: [
        item?.isVatIncluded ??
          (item as any)?.is_vat_included ??
          this.inheritIsVatIncludedFromLastLine(),
      ],
      price_per_unit_foreign: [item?.price_per_unit_foreign ?? 0],
      discount_foreign: [item?.discount_foreign ?? 0],
      sub_total_foreign: [item?.sub_total_foreign ?? 0],
      vat_amount_foreign: [item?.vat_amount_foreign ?? 0],
      total_amount_foreign: [item?.total_amount_foreign ?? 0],
    });
  }

  /**
   * Create payment method FormGroup with dynamic validators based on required_fields
   */
  createPaymentMethodFormGroup(payment?: PaymentMethodDto): FormGroup {
    const paymentType = payment?.type || PaymentType.CASH;
    const paymentMethod = this.paymentMethodsList.find((pm) => +pm.payment_method_code === +paymentType);
    const requiredFields = paymentMethod?.required_fields || [];
    const requiresDetails = paymentMethod?.requires_details || false;

    // Date is always required for all payment methods
    // Default: use invoice_date value if available, otherwise today
    let invoiceDateValue: any = this.invoiceForm?.get('invoice_date')?.value || new Date();
    if (typeof invoiceDateValue === 'string') {
      // Try to parse string date
      const parsed = new Date(invoiceDateValue);
      invoiceDateValue = isNaN(parsed.getTime()) ? new Date() : parsed;
    }

    const defaultDate = payment?.details?.date || invoiceDateValue;
    const dateValidators = [Validators.required];

    // Build details form group with dynamic validators
    const detailsControls: any = {
      date: [defaultDate, dateValidators],
      reference: [payment?.details?.reference || ''],
      bank: [payment?.details?.bank || ''],
      branch: [payment?.details?.branch || ''],
      account: [payment?.details?.account || ''],
      details: [payment?.details?.details || ''],
      credit_card_code: [payment?.details?.credit_card_code || ''],
      credit_card_name: [payment?.details?.credit_card_name || ''],
      credit_type: [payment?.details?.credit_type || ''],
      account_number: [payment?.details?.account_number || ''],
      line_id: [payment?.details?.line_id || null],
    };

    // Apply validators to required fields (bank / branch / account stay optional in UI)
    const optionalBankDetailFields = ['bank', 'branch', 'account'];
    requiredFields.forEach((field: string) => {
      if (optionalBankDetailFields.includes(field)) return;
      if (detailsControls[field]) {
        detailsControls[field] = [detailsControls[field][0], Validators.required];
      }
    });

    return this.fb.group({
      type: [paymentType, Validators.required],
      amount: [payment?.amount || 0, [Validators.required, Validators.min(0)]],
      details: this.fb.group(detailsControls),
    });
  }

  /**
   * Add new item (inserts at the beginning so user doesn't need to scroll)
   */
  addItem(item?: CreateSoldItemV2Dto, options?: { skipInvoiceTotals?: boolean; expand?: boolean }): void {
    const itemForm = this.createItemFormGroup(item);
    // Add new item at the end (last product), not at the beginning
    this.items.push(itemForm);
    const newIndex = this.items.length - 1;
    this.setupItemListeners(newIndex);
    this.calculateItemAmounts(newIndex);
    if (!this.isPopulatingForm && options?.expand !== false) {
      this.expandedItemIndex = newIndex;
    }
    if (!options?.skipInvoiceTotals) {
      this.calculateAmounts();
    }
  }

  /**
   * Remove item
   */
  removeItem(index: number): void {
    if (this.expandedItemIndex === index) {
      this.expandedItemIndex = null;
    } else if (this.expandedItemIndex != null && this.expandedItemIndex > index) {
      this.expandedItemIndex--;
    }
    this.items.removeAt(index);
    this.calculateAmounts();
  }

  isItemExpanded(index: number): boolean {
    return this.expandedItemIndex === index;
  }

  toggleItemExpanded(index: number): void {
    this.expandedItemIndex = this.expandedItemIndex === index ? null : index;
    if (this.expandedItemIndex !== index) {
      this.onProductSelectClose();
    }
  }

  onProductSelectOpened(index: number, select?: NgSelectComponent): void {
    this.productSelectOpenIndex = index;
    const companyId = this.parseCompanyId(this.invoiceForm.get('company_id')?.value);
    if (
      companyId != null &&
      this.loadedProductsCompanyId !== companyId &&
      this.pendingProductsCompanyId !== companyId
    ) {
      this.loadProducts(companyId);
    }
    if (select) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.repositionProductDropdownPanel(select);
        });
      });
    }
  }

  onProductSelectClose(): void {
    this.productSelectOpenIndex = null;
  }

  /** Widen body-appended panel and re-align under trigger (ng-select defaults to trigger width). */
  private repositionProductDropdownPanel(select: NgSelectComponent): void {
    const dropdownPanel = (select as NgSelectComponent & { dropdownPanel?: { adjustPosition?: () => void } })
      .dropdownPanel;
    dropdownPanel?.adjustPosition?.();

    const host = (select as NgSelectComponent & { element?: HTMLElement }).element;
    if (!host) {
      return;
    }

    const panelEl = document.querySelector(
      'body > .ng-dropdown-panel.invoice-v2-product-dropdown-panel',
    ) as HTMLElement | null;
    if (!panelEl) {
      return;
    }

    const rect = host.getBoundingClientRect();
    const viewportPad = 16;
    const minPanelW = Math.min(520, window.innerWidth - viewportPad * 2);
    const maxPanelW = Math.min(640, window.innerWidth - viewportPad * 2);
    const panelW = Math.min(maxPanelW, Math.max(minPanelW, Math.round(rect.width)));

    panelEl.style.width = `${panelW}px`;
    panelEl.style.minWidth = `${panelW}px`;
    panelEl.style.maxWidth = `${maxPanelW}px`;

    const isRtl =
      document.documentElement.dir === 'rtl' ||
      getComputedStyle(document.documentElement).direction === 'rtl';

    if (isRtl) {
      const left = Math.max(viewportPad, rect.right - panelW);
      panelEl.style.left = `${left}px`;
      panelEl.style.right = 'auto';
    } else {
      const left = Math.min(rect.left, window.innerWidth - panelW - viewportPad);
      panelEl.style.left = `${Math.max(viewportPad, left)}px`;
      panelEl.style.right = 'auto';
    }
  }

  trackProductById = (item: { product_id?: number } | null): number | string => {
    return item?.product_id ?? '';
  };

  /**
   * Local filter first. While the catalog is still filling (or truncated),
   * also ask the server so products outside the first page are findable.
   */
  onProductCatalogSearch(event: { term?: string } | string): void {
    const term = typeof event === 'string' ? event : event?.term ?? '';
    if (!term.trim()) {
      return;
    }
    if (this.productCatalogTruncated || this.productsLoading) {
      this.productOverflowSearch$.next(term);
    }
  }

  /** ng-select bindValue can be number|string depending on API/draft — coerce. */
  compareProductIds = (a: unknown, b: unknown): boolean => {
    if (a == null || b == null || a === '' || b === '') {
      return a === b;
    }
    return +a === +b;
  };

  onItemSummaryKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleItemExpanded(index);
    }
  }

  getProductDisplayName(product: {
    product_name_ar?: string;
    product_name_en?: string;
    product_name_he?: string;
  } | null | undefined): string {
    if (!product) {
      return '';
    }
    if (this.curentLang === 'ar') {
      return product.product_name_ar || product.product_name_en || product.product_name_he || '';
    }
    if (this.curentLang === 'he') {
      return product.product_name_he || product.product_name_en || product.product_name_ar || '';
    }
    return product.product_name_en || product.product_name_he || product.product_name_ar || '';
  }

  /** Text direction for product label/option based on displayed name script (not UI lang). */
  getProductTextDir(text: string | null | undefined): 'rtl' | 'ltr' | 'auto' {
    const value = String(text ?? '').trim();
    if (!value) {
      return 'auto';
    }
    const rtlMatch = value.match(/[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/);
    const ltrMatch = value.match(/[A-Za-z]/);
    const rtlIndex = rtlMatch?.index ?? -1;
    const ltrIndex = ltrMatch?.index ?? -1;
    if (rtlIndex >= 0 && (ltrIndex < 0 || rtlIndex <= ltrIndex)) {
      return 'rtl';
    }
    if (ltrIndex >= 0) {
      return 'ltr';
    }
    return 'auto';
  }

  getItemDisplayName(index: number): string {
    const item = this.items.at(index);
    if (!item) {
      return this.translate.instant('invoices_v2.form.items_no_product');
    }

    const itemName = String(item.get('item_name')?.value ?? '').trim();
    if (itemName) {
      return itemName;
    }

    const productId = item.get('product_id')?.value;
    if (productId) {
      const product = this.products.find((p) => p.product_id === productId);
      if (product) {
        return this.getProductDisplayName(product);
      }
    }

    return this.translate.instant('invoices_v2.form.items_no_product');
  }

  getItemDescription(index: number): string {
    const item = this.items.at(index);
    if (!item) {
      return '';
    }
    return String(item.get('description')?.value ?? '').trim();
  }

  /**
   * Include VAT checkbox change (same as invoice v1): update isVatIncluded and recalculate line.
   */
  onVatCheckboxChange(index: number, event: Event): void {
    const item = this.items.at(index);
    const checkbox = event.target as HTMLInputElement;
    if (!item || !checkbox) return;
    item.get('isVatIncluded')?.setValue(!!checkbox.checked, { emitEvent: false });
    item.get('isVatIncluded')?.updateValueAndValidity();
    this.calculateItemAmounts(index);
    this.calculateAmounts();
  }

  /**
   * Select all text in the input on focus or Enter so that typing replaces the value.
   * Used for quantity, unit price, discount (and similar amount fields).
   */
  selectAllOnFocus(event: Event): void {
    const el = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
      el.select();
    }
  }

  /**
   * Add payment method below existing rows and scroll the new row into view.
   */
  addPaymentMethod(payment?: PaymentMethodDto, options?: { scrollIntoView?: boolean }): void {
    const seed = payment ?? this.buildPaymentSeedFromLastRow();
    const paymentForm = this.createPaymentMethodFormGroup(seed);
    this.paymentMethods.push(paymentForm);
    const newIndex = this.paymentMethods.length - 1;

    this.setupPaymentMethodListeners(newIndex);
    this.applyPaymentValidationForConfirmState();
    this.calculateAmounts();

    const scrollIntoView = options?.scrollIntoView ?? !this.isPopulatingForm;
    if (scrollIntoView) {
      this.highlightAndScrollToPaymentRow(newIndex);
    }
  }

  /** Inherit payment type from the last row when adding another line manually. */
  private buildPaymentSeedFromLastRow(): PaymentMethodDto | undefined {
    if (this.paymentMethods.length === 0) {
      return undefined;
    }
    const last = this.paymentMethods.at(this.paymentMethods.length - 1);
    const type = last?.get('type')?.value;
    if (type == null || type === '') {
      return undefined;
    }
    return { type: +type, amount: 0 };
  }

  private highlightAndScrollToPaymentRow(index: number): void {
    this.highlightedPaymentIndex = index;

    if (this.highlightPaymentTimer) {
      clearTimeout(this.highlightPaymentTimer);
    }
    this.highlightPaymentTimer = setTimeout(() => {
      this.highlightedPaymentIndex = null;
      this.highlightPaymentTimer = null;
    }, 2400);

    setTimeout(() => {
      document.getElementById(`invoice-v2-payment-card-${index}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      const typeSelect = document.querySelector<HTMLElement>(
        `[data-payment-type-select="${index}"]`,
      );
      typeSelect?.focus();
    }, 0);
  }

  isPaymentHighlighted(index: number): boolean {
    return this.highlightedPaymentIndex === index;
  }

  /**
   * Setup listeners for payment method to update validators when type changes
   */
  setupPaymentMethodListeners(index: number): void {
    const payment = this.paymentMethods.at(index);
    payment.get('type')?.valueChanges.subscribe((type: number) => {
      this.updatePaymentMethodValidators(index, type);
    });

    // When bank changes, load its branches
    const details = payment.get('details') as FormGroup;
    details
      ?.get('bank')
      ?.valueChanges.subscribe((bankCode: string) => {
        if (bankCode) {
          this.loadBranchesForBank(bankCode);
        }
      });
  }

  /**
   * Clear payment row validators (save without confirming — draft / incomplete payment lines allowed).
   */
  private softenPaymentMethodRow(index: number): void {
    const payment = this.paymentMethods.at(index) as FormGroup;
    if (!payment) return;
    payment.get('type')?.clearValidators();
    payment.get('amount')?.clearValidators();
    const details = payment.get('details') as FormGroup;
    if (details) {
      Object.keys(details.controls).forEach((k) => {
        details.get(k)?.clearValidators();
        details.get(k)?.updateValueAndValidity({ emitEvent: false });
      });
    }
    payment.get('type')?.updateValueAndValidity({ emitEvent: false });
    payment.get('amount')?.updateValueAndValidity({ emitEvent: false });
    payment.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * After invoice type change or copy: set payment array validators, then apply confirm-based strict/soft rules.
   */
  private syncPaymentMethodValidatorsAfterTypeOrCopy(): void {
    const paymentMethodsArray = this.invoiceForm.get('payment_methods');
    if (!this.paymentMethodsRequired) {
      paymentMethodsArray?.clearValidators();
      for (let i = 0; i < this.paymentMethods.length; i++) {
        this.softenPaymentMethodRow(i);
      }
      paymentMethodsArray?.updateValueAndValidity({ emitEvent: false });
      return;
    }
    this.applyPaymentValidationForConfirmState();
  }

  /**
   * Draft (confirm=false): optional payment rows and relaxed detail validators.
   * Finalize (confirm=true): enforce payment_methods list + per-type required_fields (bank / branch / account optional).
   */
  private applyPaymentValidationForConfirmState(): void {
    if (!this.paymentMethodsRequired) {
      return;
    }
    const confirmImmediate = !!this.invoiceForm.get('confirm')?.value;
    const paymentMethodsArray = this.invoiceForm.get('payment_methods');
    if (!confirmImmediate) {
      paymentMethodsArray?.clearValidators();
      paymentMethodsArray?.updateValueAndValidity({ emitEvent: false });
      for (let i = 0; i < this.paymentMethods.length; i++) {
        this.softenPaymentMethodRow(i);
      }
      return;
    }
    paymentMethodsArray?.setValidators([Validators.required, Validators.minLength(1)]);
    paymentMethodsArray?.updateValueAndValidity({ emitEvent: false });
    for (let i = 0; i < this.paymentMethods.length; i++) {
      const type = this.paymentMethods.at(i).get('type')?.value;
      this.updatePaymentMethodValidators(i, type);
    }
  }

  /**
   * Update validators for payment method based on selected type
   */
  updatePaymentMethodValidators(index: number, paymentType: number): void {
    if (!this.invoiceForm.get('confirm')?.value) {
      this.softenPaymentMethodRow(index);
      return;
    }
    const payment = this.paymentMethods.at(index);
    const details = payment.get('details') as FormGroup;
    const paymentMethod = this.paymentMethodsList.find((pm) => +pm.payment_method_code === +paymentType);
    const requiredFields = paymentMethod?.required_fields || [];

    // Date is always required
    details.get('date')?.setValidators([Validators.required]);
    details.get('date')?.updateValueAndValidity();

    // Update validators for all detail fields
    const detailFields = [
      'reference',
      'bank',
      'branch',
      'account',
      'details',
      'credit_card_code',
      'credit_card_name',
      'credit_type',
      'account_number',
    ];

    const optionalBankDetailFields = ['bank', 'branch', 'account'];
    detailFields.forEach((field) => {
      const control = details.get(field);
      if (control) {
        if (optionalBankDetailFields.includes(field)) {
          control.clearValidators();
        } else if (requiredFields.includes(field)) {
          control.setValidators([Validators.required]);
        } else {
          control.clearValidators();
        }
        control.updateValueAndValidity();
      }
    });
  }

  /**
   * Remove payment method
   */
  removePaymentMethod(index: number): void {
    if (this.highlightedPaymentIndex === index) {
      this.highlightedPaymentIndex = null;
    } else if (this.highlightedPaymentIndex != null && this.highlightedPaymentIndex > index) {
      this.highlightedPaymentIndex--;
    }
    this.paymentMethods.removeAt(index);
    this.applyPaymentValidationForConfirmState();
    this.calculateAmounts();
  }

  /**
   * Setup form listeners
   */
  setupFormListeners(): void {
    this.setupProductCatalog();

    // Listen to invoice type changes
    this.invoiceForm.get('invoice_type')?.valueChanges.subscribe((type) => {
      this.onInvoiceTypeChange(type);
      this.scheduleDocumentSequenceCheck();
    });

    this.invoiceForm.get('due_date')?.valueChanges.subscribe((dueDate) => {
      if (this.isPopulatingForm) {
        return;
      }
      if (!isPaymentTrackingInvoiceType(this.invoiceForm.get('invoice_type')?.value)) {
        return;
      }
      if (dueDate == null || dueDate === '') {
        this.invoiceForm.patchValue({ payment_status: 'paid' }, { emitEvent: false });
      }
    });

    // Listen to company changes for document number prediction and loading customers
    this.invoiceForm.get('company_id')?.valueChanges.subscribe((companyId) => {
      this.clearFieldValidationError('company_id');
      const normalizedId = this.parseCompanyId(companyId);
      if (normalizedId !== companyId) {
        this.invoiceForm.patchValue({ company_id: normalizedId }, { emitEvent: false });
      }
      if (!this.isPopulatingForm) {
        this.invoiceForm.patchValue({ customer_id: null }, { emitEvent: false });
      }
      if (normalizedId) {
        this.applyInvoiceTypesFilterForCompany(normalizedId);
        if (!this.isEditMode) {
          this.predictDocumentNumber(normalizedId);
        }
        this.scheduleDocumentSequenceCheck();
        // Load customers for selected company
        this.loadCustomersByCompany(normalizedId);
        this.loadProducts(normalizedId, undefined, undefined, !this.isPopulatingForm);
        this.syncAllocationWithTaxAuthorityRules();
        if (!this.isEditMode) {
          this.scheduleRefreshCreateMergeEligibility();
        }
      } else {
        this.applyInvoiceTypesFilterForCompany(null);
        this.products = [];
        if (!this.isPopulatingForm) {
          this.clearLineItemsWithInvalidProducts(null);
        }
        // Clear customers if no company selected
        this.customers = [];
        this.updateCustomerIdDisabledState();
        if (!this.isEditMode) {
          this.clearCreateMergeEligibilityScheduleAndState();
        }
      }
    });

    // Listen to invoice date changes for VAT percentage
    this.invoiceForm.get('invoice_date')?.valueChanges.subscribe(() => {
      this.getVatPercentageByVatListIdAndDate();
      this.refreshYearlyMaxAmount();
      if (this.isForeignCurrencySelected() && this.canEditCurrencyFields()) {
        this.loadExchangeRate(false);
      }
    });

    this.invoiceForm.get('currency')?.valueChanges.subscribe((code) => {
      if (this.isPopulatingForm || this.skipExchangeRateFetch) {
        return;
      }
      this.onCurrencyChanged(String(code ?? DEFAULT_CURRENCY_CODE));
    });

    this.invoiceForm.get('exchange_rate')?.valueChanges.subscribe(() => {
      if (this.isPopulatingForm || !this.isForeignCurrencySelected()) {
        return;
      }
      if (this.canEditCurrencyFields()) {
        this.invoiceForm.patchValue({ exchange_rate_manually_changed: true }, { emitEvent: false });
      }
      this.recalculateAllItemsAndTotals();
    });

    // confirm = finalized document (strict payments); false = draft (soft payment rules). Set via footer buttons.
    this.invoiceForm.get('confirm')?.valueChanges.subscribe(() => {
      this.applyPaymentValidationForConfirmState();
    });

    // Header tax status (معفي / خاضع): zero VAT on all lines when exempt
    this.invoiceForm.get('tax_status')?.valueChanges.subscribe(() => {
      this.recalculateAllItemsAndTotals();
    });

    // Create mode: close merge modal if customer cleared; refresh merge-button eligibility when customer changes
    this.invoiceForm.get('customer_id')?.valueChanges.subscribe((customerId) => {
      this.clearFieldValidationError('customer_id');
      if (this.isEditMode) {
        return;
      }
      if (customerId == null || customerId === '') {
        this.closeCreateMergeModal();
        this.clearCreateMergeEligibilityScheduleAndState();
      } else {
        this.scheduleRefreshCreateMergeEligibility();
      }
    });
  }

  /**
   * Setup item listeners for auto-calculation
   */
  setupItemListeners(index: number): void {
    const item = this.items.at(index);
    let isProductSelecting = false;
    
    // Listen to product_id changes to load product details
    item.get('product_id')?.valueChanges.subscribe((productId) => {
      // Don't trigger product selection during form population (edit mode)
      if (this.isPopulatingForm) {
        return;
      }
      
      if (productId && !isProductSelecting) {
        isProductSelecting = true;
        this.onProductSelect(productId, index);
        // Reset flag after a short delay to allow product selection to complete
        setTimeout(() => {
          isProductSelecting = false;
        }, 1000);
      }
    });

    // Listen to quantity, price, discount, vat_rate, isVatIncluded changes for calculation
    [
      'quantity',
      'price_per_unit',
      'price_per_unit_foreign',
      'discount',
      'discount_foreign',
      'vat_rate',
      'isVatIncluded',
    ].forEach((fieldName) => {
      item.get(fieldName)?.valueChanges.subscribe(() => {
        if (!isProductSelecting) {
          this.calculateItemAmounts(index);
          this.calculateAmounts();
        }
      });
    });
  }

  isForeignCurrencySelected(): boolean {
    return isForeignCurrency(this.invoiceForm?.get('currency')?.value);
  }

  canEditCurrencyFields(): boolean {
    return !this.isEditMode || this.invoiceConfirmFromServer === 0;
  }

  getSelectedCurrencySymbol(): string {
    const code = this.invoiceForm?.get('currency')?.value ?? DEFAULT_CURRENCY_CODE;
    const match = this.currencies.find((c) => c.currency_code === code);
    if (match?.currency_symbol) {
      return match.currency_symbol;
    }
    return isForeignCurrency(code) ? code : '₪';
  }

  getCurrencyLabel(currency: CurrencyDto): string {
    const name = getCurrencyDisplayName(currency, this.curentLang);
    return `${currency.currency_symbol} ${name} (${currency.currency_code})`;
  }

  /** Product list/catalog prices are stored in ILS — show document currency in the dropdown when foreign. */
  formatProductListPrice(priceIls: number | string | null | undefined): string {
    const ils = Number(priceIls);
    if (!Number.isFinite(ils) || ils <= 0) {
      return '';
    }
    if (this.isForeignCurrencySelected()) {
      const foreign = convertIlsToForeign(ils, this.getEffectiveExchangeRate());
      return `${foreign.toFixed(2)} ${this.getSelectedCurrencySymbol()}`;
    }
    return `${ils.toFixed(2)} ₪`;
  }

  private getEffectiveExchangeRate(): number {
    const rate = parseFloat(this.invoiceForm?.get('exchange_rate')?.value);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  private isExchangeRateManuallyChanged(): boolean {
    return !!this.invoiceForm?.get('exchange_rate_manually_changed')?.value;
  }

  refreshExchangeRate(): void {
    if (!this.canEditCurrencyFields() || this.exchangeRateLoading) {
      return;
    }
    this.loadExchangeRate(true);
  }

  /** When document currency is foreign, ensure BOI rate is loaded before converting product prices. */
  private ensureExchangeRateForDocumentCurrency(): Observable<number> {
    if (!this.isForeignCurrencySelected()) {
      return of(1);
    }

    const rateDate = String(this.invoiceForm.get('exchange_rate_date')?.value ?? '').trim();
    const rate = this.getEffectiveExchangeRate();

    if (this.isExchangeRateManuallyChanged() || rateDate || rate > 1) {
      return of(rate);
    }

    const currency = this.invoiceForm.get('currency')?.value;
    const date = this.formatInvoiceDateForApi(this.invoiceForm.get('invoice_date')?.value);
    return this.currencyService.getExchangeRate(currency, date).pipe(
      map((res) => {
        const data = res.data;
        if (data) {
          this.invoiceForm.patchValue(
            {
              exchange_rate: data.exchange_rate,
              exchange_rate_date: data.exchange_rate_date,
              exchange_rate_source: data.exchange_rate_source || 'BOI',
              exchange_rate_manually_changed: false,
            },
            { emitEvent: false },
          );
        }
        return this.getEffectiveExchangeRate();
      }),
      catchError(() => of(this.getEffectiveExchangeRate())),
    );
  }

  private unwrapProductApiPayload(productData: unknown): Record<string, unknown> {
    if (productData != null && typeof productData === 'object') {
      const wrapped = productData as { data?: unknown };
      if (wrapped.data != null && typeof wrapped.data === 'object') {
        return wrapped.data as Record<string, unknown>;
      }
      return productData as Record<string, unknown>;
    }
    return {};
  }

  private resolveProductPriceIls(
    productData: unknown,
    listProduct?: { price?: number | string | null },
  ): number {
    const raw = this.unwrapProductApiPayload(productData);
    const candidates = [raw['price'], raw['product_price'], listProduct?.price];
    for (const candidate of candidates) {
      const n = Number(candidate);
      if (Number.isFinite(n) && n >= 0) {
        return n;
      }
    }
    return 0;
  }

  private formatInvoiceDateForApi(value: Date | string | null | undefined): string {
    if (!value) {
      return this.datePipe.transform(new Date(), 'yyyy-MM-dd') ?? '';
    }
    if (value instanceof Date) {
      return this.datePipe.transform(value, 'yyyy-MM-dd') ?? '';
    }
    if (typeof value === 'string' && value.includes('/')) {
      const parts = value.split('/');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    return String(value).slice(0, 10);
  }

  loadCurrencies(): void {
    this.currenciesLoading = true;
    this.currencyService.getActiveCurrencies().subscribe({
      next: (list) => {
        this.currencies = [...list].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        if (!this.currencies.some((c) => c.currency_code === DEFAULT_CURRENCY_CODE)) {
          this.currencies.unshift({
            currency_code: DEFAULT_CURRENCY_CODE,
            currency_symbol: '₪',
            name_en: 'Israeli Shekel',
            name_ar: 'شيكل إسرائيلي',
            name_he: 'שקל חדש',
            is_active: true,
            decimal_places: 2,
            sort_order: 0,
          });
        }
        this.currenciesLoading = false;
      },
      error: () => {
        this.currenciesLoading = false;
        this.toastr.warning(this.translate.instant('invoices_v2.currency.load_error'));
      },
    });
  }

  loadExchangeRate(recalculate = true, convertExistingLinesFromIls = false): void {
    if (!this.isForeignCurrencySelected() || !this.canEditCurrencyFields()) {
      return;
    }
    const currency = this.invoiceForm.get('currency')?.value;
    const date = this.formatInvoiceDateForApi(this.invoiceForm.get('invoice_date')?.value);
    const seq = ++this.exchangeRateLoadSeq;
    this.exchangeRateLoading = true;
    this.currencyService.getExchangeRate(currency, date).subscribe({
      next: (res) => {
        if (seq !== this.exchangeRateLoadSeq) {
          return;
        }
        const data = res.data;
        if (data) {
          this.invoiceForm.patchValue(
            {
              exchange_rate: data.exchange_rate,
              exchange_rate_date: data.exchange_rate_date,
              exchange_rate_source: data.exchange_rate_source || 'BOI',
              exchange_rate_manually_changed: false,
            },
            { emitEvent: false },
          );
        }
        this.exchangeRateLoading = false;
        this.applyLoadedRateToExistingLines(convertExistingLinesFromIls, recalculate);
      },
      error: () => {
        if (seq !== this.exchangeRateLoadSeq) {
          return;
        }
        this.exchangeRateLoading = false;
        this.toastr.warning(this.translate.instant('invoices_v2.currency.rate_load_error'));
        this.applyLoadedRateToExistingLines(convertExistingLinesFromIls, recalculate || convertExistingLinesFromIls);
      },
    });
  }

  private applyLoadedRateToExistingLines(convertExistingLinesFromIls: boolean, recalculate: boolean): void {
    if (convertExistingLinesFromIls) {
      this.convertItemsFromIlsToForeign(this.getEffectiveExchangeRate());
    }
    if (recalculate || convertExistingLinesFromIls) {
      this.recalculateAllItemsAndTotals();
    }
  }

  private onCurrencyChanged(newCode: string): void {
    const prevForeign = isForeignCurrency(this.previousCurrencyCode);
    const nextForeign = isForeignCurrency(newCode);

    if (nextForeign && !prevForeign) {
      this.invoiceForm.patchValue(
        { exchange_rate_source: 'BOI', exchange_rate_manually_changed: false },
        { emitEvent: false },
      );
      this.previousCurrencyCode = newCode;
      this.loadExchangeRate(true, true);
      return;
    }

    if (!nextForeign && prevForeign) {
      this.previousCurrencyCode = newCode;
      this.invoiceForm.patchValue(
        {
          exchange_rate: 1,
          exchange_rate_date: '',
          exchange_rate_source: 'BOI',
          exchange_rate_manually_changed: false,
        },
        { emitEvent: false },
      );
      this.recalculateAllItemsAndTotals();
      return;
    }

    if (nextForeign && prevForeign && newCode !== this.previousCurrencyCode) {
      this.invoiceForm.patchValue(
        { exchange_rate_source: 'BOI', exchange_rate_manually_changed: false },
        { emitEvent: false },
      );
      this.previousCurrencyCode = newCode;
      this.loadExchangeRate(true, true);
      return;
    }

    this.previousCurrencyCode = newCode;
    if (nextForeign) {
      this.loadExchangeRate(true);
    }
  }

  private convertItemsFromIlsToForeign(rate: number): void {
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items.at(i);
      const priceIls = parseFloat(item.get('price_per_unit')?.value) || 0;
      const discountIls = parseFloat(item.get('discount')?.value) || 0;
      item.patchValue(
        {
          price_per_unit_foreign: convertIlsToForeign(priceIls, rate),
          discount_foreign: convertIlsToForeign(discountIls, rate),
        },
        { emitEvent: false },
      );
    }
  }

  /** True when header tax status is exempt (معفي من الضريبة). */
  private isInvoiceTaxExempt(): boolean {
    return +this.invoiceForm?.get('tax_status')?.value === 0;
  }

  /** Recalculate every line from current inputs and header tax_status, then invoice summary. */
  private recalculateAllItemsAndTotals(): void {
    for (let i = 0; i < this.items.length; i++) {
      this.calculateItemAmounts(i);
    }
    this.calculateAmounts();
  }

  /**
   * Line gross before discount (matches legacy invoices: qty × unit price, net of VAT when price includes VAT).
   */
  private getLineGrossBeforeDiscount(item: AbstractControl): number {
    const quantity = parseFloat(item.get('quantity')?.value) || 0;
    const foreign = this.isForeignCurrencySelected();
    const pricePerUnit = foreign
      ? parseFloat(item.get('price_per_unit_foreign')?.value) || 0
      : parseFloat(item.get('price_per_unit')?.value) || 0;
    const isVatIncluded = !!item.get('isVatIncluded')?.value;
    let parsedRate = parseFloat(item.get('vat_rate')?.value);
    if (isNaN(parsedRate)) {
      parsedRate = this.vatPercentage ?? 17;
    }
    const vatRate = this.isInvoiceTaxExempt() ? 0 : (parsedRate || this.vatPercentage || 17);
    let unitBase = pricePerUnit;
    if (isVatIncluded && vatRate > 0 && !this.isInvoiceTaxExempt()) {
      unitBase = pricePerUnit / (1 + vatRate / 100);
    }
    return Math.round(quantity * unitBase * 100) / 100;
  }

  /**
   * Calculate item amounts (same as invoice v1: when isVatIncluded, discount applies to gross then net/VAT split).
   * When header tax_status is exempt, VAT is zero and line vat_rate is shown/stored as 0 for the payload.
   */
  calculateItemAmounts(index: number): void {
    const item = this.items.at(index);
    const foreign = this.isForeignCurrencySelected();
    const quantity = parseFloat(item.get('quantity')?.value) || 0;
    const pricePerUnit = foreign
      ? parseFloat(item.get('price_per_unit_foreign')?.value) || 0
      : parseFloat(item.get('price_per_unit')?.value) || 0;
    const discount = foreign
      ? parseFloat(item.get('discount_foreign')?.value) || 0
      : parseFloat(item.get('discount')?.value) || 0;
    const isVatIncluded = item.get('isVatIncluded')?.value || false;
    const taxExempt = this.isInvoiceTaxExempt();

    let parsedRate = parseFloat(item.get('vat_rate')?.value);
    if (isNaN(parsedRate)) {
      parsedRate = this.vatPercentage ?? 17;
    }
    const vatRate = taxExempt ? 0 : (parsedRate || this.vatPercentage || 17);

    const line = this.computeLineAmounts(quantity, pricePerUnit, discount, isVatIncluded, vatRate, taxExempt);
    const vatRateForForm = taxExempt ? 0 : vatRate;

    if (foreign) {
      item.patchValue(
        {
          sub_total_foreign: line.subTotal,
          vat_amount_foreign: line.vatAmount,
          total_amount_foreign: line.totalAmount,
          price_per_unit_foreign: pricePerUnit,
          discount_foreign: discount,
          vat_rate: vatRateForForm,
        },
        { emitEvent: false },
      );
      return;
    }

    item.patchValue(
      {
        sub_total: line.subTotal,
        total_amount: line.totalAmount,
        vat_amount: line.vatAmount,
        vat_rate: vatRateForForm,
      },
      { emitEvent: false },
    );
  }

  private computeLineAmounts(
    quantity: number,
    pricePerUnit: number,
    discount: number,
    isVatIncluded: boolean,
    vatRate: number,
    taxExempt: boolean,
  ): { subTotal: number; vatAmount: number; totalAmount: number } {
    let subTotal: number;
    let vatAmount: number;
    let totalAmount: number;

    if (isVatIncluded) {
      const grossBefore = quantity * pricePerUnit;
      const grossAfter = Math.max(grossBefore - discount, 0);
      if (vatRate > 0 && !taxExempt) {
        subTotal = grossAfter / (1 + vatRate / 100);
        vatAmount = subTotal * (vatRate / 100);
        totalAmount = grossAfter;
      } else {
        subTotal = grossAfter;
        vatAmount = 0;
        totalAmount = grossAfter;
      }
    } else {
      const baseAmount = quantity * pricePerUnit;
      subTotal = Math.max(baseAmount - discount, 0);
      vatAmount = taxExempt ? 0 : subTotal * (vatRate / 100);
      totalAmount = subTotal + vatAmount;
    }

    return {
      subTotal: Math.round(subTotal * 100) / 100,
      vatAmount: Math.round(vatAmount * 100) / 100,
      totalAmount: Math.round(totalAmount * 100) / 100,
    };
  }

  calculateAmounts(): void {
    if (this.isForeignCurrencySelected()) {
      this.updateForeignCalculatedAmountsFromItems();
      this.scheduleForeignCurrencyConvert();
      return;
    }

    this.applyIlsAmountsFromItems();
  }

  private updateForeignCalculatedAmountsFromItems(): void {
    let foreignSubtotal = 0;
    let foreignDiscount = 0;
    let foreignVat = 0;
    let foreignTotal = 0;
    let foreignGross = 0;
    this.items.controls.forEach((line) => {
      foreignSubtotal += parseFloat(line.get('sub_total_foreign')?.value) || 0;
      foreignDiscount += parseFloat(line.get('discount_foreign')?.value) || 0;
      foreignVat += parseFloat(line.get('vat_amount_foreign')?.value) || 0;
      foreignTotal += parseFloat(line.get('total_amount_foreign')?.value) || 0;
      foreignGross += this.getLineGrossBeforeDiscount(line);
    });
    this.foreignCalculatedAmounts = {
      gross_before_discount: Math.round(foreignGross * 100) / 100,
      all_discount: Math.round(foreignDiscount * 100) / 100,
      amount_after_discount: Math.round(foreignSubtotal * 100) / 100,
      all_vat_amount: Math.round(foreignVat * 100) / 100,
      payment_amount_including_vat: Math.round(foreignTotal * 100) / 100,
    };
  }

  private applyIlsAmountsFromItems(): void {
    let amountBeforeDiscount = 0;
    let grossBeforeDiscount = 0;
    let allDiscount = 0;
    let paymentAmount = 0;
    let allVatAmount = 0;
    let paymentAmountIncludingVat = 0;

    this.items.controls.forEach((item) => {
      const subTotal = parseFloat(item.get('sub_total')?.value) || 0;
      const discount = parseFloat(item.get('discount')?.value) || 0;
      amountBeforeDiscount += subTotal;
      allDiscount += discount;
      grossBeforeDiscount += this.getLineGrossBeforeDiscount(item);
      allVatAmount += parseFloat(item.get('vat_amount')?.value) || 0;
      paymentAmountIncludingVat += parseFloat(item.get('total_amount')?.value) || 0;
    });

    amountBeforeDiscount = Math.round(amountBeforeDiscount * 100) / 100;
    grossBeforeDiscount = Math.round(grossBeforeDiscount * 100) / 100;
    allDiscount = Math.round(allDiscount * 100) / 100;

    paymentAmount = paymentAmountIncludingVat - allVatAmount;

    const totalPaymentMethods = this.sumNonZeroPaymentAmounts();
    const totalAmount = this.hasItemsForSelectedType
      ? paymentAmountIncludingVat
      : totalPaymentMethods;

    this.calculatedAmounts = {
      amount_before_discount: amountBeforeDiscount,
      gross_before_discount: grossBeforeDiscount,
      all_discount: allDiscount,
      amount_after_discount: amountBeforeDiscount,
      payment_amount: paymentAmount,
      all_vat_amount: allVatAmount,
      payment_amount_including_vat: paymentAmountIncludingVat,
      total_amount: totalAmount,
    };

    this.foreignCalculatedAmounts = {
      gross_before_discount: 0,
      all_discount: 0,
      amount_after_discount: 0,
      all_vat_amount: 0,
      payment_amount_including_vat: 0,
    };

    this.invoiceForm.patchValue(
      {
        amount_before_discount: amountBeforeDiscount,
        all_discount: allDiscount,
        payment_amount: paymentAmount,
        all_vat_amount: allVatAmount,
        payment_amount_including_vat: paymentAmountIncludingVat,
        total_amount: totalAmount,
      },
      { emitEvent: false },
    );

    this.syncAllocationWithTaxAuthorityRules();
    this.checkYearlyCapThresholdAndNotify();
    this.checkPerInvoiceThresholdAndNotify();
  }

  private scheduleForeignCurrencyConvert(): void {
    if (!this.isForeignCurrencySelected() || this.isPopulatingForm) {
      return;
    }
    if (this.foreignConvertTimer) {
      clearTimeout(this.foreignConvertTimer);
    }
    this.foreignConvertTimer = setTimeout(() => this.runForeignCurrencyConvert(), 350);
  }

  private runForeignCurrencyConvert(): void {
    if (!this.isForeignCurrencySelected()) {
      return;
    }

    const seq = ++this.foreignConvertSeq;
    const currency = String(this.invoiceForm.get('currency')?.value ?? '');
    const date = this.formatInvoiceDateForApi(this.invoiceForm.get('invoice_date')?.value);
    const rate = this.getEffectiveExchangeRate();
    const lines = this.items.controls.map((item, index) => ({
      index: index + 1,
      price_per_unit_foreign: parseFloat(item.get('price_per_unit_foreign')?.value) || 0,
      discount_foreign: parseFloat(item.get('discount_foreign')?.value) || 0,
      sub_total_foreign: parseFloat(item.get('sub_total_foreign')?.value) || 0,
      vat_amount_foreign: parseFloat(item.get('vat_amount_foreign')?.value) || 0,
      total_amount_foreign: parseFloat(item.get('total_amount_foreign')?.value) || 0,
    }));

    const request = buildForeignToIlsConvertRequest(
      currency,
      date,
      rate,
      lines,
      {
        subtotal_original: this.foreignCalculatedAmounts.amount_after_discount,
        discount_original: this.foreignCalculatedAmounts.all_discount,
        vat_original: this.foreignCalculatedAmounts.all_vat_amount,
        total_original: this.foreignCalculatedAmounts.payment_amount_including_vat,
      },
    );

    this.foreignConvertLoading = true;
    this.currencyService.convertAmounts(request).subscribe({
      next: (res) => {
        if (seq !== this.foreignConvertSeq) {
          return;
        }
        this.foreignConvertLoading = false;
        if (res?.data) {
          this.applyForeignConvertResponse(res.data);
        } else {
          this.applyLocalForeignToIlsFallback();
        }
      },
      error: () => {
        if (seq !== this.foreignConvertSeq) {
          return;
        }
        this.foreignConvertLoading = false;
        this.applyLocalForeignToIlsFallback();
      },
    });
  }

  private applyForeignConvertResponse(data: ExchangeConvertResponseData): void {
    if (data.exchange_rate != null && !this.isExchangeRateManuallyChanged()) {
      this.invoiceForm.patchValue(
        {
          exchange_rate: data.exchange_rate,
          exchange_rate_date: data.exchange_rate_date ?? this.invoiceForm.get('exchange_rate_date')?.value,
          exchange_rate_source: data.exchange_rate_source ?? 'BOI',
          exchange_rate_manually_changed: false,
        },
        { emitEvent: false },
      );
    }

    if (Array.isArray(data.lines)) {
      data.lines.forEach((line) => {
        const itemIndex = (line.index ?? 0) - 1;
        const item = this.items.at(itemIndex);
        if (!item || !line.ils) {
          return;
        }
        item.patchValue(
          {
            price_per_unit: line.ils.price_per_unit ?? item.get('price_per_unit')?.value,
            discount: line.ils.discount ?? item.get('discount')?.value,
            sub_total: line.ils.sub_total ?? item.get('sub_total')?.value,
            vat_amount: line.ils.vat_amount ?? item.get('vat_amount')?.value,
            total_amount: line.ils.total_amount ?? item.get('total_amount')?.value,
          },
          { emitEvent: false },
        );
      });
    }

    const ils = data.amounts?.ils;
    if (ils) {
      const paymentAmountIncludingVat = ils.total ?? 0;
      const allVatAmount = ils.vat ?? 0;
      const allDiscount = ils.discount ?? 0;
      const amountAfterDiscount = ils.subtotal ?? 0;
      const paymentAmount = paymentAmountIncludingVat - allVatAmount;
      const totalPaymentMethods = this.sumNonZeroPaymentAmounts();
      const totalAmount = this.hasItemsForSelectedType
        ? paymentAmountIncludingVat
        : totalPaymentMethods;

      this.calculatedAmounts = {
        amount_before_discount: amountAfterDiscount,
        gross_before_discount: amountAfterDiscount + allDiscount,
        all_discount: allDiscount,
        amount_after_discount: amountAfterDiscount,
        payment_amount: paymentAmount,
        all_vat_amount: allVatAmount,
        payment_amount_including_vat: paymentAmountIncludingVat,
        total_amount: totalAmount,
      };

      this.invoiceForm.patchValue(
        {
          amount_before_discount: amountAfterDiscount,
          all_discount: allDiscount,
          payment_amount: paymentAmount,
          all_vat_amount: allVatAmount,
          payment_amount_including_vat: paymentAmountIncludingVat,
          total_amount: totalAmount,
        },
        { emitEvent: false },
      );
    } else {
      this.applyIlsAmountsFromItems();
      return;
    }

    if (data.amounts?.foreign) {
      const foreign = data.amounts.foreign;
      this.foreignCalculatedAmounts = {
        gross_before_discount:
          (foreign.subtotal ?? 0) + (foreign.discount ?? 0),
        all_discount: foreign.discount ?? this.foreignCalculatedAmounts.all_discount,
        amount_after_discount: foreign.subtotal ?? this.foreignCalculatedAmounts.amount_after_discount,
        all_vat_amount: foreign.vat ?? this.foreignCalculatedAmounts.all_vat_amount,
        payment_amount_including_vat: foreign.total ?? this.foreignCalculatedAmounts.payment_amount_including_vat,
      };
    }

    this.syncAllocationWithTaxAuthorityRules();
    this.checkYearlyCapThresholdAndNotify();
    this.checkPerInvoiceThresholdAndNotify();
    this.cdr.markForCheck();
  }

  private applyLocalForeignToIlsFallback(): void {
    const rate = this.getEffectiveExchangeRate();
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items.at(i);
      const subTotalForeign = parseFloat(item.get('sub_total_foreign')?.value) || 0;
      const discountForeign = parseFloat(item.get('discount_foreign')?.value) || 0;
      const vatForeign = parseFloat(item.get('vat_amount_foreign')?.value) || 0;
      const totalForeign = parseFloat(item.get('total_amount_foreign')?.value) || 0;
      const priceForeign = parseFloat(item.get('price_per_unit_foreign')?.value) || 0;
      item.patchValue(
        {
          sub_total: convertForeignToIls(subTotalForeign, rate),
          discount: convertForeignToIls(discountForeign, rate),
          vat_amount: convertForeignToIls(vatForeign, rate),
          total_amount: convertForeignToIls(totalForeign, rate),
          price_per_unit: convertForeignToIls(priceForeign, rate),
        },
        { emitEvent: false },
      );
    }
    this.applyIlsAmountsFromItems();
  }

  /**
   * Get payment method name by type code
   */
  getPaymentMethodName(typeCode: number): string {
    const paymentMethod = this.paymentMethodsList.find(
      (pm) => +pm.payment_method_code === +typeCode
    );
    if (!paymentMethod) return '';
    
    if (this.curentLang === 'en') {
      return paymentMethod.payment_method_name_en || '';
    } else if (this.curentLang === 'ar') {
      return paymentMethod.payment_method_name_ar || '';
    } else {
      return paymentMethod.payment_method_name_he || '';
    }
  }

  /** Sum payment rows with amount > 0 (empty/zero rows are excluded). */
  private sumNonZeroPaymentAmounts(): number {
    let total = 0;
    this.paymentMethods.controls.forEach((pm) => {
      const amount = parseFloat(pm.get('amount')?.value) || 0;
      if (amount > 0) {
        total += amount;
      }
    });
    return Math.round(total * 100) / 100;
  }

  getPaymentRowAmount(payment: AbstractControl): number {
    return parseFloat(payment.get('amount')?.value) || 0;
  }

  getNonZeroPaymentCount(): number {
    return this.getNonZeroPaymentControls().length;
  }

  getNonZeroPaymentControls(): AbstractControl[] {
    return this.paymentMethods.controls.filter(
      (pm) => this.getPaymentRowAmount(pm) > 0,
    );
  }

  /**
   * Calculate total payments amount (non-zero rows only).
   */
  getTotalPayments(): number {
    return this.sumNonZeroPaymentAmounts();
  }

  /**
   * "طلب رقم تخصيص" is offered only for tax invoice, invoice+receipt,
   * reverse invoice, and financial request.
   */
  private isAllocationRequestDocumentType(invoiceType: number): boolean {
    const type = +invoiceType;
    return (
      type === InvoiceType.TAX_INVOICE ||
      type === InvoiceType.INVOICE_WITH_RECEIPT ||
      type === InvoiceType.REVERSE_INVOICE ||
      type === InvoiceType.FINANCIAL_REQUEST
    );
  }

  /**
   * Returns the per-invoice amount-before-VAT threshold for mandatory tax authority reporting.
   * Regulation: ₪10,000 before 1 June 2026; ₪5,000 from 1 June 2026 onward.
   * Uses the invoice date (not today's date) so that invoices dated on/after June 1
   * correctly apply the new threshold regardless of when they are being created.
   */
  private getPerInvoiceThreshold(invoiceDate?: Date | string | null): number {
    const june1 = new Date(2026, 5, 1);
    const effective = invoiceDate ? new Date(invoiceDate) : new Date();
    return effective >= june1 ? 5000 : 10000;
  }

  /** Reads the invoice date from the form and returns it as a Date (or null if not set). */
  private getInvoiceDateFromForm(): Date | null {
    const raw = this.invoiceForm?.get('invoice_date')?.value;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * Returns true when the document type is subject to the per-invoice tax authority threshold rule.
   * Only Tax Invoice (305) and Tax Invoice / Receipt (320) qualify per regulation.
   */
  private isPerInvoiceThresholdEligibleType(type: number): boolean {
    return type === InvoiceType.TAX_INVOICE || type === InvoiceType.INVOICE_WITH_RECEIPT;
  }

  /**
   * When line totals (before VAT) strictly exceed the per-invoice legal threshold for types
   * 305/320, auto-enable tax authority submission once per below→above transition.
   * When totals drop back to or below the threshold, auto-clear allocation_number (unless the
   * yearly-cap rule already triggered it independently).
   */
  private checkPerInvoiceThresholdAndNotify(): void {
    if (this.isPopulatingForm || this.copyFlowPending) {
      return;
    }
    const type = this.invoiceForm.get('invoice_type')?.value;
    if (type == null) {
      return;
    }
    const companyId = this.invoiceForm.get('company_id')?.value;
    const meta = this.resolveSelectedInvoiceType(+type);

    if (
      !this.isPerInvoiceThresholdEligibleType(+type) ||
      !this.canSendToTaxAuthority(+type, meta, companyId)
    ) {
      this.perInvoiceBelowThreshold = true;
      return;
    }

    const threshold = this.getPerInvoiceThreshold(this.getInvoiceDateFromForm());
    const beforeVat = this.calculatedAmounts.amount_before_discount ?? 0;
    const exceeded = beforeVat > threshold;

    if (!exceeded) {
      const wasAbove = !this.perInvoiceBelowThreshold;
      this.perInvoiceBelowThreshold = true;
      // Dropped below threshold — clear allocation only if yearly cap is also not triggered
      if (wasAbove && this.yearlyCapBelowThreshold) {
        if (
          this.isSendToTaxAuthorityCheckboxEnabled &&
          +this.invoiceForm.get('allocation_number')?.value === 1
        ) {
          this.invoiceForm.patchValue({ allocation_number: 0 }, { emitEvent: false });
          if (this.isEditMode && this.invoiceId != null && this.invoiceConfirmFromServer === 0) {
            this.invoiceV2Service.updateAllocationNumber(this.invoiceId, 0).subscribe({
              next: () => {},
              error: (err) => {
                this.invoiceForm.patchValue({ allocation_number: 1 }, { emitEvent: false });
                this.toastr.error(
                  this.getApiErrorMessage(
                    err,
                    this.curentLang === 'ar'
                      ? 'تعذر تحديث إعداد الإرسال لمصلحة الضرائب'
                      : this.curentLang === 'he'
                        ? 'לא ניתן לעדכן את שליחת רשות המסים'
                        : 'Could not update tax authority send setting',
                  ),
                );
              },
            });
          }
        }
      }
      return;
    }

    // Amount strictly exceeds threshold
    if (!this.perInvoiceBelowThreshold) {
      return; // Already triggered — no repeated fire
    }
    this.perInvoiceBelowThreshold = false;

    if (!this.isSendToTaxAuthorityCheckboxEnabled) {
      return;
    }
    if (+this.invoiceForm.get('allocation_number')?.value === 1) {
      return;
    }

    this.invoiceForm.patchValue({ allocation_number: 1 }, { emitEvent: false });

    if (this.isEditMode && this.invoiceId != null && this.invoiceConfirmFromServer === 0) {
      this.invoiceV2Service.updateAllocationNumber(this.invoiceId, 1).subscribe({
        next: () => {},
        error: (err) => {
          this.invoiceForm.patchValue({ allocation_number: 0 }, { emitEvent: false });
          this.toastr.error(
            this.getApiErrorMessage(
              err,
              this.curentLang === 'ar'
                ? 'تعذر تحديث إعداد الإرسال لمصلحة الضرائب'
                : this.curentLang === 'he'
                  ? 'לא ניתן לעדכן את שליחת רשות המסים'
                  : 'Could not update tax authority send setting',
            ),
          );
        },
      });
    }
  }

  private resolveSelectedInvoiceType(type: number): any {
    return (
      this.invoiceTypes.find((t) => +t.id === +type) ||
      this.allInvoiceTypes.find((t) => +t.id === +type)
    );
  }

  /**
   * Localized document-type label from API (same as ng-select options) for dynamic copy (إيصال، فاتورة…).
   */
  get selectedDocumentTypeLabel(): string {
    const typeId = this.invoiceForm?.get('invoice_type')?.value;
    const row = this.resolveSelectedInvoiceType(typeId);
    if (!row) {
      return this.selectedDocumentTypeFallback();
    }
    const lang = this.curentLang || 'en';
    if (lang === 'en') {
      return String(row.name_en || row.name_ar || 'document').trim();
    }
    if (lang === 'ar') {
      return String(row.name_ar || row.name_en || 'المستند').trim();
    }
    return String(row.name_he || row.name_en || 'מסמך').trim();
  }

  private selectedDocumentTypeFallback(): string {
    const lang = this.curentLang || 'en';
    if (lang === 'ar') {
      return 'المستند';
    }
    if (lang === 'he') {
      return 'מסמך';
    }
    return 'document';
  }

  /**
   * Bumped in {@link onInvoiceTypeChange} so ngx-translate pipes refresh `{{type}}` substitution when the dropdown changes.
   */
  documentTypeUiRevision = 0;

  /** Params for translate pipe on strings that include {{type}}. */
  get documentTypeTranslateParams(): { type: string; _rev: number } {
    return { type: this.selectedDocumentTypeLabel, _rev: this.documentTypeUiRevision };
  }

  /** Card header: Create/Edit + selected type name */
  get pageHeadingTitle(): string {
    const params = { type: this.selectedDocumentTypeLabel };
    const key = this.isEditMode
      ? 'invoices_v2.form.update_invoice'
      : 'invoices_v2.form.create_invoice';
    return this.translate.instant(key, params);
  }

  private getCompanyTypeIdForCompany(companyId: number | null | undefined): number | null {
    if (companyId == null || companyId === ('' as any)) {
      return null;
    }
    const row = this.companies.find((c) => +c.company_id === +companyId);
    const raw =
      row?.fullData?.company_type_id ?? row?.fullData?.companyType?.company_type_id;
    if (raw == null) {
      return null;
    }
    return +raw;
  }

  /**
   * Parse invoice date control value to calendar year (for yearly-max-amount API).
   */
  private getYearFromInvoiceDateValue(value: any): number {
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value.getFullYear();
    }
    if (typeof value === 'string' && value) {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        return parsed.getFullYear();
      }
    }
    return new Date().getFullYear();
  }

  private logTaxDebug(message: string, payload?: Record<string, unknown>): void {
    if (environment.production) {
      return;
    }
else {
      }
  }

  /**
   * Load yearly before-VAT ceiling for the invoice date’s year (exempt / nonprofit gating).
   */
  refreshYearlyMaxAmount(): void {
    const year = this.getYearFromInvoiceDateValue(this.invoiceForm?.get('invoice_date')?.value);
    const invoiceDateRaw = this.invoiceForm?.get('invoice_date')?.value;
    this.logTaxDebug('refreshYearlyMaxAmount: requesting', {
      year,
      invoice_date_control: invoiceDateRaw,
      urlHint: `GET yearly-max-amount?year=${year}`,
    });
    this.httpHelpService.getYearlyMaxAmount(year).subscribe({
      next: (rows: any) => {
        const arr = Array.isArray(rows) ? rows : Array.isArray(rows?.data) ? rows.data : [];
        const row = arr[0];
        const raw = row?.maxAmount ?? row?.max_amount;
        const parsed = raw != null && raw !== '' && !isNaN(+raw) ? +raw : null;
        // Clamp the API value upward to the regulatory minimum for the invoice date period.
        // Before 1 June 2026 the legal threshold is ₪10,000; from 1 June 2026 it drops to ₪5,000.
        // Uses the invoice date (not today) so pre-June-1 invoices aren't triggered by a backend
        // that already stores 5,000 for the full year.
        const regulatoryMin = this.getPerInvoiceThreshold(this.getInvoiceDateFromForm());
        this.yearlyMaxAmountBeforeVat =
          parsed != null ? Math.max(parsed, regulatoryMin) : null;
        this.yearlyMaxAmountLoaded = true;
        this.logTaxDebug('refreshYearlyMaxAmount: response OK', {
          year,
          rawResponseSample: Array.isArray(rows) ? rows : rows,
          firstRow: row ?? null,
          parsedMaxAmount: this.yearlyMaxAmountBeforeVat,
          arrayLength: arr.length,
        });
        this.initYearlyCapThresholdTracking();
        this.syncAllocationWithTaxAuthorityRules();
      },
      error: (err: unknown) => {
        console.error('[InvoicesV2Form tax] refreshYearlyMaxAmount: HTTP error', err);
        this.yearlyMaxAmountBeforeVat = null;
        this.yearlyMaxAmountLoaded = true;
        this.initYearlyCapThresholdTracking();
        this.syncAllocationWithTaxAuthorityRules();
      },
    });
  }

  /**
   * After load or when yearly max changes: avoid auto-enabling on first paint if invoice is already over the cap.
   */
  private initYearlyCapThresholdTracking(): void {
    const max = this.yearlyMaxAmountBeforeVat;
    if (max == null || max <= 0 || !this.yearlyMaxAmountLoaded) {
      this.yearlyCapBelowThreshold = true;
      return;
    }
    const beforeVat = this.calculatedAmounts.amount_before_discount ?? 0;
    this.yearlyCapBelowThreshold = beforeVat + 1e-9 < max;
  }

  /**
   * When line totals (before VAT) reach the yearly max, set send-to-tax-authority once per
   * below→above transition. When totals fall below the max again, clear it automatically.
   * In edit mode (unconfirmed draft), persists via `updateAllocationNumber` like manual toggles.
   */
  private checkYearlyCapThresholdAndNotify(): void {
    if (this.isPopulatingForm || this.copyFlowPending) {
      return;
    }
    const type = this.invoiceForm.get('invoice_type')?.value;
    if (type == null || +type === InvoiceType.FINANCIAL_REQUEST) {
      return;
    }
    if (!this.yearlyMaxAmountLoaded) {
      return;
    }
    const max = this.yearlyMaxAmountBeforeVat;
    if (max == null || max <= 0) {
      return;
    }
    const meta = this.resolveSelectedInvoiceType(+type);
    if (!this.isSendToTaxAuthorityAllowedByApi(+type, meta)) {
      return;
    }
    const companyId = this.invoiceForm.get('company_id')?.value;
    if (this.isCompanyTypeBlockedFromTaxAuthority(companyId)) {
      return;
    }

    const beforeVat = this.calculatedAmounts.amount_before_discount ?? 0;
    const reached = beforeVat + 1e-9 >= max;
    if (!reached) {
      this.yearlyCapBelowThreshold = true;
      /* Below ceiling: undo auto-send (and manual check per product rule) so UI matches yearly max. */
      if (
        this.isSendToTaxAuthorityCheckboxEnabled &&
        +this.invoiceForm.get('allocation_number')?.value === 1
      ) {
        this.invoiceForm.patchValue({ allocation_number: 0 }, { emitEvent: false });
        if (this.isEditMode && this.invoiceId != null && this.invoiceConfirmFromServer === 0) {
          this.invoiceV2Service.updateAllocationNumber(this.invoiceId, 0).subscribe({
            next: () => {},
            error: (err) => {
              this.invoiceForm.patchValue({ allocation_number: 1 }, { emitEvent: false });
              this.toastr.error(
                this.getApiErrorMessage(
                  err,
                  this.curentLang === 'ar'
                    ? 'تعذر تحديث إعداد الإرسال لمصلحة الضرائب'
                    : this.curentLang === 'he'
                      ? 'לא ניתן לעדכן את שליחת רשות המסים'
                      : 'Could not update tax authority send setting',
                ),
              );
            },
          });
        }
      }
      return;
    }
    if (!this.yearlyCapBelowThreshold) {
      return;
    }
    this.yearlyCapBelowThreshold = false;

    if (!this.isSendToTaxAuthorityCheckboxEnabled) {
      return;
    }
    if (+this.invoiceForm.get('allocation_number')?.value === 1) {
      return;
    }

    this.invoiceForm.patchValue({ allocation_number: 1 }, { emitEvent: false });

    if (this.isEditMode && this.invoiceId != null && this.invoiceConfirmFromServer === 0) {
      this.invoiceV2Service.updateAllocationNumber(this.invoiceId, 1).subscribe({
        next: () => {},
        error: (err) => {
          this.invoiceForm.patchValue({ allocation_number: 0 }, { emitEvent: false });
          this.toastr.error(
            this.getApiErrorMessage(
              err,
              this.curentLang === 'ar'
                ? 'تعذر تحديث إعداد الإرسال لمصلحة الضرائب'
                : this.curentLang === 'he'
                  ? 'לא ניתן לעדכן את שליחת רשות המסים'
                  : 'Could not update tax authority send setting',
            ),
          );
        },
      });
    }
  }

  /**
   * Exempt dealer (4) and nonprofit (3): subject to yearly before-VAT ceiling for tax-authority checkbox.
   */
  isCompanyTypeBlockedFromTaxAuthority(companyId: number | null | undefined): boolean {
    const typeId = this.getCompanyTypeIdForCompany(companyId);
    if (typeId == null) {
      return false;
    }
    return InvoicesV2FormComponent.COMPANY_TYPE_IDS_NO_TAX_AUTHORITY.has(typeId);
  }

  /**
   * Document type must allow reporting; exempt/nonprofit (3,4) cannot use tax authority here.
   * Yearly max does not disable the checkbox — crossing it auto-enables send-to-tax once.
   */
  private canSendToTaxAuthority(
    invoiceType: number,
    selectedInvoiceType: any,
    companyId: number | null | undefined,
  ): boolean {
    if (!this.isSendToTaxAuthorityAllowedByApi(invoiceType, selectedInvoiceType)) {
      return false;
    }
    if (this.isCompanyTypeBlockedFromTaxAuthority(companyId)) {
      return false;
    }
    return true;
  }

  /**
   * If the current type + company cannot send to tax authority, force allocation_number to 0.
   */
  private syncAllocationWithTaxAuthorityRules(): void {
    const companyId = this.invoiceForm.get('company_id')?.value;
    const type = this.invoiceForm.get('invoice_type')?.value;
    if (type == null) {
      this.logTaxDebug('syncAllocation: skip (no invoice_type)');
      return;
    }
    const meta = this.resolveSelectedInvoiceType(+type);
    const beforeVat = this.calculatedAmounts.amount_before_discount ?? 0;
    const companyTypeId = this.getCompanyTypeIdForCompany(companyId);
    const exemptLike = this.isCompanyTypeBlockedFromTaxAuthority(companyId);
    const docTypeAllows = this.isSendToTaxAuthorityAllowedByApi(+type, meta);
    const max = this.yearlyMaxAmountBeforeVat;
    const canSend = this.canSendToTaxAuthority(+type, meta, companyId);
    const allocationBefore = this.invoiceForm.get('allocation_number')?.value;

    if (!environment.production) {
      const key = JSON.stringify({
        companyId,
        companyTypeId,
        type,
        beforeVat,
        max,
        loaded: this.yearlyMaxAmountLoaded,
        exemptLike,
        docTypeAllows,
        canSend,
        allocation: allocationBefore,
      });
      if (key !== this.lastTaxSyncDebugKey) {
        this.lastTaxSyncDebugKey = key;
        this.logTaxDebug('syncAllocation: state', {
          companyId,
          companyTypeId_from_fullData: companyTypeId,
          exemptOrNonprofit_types_3_4: exemptLike,
          invoice_type: type,
          invoice_type_meta_send_to_tax_API: meta?.send_to_tax_authority,
          docTypeAllows_sendToTax: docTypeAllows,
          yearlyMax_loaded: this.yearlyMaxAmountLoaded,
          yearlyMaxAmountBeforeVat: max,
          beforeVat_total_line_subtotals: beforeVat,
          reachedYearlyCap: max != null && max > 0 && beforeVat + 1e-9 >= max,
          canSend_checkbox_enabled_logic: canSend,
          allocation_number_before: allocationBefore,
          companiesRowFound: !!this.companies.find((c) => +c.company_id === +companyId),
        });
      }
    }

    if (!canSend) {
      this.invoiceForm.patchValue({ allocation_number: 0 }, { emitEvent: false });
      if (!environment.production && +allocationBefore !== 0) {
        this.logTaxDebug('syncAllocation: forced allocation_number → 0');
      }
    }
  }

  /**
   * Which notice to show under the tax checkbox when it is disabled.
   */
  get taxAuthorityDisabledNoticeKind(): 'max_missing' | 'company' | 'document' {
    if (this.isSendToTaxAuthorityCheckboxEnabled) {
      return 'document';
    }
    const companyId = this.invoiceForm?.get('company_id')?.value;
    const type = this.invoiceForm?.get('invoice_type')?.value;
    if (type == null) {
      return 'document';
    }
    const meta = this.resolveSelectedInvoiceType(+type);
    if (!this.isSendToTaxAuthorityAllowedByApi(+type, meta)) {
      return 'document';
    }
    if (this.isCompanyTypeBlockedFromTaxAuthority(companyId)) {
      if (this.yearlyMaxAmountLoaded && (this.yearlyMaxAmountBeforeVat == null || this.yearlyMaxAmountBeforeVat <= 0)) {
        return 'max_missing';
      }
      return 'company';
    }
    return 'document';
  }

  /**
   * The allocation checkbox is shown only for tax invoice (305), invoice with receipt (320),
   * reverse invoice (330), and financial request (332).
   */
  isSendToTaxAuthorityAllowedByApi(invoiceType: number, _selectedType?: any): boolean {
    return this.isAllocationRequestDocumentType(invoiceType);
  }

  /**
   * Hide the tax-authority block for document types that cannot report (e.g. Receipt).
   * Still show when the type allows reporting but company or yearly rules disable the checkbox.
   */
  get showTaxAuthorityReportingPanel(): boolean {
    const type = this.invoiceForm?.get('invoice_type')?.value;
    if (type == null) {
      return false;
    }
    const meta = this.resolveSelectedInvoiceType(+type);
    return this.isSendToTaxAuthorityAllowedByApi(+type, meta);
  }

  /** True when the user may toggle "send to tax authority" (allocation_number) for the current type + company. */
  get isSendToTaxAuthorityCheckboxEnabled(): boolean {
    const type = this.invoiceForm?.get('invoice_type')?.value;
    if (type == null) {
      return false;
    }
    const meta = this.resolveSelectedInvoiceType(+type);
    const companyId = this.invoiceForm?.get('company_id')?.value;
    return this.canSendToTaxAuthority(+type, meta, companyId);
  }

  private computeAllocationNumberForSubmit(formValue: any): number {
    const type = formValue.invoice_type;
    const st = this.resolveSelectedInvoiceType(type);
    if (!this.canSendToTaxAuthority(type, st, formValue.company_id)) {
      return 0;
    }
    return +formValue.allocation_number === 1 ? 1 : 0;
  }

  /**
   * Default for "Subject to VAT" (tax_status: 1 = subject, 0 = exempt).
   * Tax invoice, invoice with receipt, financial request, and reverse invoice are subject.
   */
  private isSubjectToVatDefault(invoiceType: number): boolean {
    const type = +invoiceType;
    return (
      type === InvoiceType.TAX_INVOICE ||
      type === InvoiceType.INVOICE_WITH_RECEIPT ||
      type === InvoiceType.FINANCIAL_REQUEST ||
      type === InvoiceType.REVERSE_INVOICE
    );
  }

  /** True when the selected invoice type is Receipt (إيصال) – used to hide VAT select and Send to Tax Authority. */
  get isReceiptType(): boolean {
    const type = this.invoiceForm.get('invoice_type')?.value;
    return type != null && +type === InvoiceType.RECEIPT;
  }

  /** Payment status + due date apply only to tax invoices (305). */
  get showPaymentTrackingFields(): boolean {
    return isPaymentTrackingInvoiceType(this.invoiceForm.get('invoice_type')?.value);
  }

  /** Non-collectible types: auto-paid, no due date. Tax invoice (305): keep user values. */
  private applyPaymentTrackingForInvoiceType(type: number): void {
    if (isPaymentTrackingInvoiceType(type)) {
      return;
    }
    this.invoiceForm.patchValue(
      { payment_status: 'paid', due_date: null },
      { emitEvent: false },
    );
  }

  /**
   * Allowed invoice type codes for the selected company (same rules as Document V1 / CompaniesService).
   * `null` means no company yet — no restriction (show all loaded types).
   */
  private getAllowedInvoiceTypeCodesForCompany(companyId: number | null): number[] | null {
    if (companyId == null) {
      return null;
    }
    const row = this.companies.find((c) => +c.company_id === +companyId);
    const fromApi = row?.fullData?.companyType?.invoice_types;
    if (fromApi && fromApi.length > 0) {
      return fromApi.map((x: number) => +x);
    }
    return InvoicesV2FormComponent.DEFAULT_COMPANY_INVOICE_TYPE_CODES;
  }

  /**
   * Restrict invoice type dropdown to companyType.invoice_types (e.g. معفي vs مرخص), matching legacy documents.
   */
  private applyInvoiceTypesFilterForCompany(
    companyId: number | null,
    options?: { silent?: boolean },
  ): void {
    if (!this.allInvoiceTypes.length) {
      return;
    }
    const allowedCodes = this.getAllowedInvoiceTypeCodesForCompany(companyId);
    let filtered: typeof this.allInvoiceTypes;
    if (allowedCodes == null) {
      filtered = [...this.allInvoiceTypes];
    } else {
      const allowedSet = new Set(allowedCodes);
      filtered = this.allInvoiceTypes.filter((t) => allowedSet.has(+t.id));
      if (filtered.length === 0) {
        filtered = [...this.allInvoiceTypes];
      }
    }
    this.invoiceTypes = filtered;

    if (this.showDocumentTypePicker && !filtered.length) {
      this.showDocumentTypePicker = false;
    }

    const allowedSetForCurrent =
      allowedCodes == null ? null : new Set(allowedCodes);

    const current = this.invoiceForm.get('invoice_type')?.value;
    if (
      current != null &&
      allowedSetForCurrent != null &&
      !allowedSetForCurrent.has(+current)
    ) {
      const replacement = filtered[0] ? +filtered[0].id : +this.allInvoiceTypes[0].id;
      this.invoiceForm.patchValue({ invoice_type: replacement }, { emitEvent: false });
      this.onInvoiceTypeChange(replacement);
      if (!options?.silent) {
        this.toastr.warning(
          this.curentLang === 'ar'
            ? 'تم تعديل نوع المستند ليتوافق مع نوع المصلحة التجارية المسموح به'
            : this.curentLang === 'he'
              ? 'סוג המסמך עודכן בהתאם לסוג החברה המותר'
              : 'Document type was adjusted to match the company’s allowed types',
        );
      }
      return;
    }
    // Do not call onInvoiceTypeChange here when the type stayed valid — it would reset tax/allocation
    // (e.g. after loading an invoice for edit or when only the company changed).
  }

  private isInvoiceTypeAllowedForCompany(companyId: number | null, invoiceType: number): boolean {
    const allowed = this.getAllowedInvoiceTypeCodesForCompany(companyId);
    if (allowed == null) {
      return this.allInvoiceTypes.some((t) => +t.id === +invoiceType);
    }
    return allowed.includes(+invoiceType);
  }

  /**
   * Handle invoice type change
   * @param preserveAllocationAndTax When true, do not overwrite allocation_number / tax_status (edit load).
   */
  onInvoiceTypeChange(type: number, options?: { preserveAllocationAndTax?: boolean }): void {
    const selectedType =
      this.invoiceTypes.find((t) => +t.id === +type) ||
      this.allInvoiceTypes.find((t) => +t.id === +type);

    // Check flags from API - handle boolean and numeric (0/1) values
    // has_items: false/0 means no items required, true/1 or undefined means items required
    const hasItems = selectedType ? (selectedType.has_items === false || selectedType.has_items === 0 ? false : true) : true;
    // has_payments: true/1 means payments required, false/0 or undefined means not required
    const hasPayments = selectedType ? (selectedType.has_payments === true || selectedType.has_payments === 1) : false;
    const companyId = this.invoiceForm.get('company_id')?.value;
    const subjectToVat = this.isSubjectToVatDefault(type);

    this.hasItemsForSelectedType = hasItems;
    this.paymentMethodsRequired = hasPayments;

    const itemsArray = this.invoiceForm.get('items') as FormArray;

    // Items (products/lines) visibility & validation
    if (this.hasItemsForSelectedType) {
      itemsArray.setValidators([Validators.required, Validators.minLength(1)]);
      if (this.items.length === 0 && !this.isPopulatingForm) {
        this.addItem(undefined, { expand: false });
      }
    } else {
      // If this type does not have items, clear validators and remove all items
      itemsArray.clearValidators();
      // Clear all items from the array since they are not needed
      while (itemsArray.length > 0) {
        itemsArray.removeAt(0);
      }
    }
    itemsArray.updateValueAndValidity();

    if (!options?.preserveAllocationAndTax) {
      this.invoiceForm.patchValue(
        {
          allocation_number: 0,
          tax_status: subjectToVat ? 1 : 0,
        },
        { emitEvent: false },
      );
    }

    // Payment methods requirement from backend flag
    if (this.paymentMethodsRequired && this.paymentMethods.length === 0) {
      this.addPaymentMethod(undefined, { scrollIntoView: false });
    }

    this.syncPaymentMethodValidatorsAfterTypeOrCopy();

    // Recalculate predicted document number when invoice type changes (for new invoices)
    if (companyId && !this.isEditMode) {
      this.predictDocumentNumber(companyId);
    }

    if (!this.isPopulatingForm) {
      this.recalculateAllItemsAndTotals();
    }

    this.applyPaymentTrackingForInvoiceType(+type);

    this.documentTypeUiRevision++;
    this.initYearlyCapThresholdTracking();
  }

  /**
   * Checkbox "إرسال لمصلحة الضرائب" changed: set allocation_number (1 = send, 0 = do not send).
   * Independent of tax status (معفي / خاضع).
   */
  onSendToTaxAuthorityChange(event: Event): void {
    if (!this.isSendToTaxAuthorityCheckboxEnabled) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const checked = input.checked;
    const previousAlloc: 0 | 1 = checked ? 0 : 1;
    const nextAlloc: 0 | 1 = checked ? 1 : 0;
    this.invoiceForm.patchValue({ allocation_number: nextAlloc }, { emitEvent: false });

    if (this.isEditMode && this.invoiceId != null && this.invoiceConfirmFromServer === 0) {
      this.invoiceV2Service.updateAllocationNumber(this.invoiceId, nextAlloc).subscribe({
        next: () => {},
        error: (err) => {
          this.invoiceForm.patchValue({ allocation_number: previousAlloc }, { emitEvent: false });
          input.checked = previousAlloc === 1;
          this.toastr.error(
            this.getApiErrorMessage(
              err,
              this.curentLang === 'ar'
                ? 'تعذر تحديث إعداد الإرسال لمصلحة الضرائب'
                : this.curentLang === 'he'
                  ? 'לא ניתן לעדכן את שליחת רשות המסים'
                  : 'Could not update tax authority send setting',
            ),
          );
        },
      });
    }
  }

  /**
   * Predict document number
   */
  predictDocumentNumber(companyId: number): void {
    const invoiceType = this.invoiceForm.get('invoice_type')?.value;
    if (companyId && invoiceType) {
      this.invoiceV2Service.predictDocumentNumber(companyId, invoiceType).subscribe({
        next: (response) => {
          this.predictedDocumentNumber = response.predicted_document_number;
        },
        error: (error) => {
          console.error('Error predicting document number:', error);
        },
      });
    }
  }

  /**
   * Load initial data
   */
  loadInitialData(): void {
    this.loadInvoiceTypes();
    this.loadPaymentMethods();
    this.loadBanks();
    this.loadCompanies();
    this.loadCurrencies();
    this.getVatPercentageByVatListIdAndDate();
    // Customers will be loaded when company is selected
  }

  /**
   * Load payment methods from backend
   */
  loadPaymentMethods(): void {
    this.httpHelpService.getAllActivePaymentMethods().subscribe({
      next: (response: any) => {
        let methodsData: any[] = [];
        if (Array.isArray(response)) {
          methodsData = response;
        } else if (response && Array.isArray(response.data)) {
          methodsData = response.data;
        }

        this.paymentMethodsList = methodsData.filter((pm: any) => pm.status === 1);
      },
      error: (error) => {
        console.error('Error loading payment methods:', error);
        this.toastr.warning(
          this.curentLang === 'ar'
            ? 'خطأ في تحميل طرق الدفع'
            : this.curentLang === 'he'
              ? 'שגיאה בטעינת אמצעי תשלום'
              : 'Error loading payment methods',
        );
      },
    });
  }

  /**
   * Load banks from backend
   */
  loadBanks(): void {
    this.banksLoading = true;
    this.banksLoadError = false;

    this.httpHelpService.GetBankList().subscribe({
      next: (banksData) => {
        this.banks = (banksData || []).map((b: any) => this.decorateBankForSelect(b));
        this.banksLoading = false;
        },
      error: (error) => {
        this.banksLoading = false;
        this.banksLoadError = true;
        this.banks = [];
        console.error('Error loading banks:', error);
        this.toastr.warning(
          this.curentLang === 'ar'
            ? 'خطأ في تحميل البنوك'
            : this.curentLang === 'he'
              ? 'שגיאה בטעינת הבנקים'
              : 'Error loading banks',
        );
      },
    });
  }

  /**
   * Load invoice types from backend
   */
  loadInvoiceTypes(): void {
    this.spinner.show();
    this.httpHelpService.GetInvoiceTypeList().subscribe({
      next: (response: any) => {
        let typesData: any[] = [];
        if (Array.isArray(response)) {
          typesData = response;
        } else if (response && Array.isArray(response.data)) {
          typesData = response.data;
        }

        // Map backend structure to the structure used in the template.
        // Exclude "إيصال ملغي" (430 - Cancelled Receipt): user must not select it when creating/editing.
        const CANCELLED_RECEIPT_TYPE_CODE = 430;
        this.allInvoiceTypes = typesData
          .filter((t: any) => t.status === 1 && t.invoice_type_code !== CANCELLED_RECEIPT_TYPE_CODE)
          .map((t: any) => ({
            id: String(t.invoice_type_code),
            name_en: t.invoice_type_name_en,
            name_ar: t.invoice_type_name_ar,
            name_he: t.invoice_type_name_he,
            has_items: t.has_items,
            has_payments: t.has_payments,
            send_to_tax_authority: t.send_to_tax_authority,
            otp_on_exceed_average: t.otp_on_exceed_average === true || t.otp_on_exceed_average === 1,
            convert_to_invoice_type_code:
              t.convert_to_invoice_type_code != null ? +t.convert_to_invoice_type_code : null,
            can_convert: t.can_convert === true || t.can_convert === 1,
          }));

        this.applyInvoiceTypesFilterForCompany(
          this.invoiceForm.get('company_id')?.value ?? null,
          { silent: true },
        );

        if (!this.isEditMode) {
          const t = this.invoiceForm.get('invoice_type')?.value;
          if (t != null) {
            this.onInvoiceTypeChange(t);
          }
          this.scheduleCreateFormScrollReset();
        } else {
          this.syncAllocationWithTaxAuthorityRules();
        }

        this.spinner.hide();
        this.scheduleDocumentSequenceCheck();
        if (!this.isEditMode) {
          this.invoiceTypesLoadedForDraftRestore = true;
          this.tryOfferLocalDraftRestoreWhenReady();
        }
      },
      error: (error) => {
        console.error('Error loading invoice types:', error);
        this.spinner.hide();
        if (!this.isEditMode) {
          this.invoiceTypesLoadedForDraftRestore = true;
          this.tryOfferLocalDraftRestoreWhenReady();
        }
        this.toastr.error(
          this.curentLang === 'ar'
            ? 'خطأ في تحميل أنواع الفواتير'
            : this.curentLang === 'he'
              ? 'שגיאה בטעינת סוגי החשבוניות'
              : 'Error loading invoice types',
        );
      },
    });
  }

  /**
   * Default starting number for a new document sequence (aligned with `document-head`).
   */
  private getDefaultSequenceStartForDocType(docType: string): number {
    const defaults: Record<string, number> = {
      '305': 10001,
      '320': 40001,
      '332': 50001,
      '400': 30001,
      '330': 20001,
      '430': 60001,
      '100': 70001,
      '200': 80001,
      '0': 90001,
      '000': 90001,
    };
    return defaults[docType] ?? 1;
  }

  /**
   * Debounced: when company + invoice type are set, ensure `company/{id}/sequence/{docType}` exists; if not, prompt like V1 add-invoice.
   */
  private scheduleDocumentSequenceCheck(): void {
    if (this.documentSequenceCheckTimer) {
      clearTimeout(this.documentSequenceCheckTimer);
    }
    this.documentSequenceCheckTimer = setTimeout(() => {
      this.documentSequenceCheckTimer = null;
      this.runDocumentSequenceCheck();
    }, 450);
  }

  private runDocumentSequenceCheck(): void {
    if (this.isEditMode || this.copyFlowPending || this.isPopulatingForm) {
      return;
    }
    const companyId = this.invoiceForm.get('company_id')?.value;
    const invoiceType = this.invoiceForm.get('invoice_type')?.value;
    if (!companyId || invoiceType == null || invoiceType === '') {
      return;
    }
    if (this.documentSequenceCheckInFlight) {
      return;
    }
    const docType = String(+invoiceType);
    this.documentSequenceCheckInFlight = true;
    this.spinner.show();
    this.httpHelpService.checkSequenceExists(+companyId, docType).subscribe({
      next: (exists: boolean) => {
        this.documentSequenceCheckInFlight = false;
        this.spinner.hide();
        if (!exists) {
          this.promptCreateDocumentSequence(+companyId, docType);
        }
      },
      error: () => {
        this.documentSequenceCheckInFlight = false;
        this.spinner.hide();
        this.toastr.error(
          this.curentLang === 'ar'
            ? 'تعذر التحقق من تسلسل المستندات'
            : this.curentLang === 'he'
              ? 'לא ניתן לבדוק את רצף המסמכים'
              : 'Could not verify document sequence',
        );
      },
    });
  }

  /**
   * SweetAlert prompt for initial sequence number — same flow as legacy `add-invoice` / `document-head` intent.
   */
  private promptCreateDocumentSequence(companyId: number, docType: string): void {
    const defaultStart = this.getDefaultSequenceStartForDocType(docType);
    Swal.fire({
      title:
        this.curentLang === 'en'
          ? 'Enter the starting number for the document sequence:'
          : this.curentLang === 'ar'
            ? 'أدخل الرقم الابتدائي لتسلسل المستند:'
            : 'הזן את המספר ההתחלתי של רצף המסמכים:',
      input: 'number',
      inputAttributes: {
        min: '1',
        step: '1',
      },
      inputValue: defaultStart,
      showCancelButton: true,
      confirmButtonText:
        this.curentLang === 'en' ? 'Submit' : this.curentLang === 'ar' ? 'إرسال' : 'שלח',
      cancelButtonText:
        this.curentLang === 'en' ? 'Cancel' : this.curentLang === 'ar' ? 'إلغاء' : 'ביטול',
      allowOutsideClick: false,
      preConfirm: (value) => {
        const n = parseInt(String(value), 10);
        if (!value || isNaN(n) || n < 1) {
          Swal.showValidationMessage(
            this.curentLang === 'en'
              ? 'Please enter a valid number greater than 0'
              : this.curentLang === 'ar'
                ? 'يرجى إدخال رقم صالح أكبر من 0'
                : 'אנא הזן מספר חוקי גדול מ-0',
          );
          return false;
        }
        return n;
      },
    }).then((result) => {
      if (result.isConfirmed && result.value != null) {
        this.createDocumentSequence(companyId, docType, +result.value);
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        this.toastr.warning(
          this.curentLang === 'ar'
            ? 'لم يتم إنشاء التسلسل — قد تحتاج إلى رقم تسلسل لاحقاً'
            : this.curentLang === 'he'
              ? 'הרצף לא נוצר — ייתכן שתזדקק למספר רצף מאוחר יותר'
              : 'Sequence was not created — you may need a sequence later',
        );
      }
    });
  }

  private createDocumentSequence(companyId: number, docType: string, startWith: number): void {
    this.spinner.show();
    this.httpHelpService.createSequence(companyId, docType, startWith).subscribe({
      next: () => {
        this.spinner.hide();
        this.toastr.success(
          this.curentLang === 'ar'
            ? 'تم إنشاء تسلسل المستندات بنجاح'
            : this.curentLang === 'he'
              ? 'רצף המסמכים נוצר בהצלחה'
              : 'Document sequence created successfully',
        );
        if (!this.isEditMode) {
          this.predictDocumentNumber(companyId);
        }
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error(
          this.curentLang === 'ar'
            ? 'فشل إنشاء تسلسل المستندات'
            : this.curentLang === 'he'
              ? 'יצירת רצף המסמכים נכשלה'
              : 'Failed to create document sequence',
        );
      },
    });
  }

  /**
   * Load the company product catalog once. The dropdown filters that list locally.
   * A later call for the same company is ignored unless a new product must be selected.
   */
  loadProducts(
    companyId?: number | null,
    selectProductId?: number,
    itemIndex?: number,
    clearInvalidLineProducts = false,
    fallbackProduct?: any,
  ): void {
    this.setupProductCatalog();

    const cid = this.parseCompanyId(companyId ?? this.invoiceForm.get('company_id')?.value);
    if (cid == null) {
      this.products = [];
      this.loadedProductsCompanyId = null;
      this.pendingProductsCompanyId = null;
      this.productCatalogTruncated = false;
      this.productsLoading = false;
      if (clearInvalidLineProducts) {
        this.clearLineItemsWithInvalidProducts(null);
      }
      this.cdr.markForCheck();
      return;
    }

    const refresh = selectProductId != null || fallbackProduct != null;
    if (!refresh && this.loadedProductsCompanyId === cid) {
      if (clearInvalidLineProducts) {
        this.clearLineItemsWithInvalidProducts(cid);
      }
      this.ensureMissingSelectedProductsInList();
      return;
    }
    if (!refresh && this.productsLoading && this.pendingProductsCompanyId === cid) {
      return;
    }

    if (this.loadedProductsCompanyId !== cid) {
      // Keep previous list visible until the first page of the new company arrives
      // only when switching companies we clear after marking loading.
      this.loadedProductsCompanyId = null;
      this.productCatalogTruncated = false;
      this.productOverflowSearch$.next('');
      this.products = [];
    }

    this.pendingProductsCompanyId = cid;
    this.productLoad$.next({
      companyId: cid,
      selectProductId,
      itemIndex,
      clearInvalidLineProducts,
      fallbackProduct,
    });
  }

  private setupProductCatalog(): void {
    if (this.productLoadSub) {
      return;
    }

    // Fast first page so the dropdown is usable immediately.
    this.productLoadSub = this.productLoad$
      .pipe(
        switchMap((request) => {
          this.productsLoading = true;
          this.cdr.markForCheck();
          return this.httpHelpService
            .getActiveProductsForPicker(this.PRODUCT_FIRST_PAGE, 0, undefined, request.companyId)
            .pipe(
              map((page) => ({ request, page, failed: false as const })),
              catchError((error) => {
                console.error('Error loading products:', error);
                return of({
                  request,
                  page: { products: [] as any[], total: 0 },
                  failed: true as const,
                });
              }),
            );
        }),
      )
      .subscribe(({ request, page, failed }) => {
        if (this.pendingProductsCompanyId !== request.companyId) {
          return;
        }

        this.productsLoading = false;
        this.pendingProductsCompanyId = null;

        if (failed) {
          this.products = [];
          this.loadedProductsCompanyId = null;
          this.productCatalogTruncated = false;
          this.toastr.warning(
            this.curentLang === 'ar'
              ? 'خطأ في تحميل المنتجات'
              : this.curentLang === 'he'
                ? 'שגיאה בטעינת המוצרים'
                : 'Error loading products',
          );
          this.cdr.markForCheck();
          return;
        }

        const productsData = Array.isArray(page.products) ? page.products : [];
        const total = Number(page.total) || productsData.length;
        this.productCatalogTruncated = total > productsData.length;
        this.setProductsFromApi(productsData, true);
        this.loadedProductsCompanyId = request.companyId;

        if (request.clearInvalidLineProducts) {
          this.clearLineItemsWithInvalidProducts(request.companyId);
        }

        this.ensureMissingSelectedProductsInList();

        if (
          request.selectProductId != null &&
          request.fallbackProduct &&
          !this.products.some((p) => +p.product_id === +request.selectProductId)
        ) {
          const mapped = this.mapProductOption({
            ...request.fallbackProduct,
            product_id: request.selectProductId,
          });
          if (mapped?.product_id) {
            this.products = [mapped, ...this.products];
          }
        }

        if (request.selectProductId != null && request.itemIndex != null) {
          const item = this.items.at(request.itemIndex);
          if (item) {
            item.patchValue({ product_id: +request.selectProductId });
          }
        }

        this.cdr.markForCheck();

        // Fill remaining pages in the background (does not block the dropdown).
        if (total > productsData.length) {
          this.fillProductCatalogPages(
            request.companyId,
            productsData.length,
            Math.min(total, this.PRODUCT_CATALOG_LIMIT),
          );
        }
      });

    this.productOverflowSub = this.productOverflowSearch$
      .pipe(
        debounceTime(250),
        map((term) => term.trim()),
        distinctUntilChanged(),
        filter((term) => term.length > 0),
        switchMap((term) => {
          const companyId =
            this.loadedProductsCompanyId ??
            this.pendingProductsCompanyId ??
            this.parseCompanyId(this.invoiceForm.get('company_id')?.value);
          if (companyId == null) {
            return of({ companyId: null as number | null, products: [] as any[] });
          }
          return this.httpHelpService
            .getAllActiveProductsBasedOnMerchantId(
              this.PRODUCT_OVERFLOW_SEARCH_LIMIT,
              0,
              term,
              companyId,
            )
            .pipe(
              map((products) => ({
                companyId,
                products: Array.isArray(products) ? products : [],
              })),
              catchError(() => of({ companyId, products: [] as any[] })),
            );
        }),
      )
      .subscribe(({ companyId, products }) => {
        if (
          companyId == null ||
          (this.loadedProductsCompanyId != null && companyId !== this.loadedProductsCompanyId) ||
          !products.length
        ) {
          return;
        }
        this.mergeProductOptions(products);
        this.cdr.markForCheck();
      });
  }

  /** Sequential background pages — keeps the first-page dropdown responsive. */
  private fillProductCatalogPages(companyId: number, offset: number, total: number): void {
    const nextLimit = Math.min(this.PRODUCT_PAGE_SIZE, total - offset);
    if (nextLimit <= 0 || companyId !== this.loadedProductsCompanyId) {
      return;
    }

    this.productBackgroundFillSub?.unsubscribe();
    this.productBackgroundFillSub = this.httpHelpService
      .getActiveProductsForPicker(nextLimit, offset, undefined, companyId)
      .subscribe({
        next: (page) => {
          if (companyId !== this.loadedProductsCompanyId) {
            return;
          }
          const chunk = Array.isArray(page.products) ? page.products : [];
          if (chunk.length) {
            this.mergeProductOptions(chunk);
          }
          const knownTotal = Number(page.total) || total;
          const nextOffset = offset + chunk.length;
          this.productCatalogTruncated = knownTotal > this.products.length;
          this.cdr.markForCheck();

          if (chunk.length > 0 && nextOffset < Math.min(knownTotal, this.PRODUCT_CATALOG_LIMIT)) {
            this.fillProductCatalogPages(
              companyId,
              nextOffset,
              Math.min(knownTotal, this.PRODUCT_CATALOG_LIMIT),
            );
          }
        },
        error: (error) => {
          console.error('Error filling product catalog:', error);
        },
      });
  }

  private setProductsFromApi(rawProducts: any[], preserveSelected: boolean): void {
    const mapped = rawProducts.map((product: any) => this.mapProductOption(product));
    if (!preserveSelected) {
      this.products = mapped;
      return;
    }

    const byId = new Map<number, (typeof this.products)[number]>();
    for (const product of mapped) {
      byId.set(+product.product_id, product);
    }
    for (const selectedId of this.getSelectedLineProductIds()) {
      const existing = this.products.find((p) => +p.product_id === +selectedId);
      if (existing && !byId.has(+selectedId)) {
        byId.set(+selectedId, existing);
      }
    }
    this.products = Array.from(byId.values());
  }

  /** Append server matches without dropping the catalog already used for local search. */
  private mergeProductOptions(rawProducts: any[]): void {
    const byId = new Map(this.products.map((product) => [+product.product_id, product]));
    for (const raw of rawProducts) {
      const mapped = this.mapProductOption(raw);
      if (mapped?.product_id) {
        byId.set(+mapped.product_id, mapped);
      }
    }
    this.products = Array.from(byId.values());
  }

  /** Keep line selections visible when the product is outside the first API page. */
  private ensureMissingSelectedProductsInList(): void {
    const missing = this.getSelectedLineProductIds().filter(
      (id) => !this.products.some((p) => +p.product_id === +id),
    );
    if (!missing.length) {
      return;
    }

    forkJoin(
      missing.map((id) =>
        this.httpHelpService.GetProductbycode(id).pipe(
          catchError(() => of(null)),
          map((response) => {
            if (response == null) {
              return null;
            }
            return this.mapProductOption(this.unwrapProductApiPayload(response));
          }),
        ),
      ),
    ).subscribe((fetched) => {
      const toAdd = fetched.filter((p): p is NonNullable<typeof p> => !!p?.product_id);
      if (!toAdd.length) {
        return;
      }
      const byId = new Map(this.products.map((p) => [+p.product_id, p]));
      for (const product of toAdd) {
        byId.set(+product.product_id, product);
      }
      this.products = Array.from(byId.values());
      this.cdr.markForCheck();
    });
  }

  private mapProductOption(product: any): {
    product_id: number;
    company_id: number | null;
    product_name_en: string;
    product_name_ar: string;
    product_name_he: string;
    price: number;
    product_code: string;
    barcode: string;
    catalog_id: string;
    category: string;
    categories_id: number | null;
    measurement_unit_id: number | null;
    measure_unit_description: string;
    description: string;
    des_en: string;
    des_ar: string;
    des_he: string;
  } {
    const rawCompanyId = product.company_id ?? product.company?.company_id ?? product.company?.code;
    const companyId =
      rawCompanyId != null && rawCompanyId !== '' && !Number.isNaN(+rawCompanyId) ? +rawCompanyId : null;

    return {
      product_id: +product.product_id,
      company_id: companyId,
      product_name_en: product.product_name_en || '',
      product_name_ar: product.product_name_ar || '',
      product_name_he: product.product_name_he || '',
      price: product.price || 0,
      product_code: product.product_code || '',
      barcode: product.barcode || '',
      catalog_id: product.catalog_id || product.product_code || '',
      category: product.category || product.categories?.categories_code || '',
      categories_id: product.categories_id || product.categories?.categories_id || null,
      measurement_unit_id: product.measurement_unit_id || product.measurementUnits?.measurement_unit_id || null,
      measure_unit_description:
        product.measure_unit_description ||
        product.measurementUnits?.[`measurement_unit_name_${this.curentLang}`] ||
        '',
      description: product[`des_${this.curentLang}`] || product.description || '',
      des_en: product.des_en || product.description || '',
      des_ar: product.des_ar || product.description || '',
      des_he: product.des_he || product.description || '',
    };
  }

  private getSelectedLineProductIds(): number[] {
    const ids: number[] = [];
    this.items.controls.forEach((ctrl) => {
      const pid = ctrl.get('product_id')?.value;
      if (pid != null && pid !== '' && !Number.isNaN(+pid)) {
        ids.push(+pid);
      }
    });
    return ids;
  }

  /**
   * Clear product_id on lines whose product belongs to a *different* company.
   * Merchant-wide products (company_id null) stay valid for any company.
   */
  private clearLineItemsWithInvalidProducts(companyId: number | null): void {
    this.items.controls.forEach((ctrl) => {
      const productId = ctrl.get('product_id')?.value;
      if (productId == null || productId === '') {
        return;
      }
      const product = this.products.find((p) => +p.product_id === +productId);
      if (!product) {
        return;
      }
      if (
        companyId != null &&
        product.company_id != null &&
        +product.company_id !== +companyId
      ) {
        ctrl.patchValue({ product_id: null }, { emitEvent: false });
      }
    });
  }

  /**
   * Get VAT percentage from backend
   */
  getVatPercentageByVatListIdAndDate(): void {
    let invoiceDate = this.invoiceForm.get('invoice_date')?.value;
    if (!invoiceDate || !this.vatListId) {
      return;
    }

    // Format date to YYYY-MM-DD string for API call
    if (invoiceDate instanceof Date) {
      invoiceDate = this.datePipe.transform(invoiceDate, 'yyyy-MM-dd');
    } else if (typeof invoiceDate === 'string' && invoiceDate.includes('/')) {
      // Handle DD/MM/YYYY format from PrimeNG calendar display
      const parts = invoiceDate.split('/');
      if (parts.length === 3) {
        invoiceDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

    this.httpHelpService.getVatPercentageByVatListIdAndDate(this.vatListId, invoiceDate).subscribe({
      next: (response) => {
        // Handle different response formats
        let vatData: any = null;
        if (Array.isArray(response) && response.length > 0) {
          vatData = response[0];
        } else if (response && response.vat_percentage !== undefined) {
          vatData = response;
        }

        if (vatData && vatData.vat_percentage !== undefined) {
          this.vatPercentage = vatData.vat_percentage;
          if (!this.isInvoiceTaxExempt()) {
            this.items.controls.forEach((ctrl) => {
              ctrl.patchValue({ vat_rate: this.vatPercentage }, { emitEvent: false });
            });
          }
          this.recalculateAllItemsAndTotals();
        }
      },
      error: (error) => {
        console.error('Error loading VAT percentage:', error);
        // Use default VAT rate
        this.vatPercentage = 17;
        this.recalculateAllItemsAndTotals();
      },
    });
  }

  /**
   * Handle product selection.
   * Apply the catalog row immediately so the line is usable; enrich from the
   * detail API in the background when that payload has extra fields.
   */
  onProductSelect(productId: number, itemIndex: number): void {
    if (!productId) {
      return;
    }

    const normalizedProductId = +productId;
    const product = this.products.find((p) => +p.product_id === normalizedProductId);
    if (!product) {
      this.toastr.warning(
        this.curentLang === 'ar'
          ? 'المنتج غير موجود'
          : this.curentLang === 'he'
            ? 'המוצר לא נמצא'
            : 'Product not found',
      );
      return;
    }

    // Instant path — catalog already has name/price/description for the picker.
    // Do NOT wait on getProductById (can take many seconds and made selection feel broken).
    this.applyProductDataToLine(itemIndex, product, product);

    this.httpHelpService.GetProductbycode(normalizedProductId).subscribe({
      next: (productResponse: any) => {
        const item = this.items.at(itemIndex);
        if (!item || +item.get('product_id')?.value !== normalizedProductId) {
          return;
        }
        this.applyProductDataToLine(itemIndex, productResponse, product);
      },
      error: (error) => {
        // Catalog apply already succeeded; detail enrich is best-effort.
        console.error('Error loading product details:', error);
      },
    });
  }

  /** Patch a line item from catalog and/or detail-API product payloads. */
  private applyProductDataToLine(
    itemIndex: number,
    productDataRaw: unknown,
    catalogFallback?: {
      product_id?: number;
      product_name_ar?: string;
      product_name_en?: string;
      product_name_he?: string;
      price?: number;
      product_code?: string;
      catalog_id?: string;
      category?: string;
      categories_id?: number | null;
      measurement_unit_id?: number | null;
      measure_unit_description?: string;
      description?: string;
      des_en?: string;
      des_ar?: string;
      des_he?: string;
    } | null,
  ): void {
    const item = this.items.at(itemIndex);
    if (!item) {
      return;
    }

    const productData = this.unwrapProductApiPayload(productDataRaw);
    const normalizedProductId = +(
      productData['product_id'] ??
      catalogFallback?.product_id ??
      item.get('product_id')?.value ??
      0
    );
    if (!normalizedProductId) {
      return;
    }

    const productName =
      this.curentLang === 'ar'
        ? String(
            productData['product_name_ar'] ||
              catalogFallback?.product_name_ar ||
              productData['product_name_en'] ||
              catalogFallback?.product_name_en ||
              '',
          )
        : this.curentLang === 'he'
          ? String(
              productData['product_name_he'] ||
                catalogFallback?.product_name_he ||
                productData['product_name_en'] ||
                catalogFallback?.product_name_en ||
                '',
            )
          : String(productData['product_name_en'] || catalogFallback?.product_name_en || '');

    const description = String(
      productData[`des_${this.curentLang}`] ||
        catalogFallback?.[`des_${this.curentLang}` as 'des_en' | 'des_ar' | 'des_he'] ||
        productData['description'] ||
        catalogFallback?.description ||
        '',
    );

    const measurementUnits = productData['measurementUnits'] as Record<string, unknown> | undefined;
    const categories = productData['categories'] as Record<string, unknown> | undefined;

    const measureUnitDescription =
      String(measurementUnits?.[`measurement_unit_name_${this.curentLang}`] ?? '').substring(0, 20) ||
      String(
        productData['measure_unit_description'] ||
          catalogFallback?.measure_unit_description ||
          '',
      );

    const productPriceIls = this.resolveProductPriceIls(productDataRaw, catalogFallback);

    const commit = (exchangeRate: number) => {
      const current = this.items.at(itemIndex);
      if (!current || +current.get('product_id')?.value !== normalizedProductId) {
        return;
      }

      const foreign = this.isForeignCurrencySelected();
      const pricePatch = foreign
        ? {
            price_per_unit: productPriceIls,
            price_per_unit_foreign: convertIlsToForeign(productPriceIls, exchangeRate),
          }
        : { price_per_unit: productPriceIls };

      current.patchValue(
        {
          product_id: normalizedProductId,
          item_name: productName,
          ...pricePatch,
          catalog_id:
            String(productData['product_code'] ?? catalogFallback?.product_code ?? '').substring(
              0,
              13,
            ) ||
            String(catalogFallback?.catalog_id ?? '').substring(0, 13) ||
            '',
          category: String(
            categories?.['categories_code'] ||
              productData['category'] ||
              catalogFallback?.category ||
              '',
          ),
          measurement_unit_id:
            measurementUnits?.['measurement_unit_id'] ??
            productData['measurement_unit_id'] ??
            catalogFallback?.measurement_unit_id ??
            null,
          measure_unit_description: measureUnitDescription,
          description: description.substring(0, 300),
          vat_rate: this.vatPercentage || 17,
          quantity: current.get('quantity')?.value || 1,
          discount: 0,
          discount_foreign: 0,
        },
        { emitEvent: false },
      );

      this.calculateItemAmounts(itemIndex);
      this.calculateAmounts();
      this.cdr.markForCheck();
    };

    // ILS (default): apply immediately. Foreign: wait for exchange rate.
    if (!this.isForeignCurrencySelected()) {
      commit(1);
      return;
    }

    this.ensureExchangeRateForDocumentCurrency().subscribe((exchangeRate) => commit(exchangeRate));
  }

  /**
   * Load companies
   */
  loadCompanies(): void {
    this.spinner.show();
    this.companiesService.getCompanies().subscribe({
      next: (companies) => {
        this.companies = companies.map((c: any) => {
          const row = {
            company_id: +c.code,
            company_name_en: c.fullData?.company_name_en ?? '',
            company_name_ar: c.fullData?.company_name_ar ?? '',
            company_name_he: c.fullData?.company_name_he ?? '',
            fullData: c.fullData,
          };
          return {
            ...row,
            displayName: companyDisplayName(row, this.curentLang) || companyDisplayName(c.fullData, this.curentLang),
          };
        });

        if (!environment.production) {
          const sample = this.companies.slice(0, 5).map((c) => ({
            company_id: c.company_id,
            company_type_id:
              c.fullData?.companyType?.company_type_id ?? c.fullData?.company_type_id ?? null,
            hasCompanyTypeObject: !!c.fullData?.companyType,
          }));
          this.logTaxDebug('loadCompanies: companyType on fullData (first 5)', {
            total: this.companies.length,
            sample,
          });
        }

        // Default company: single-company merchants auto-select; else localStorage default
        if (!this.isEditMode) {
          if (this.companies.length === 1) {
            this.invoiceForm.patchValue(
              { company_id: this.companies[0].company_id },
              { emitEvent: true },
            );
          } else {
            const defaultCompanyId = this.parseCompanyId(localStorage.getItem('companyDefault'));
            if (defaultCompanyId) {
              const defaultCompany = this.companies.find(
                (c) => c.company_id === defaultCompanyId,
              );
              if (defaultCompany) {
                this.invoiceForm.patchValue({
                  company_id: defaultCompany.company_id,
                });
              } else {
                this.invoiceForm.patchValue({ company_id: null }, { emitEvent: false });
              }
            }
          }
        } else {
          const currentCompanyId = this.parseCompanyId(this.invoiceForm.get('company_id')?.value);
          const companyExists = currentCompanyId
            ? this.companies.some((c) => c.company_id === currentCompanyId)
            : false;
          if (currentCompanyId && !companyExists) {
            this.invoiceForm.patchValue({ company_id: null }, { emitEvent: false });
          } else if (currentCompanyId && currentCompanyId !== this.invoiceForm.get('company_id')?.value) {
            this.invoiceForm.patchValue({ company_id: currentCompanyId }, { emitEvent: false });
          }
        }
        // Re-apply now that fullData is available (edit/copy, or no default company)
        this.applyInvoiceTypesFilterForCompany(
          this.invoiceForm.get('company_id')?.value ?? null,
          { silent: true },
        );
        this.loadProducts(this.parseCompanyId(this.invoiceForm.get('company_id')?.value));
        this.syncAllocationWithTaxAuthorityRules();
        this.spinner.hide();
        this.scheduleDocumentSequenceCheck();
        if (!this.isEditMode) {
          this.companiesLoadedForDraftRestore = true;
          this.tryOfferLocalDraftRestoreWhenReady();
        }
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.spinner.hide();
        if (!this.isEditMode) {
          this.companiesLoadedForDraftRestore = true;
          this.tryOfferLocalDraftRestoreWhenReady();
        }
        this.toastr.error(
          this.curentLang === 'ar'
            ? 'خطأ في تحميل المصالح التجارية'
            : this.curentLang === 'he'
              ? 'שגיאה בטעינת החברות'
              : 'Error loading companies',
        );
      },
    });
  }

  /**
   * Update customer_id control disabled state (use FormControl enable/disable to avoid reactive form warning)
   */
  private updateCustomerIdDisabledState(): void {
    const companyId = this.invoiceForm.get('company_id')?.value;
    const shouldDisable = !companyId || this.customers.length === 0;
    const control = this.invoiceForm.get('customer_id');
    if (control) {
      if (shouldDisable) {
        control.disable();
      } else {
        control.enable();
      }
    }
  }

  /**
   * Open add-customer modal (same as old invoice). Requires company to be selected.
   */
  openAddCustomerModal(): void {
    const companyId = this.invoiceForm.get('company_id')?.value;
    if (!companyId) {
      this.toastr.warning(
        this.curentLang === 'ar' ? 'يرجى اختيار المصلحة التجارية أولاً' : this.curentLang === 'he' ? 'אנא בחר חברה תחילה' : 'Please select a company first',
      );
      return;
    }
    this.customerSelect?.close();
    this.showAddCustomerModal = true;
  }

  /**
   * When a new customer is saved from the modal: reload customers and select the new one.
   */
  onAddCustomerSaved(newCustomer: any): void {
    if (!newCustomer?.customer_id) {
      this.showAddCustomerModal = false;
      return;
    }
    const companyId = this.invoiceForm.get('company_id')?.value;
    this.showAddCustomerModal = false;
    this.loadCustomersByCompany(companyId, newCustomer.customer_id);
    this.toastr.success(
      this.curentLang === 'ar' ? 'تمت إضافة الزبون بنجاح' : this.curentLang === 'he' ? 'הלקוח נוסף בהצלחה' : 'Customer added successfully',
    );
  }

  /**
   * Close add-customer modal without saving.
   */
  onAddCustomerModalClose(): void {
    this.showAddCustomerModal = false;
  }

  /** Selected invoice company — passed to add-product modal so new products are company-scoped. */
  get invoiceCompanyId(): number | null {
    return this.parseCompanyId(this.invoiceForm?.get('company_id')?.value);
  }

  /**
   * Open add-product modal for the given invoice line item (same as legacy invoice CRUD).
   */
  openAddProductModal(itemIndex: number): void {
    this.addProductItemIndex = itemIndex;
    this.showAddProductModal = true;
  }

  /**
   * When a new product is saved from the modal: reload products and select it on the line.
   */
  onAddProductSaved(newProduct: any): void {
    const productId = newProduct?.product_id;
    const itemIndex = this.addProductItemIndex;
    this.showAddProductModal = false;
    this.addProductItemIndex = null;

    if (!productId || itemIndex == null) {
      return;
    }

    this.loadProducts(undefined, productId, itemIndex, false, newProduct);
    this.toastr.success(
      this.curentLang === 'ar'
        ? 'تمت إضافة المنتج بنجاح'
        : this.curentLang === 'he'
          ? 'המוצר נוסף בהצלחה'
          : 'Product added successfully',
    );
  }

  /**
   * Close add-product modal without saving.
   */
  onAddProductModalClose(): void {
    this.showAddProductModal = false;
    this.addProductItemIndex = null;
  }

  private mapCustomerOption(customer: any): {
    customer_id: number;
    customer_name: string;
    customer_name_en: string;
    customer_name_ar: string;
    customer_name_he: string;
    customer_vat_number: string;
    id_number: string;
  } {
    return {
      customer_id: customer.customer_id || customer.customer_customer_id,
      customer_name:
        customer[`customer_customer_name_${this.curentLang}`] ||
        customer.customer_name_en ||
        customer.customer_name_ar ||
        customer.customer_name_he ||
        customer.customer_name ||
        '',
      customer_name_en: customer.customer_name_en || customer.customer_customer_name_en || '',
      customer_name_ar: customer.customer_name_ar || customer.customer_customer_name_ar || '',
      customer_name_he: customer.customer_name_he || customer.customer_customer_name_he || '',
      customer_vat_number: customer.customer_vat_number || '',
      id_number: customer.id_number || customer.customer_id_number || '',
    };
  }

  /** Keep the selected customer visible in ng-select even if missing from the company list. */
  private ensureCustomerOptionInList(customerId: number, fallback?: any): void {
    if (this.customers.some((customer) => customer.customer_id === customerId)) {
      return;
    }
    const source = fallback ?? { customer_id: customerId };
    this.customers = [...this.customers, this.mapCustomerOption(source)];
  }

  /**
   * Load customers by company ID. Optionally select a customer after load (e.g. after adding new one).
   */
  loadCustomersByCompany(companyId: number, selectCustomerId?: number, fallbackCustomer?: any): void {
    if (!companyId) {
      this.customers = [];
      return;
    }

    this.spinner.show();
    this.httpHelpService.getCustomersByCompanyId(companyId).subscribe({
      next: (response) => {
        // Service already normalises response into a plain array.
        const customersData: any[] = Array.isArray(response) ? response : [];

        this.customers = customersData.map((customer: any) => this.mapCustomerOption(customer));

        if (selectCustomerId != null) {
          this.ensureCustomerOptionInList(selectCustomerId, fallbackCustomer);
          this.invoiceForm.patchValue({ customer_id: selectCustomerId }, { emitEvent: false });
        }
        this.updateCustomerIdDisabledState();
        this.spinner.hide();
        if (!this.isEditMode) {
          this.scheduleRefreshCreateMergeEligibility();
        }
      },
      error: (error) => {
        console.error('Error loading customers:', error);
        this.spinner.hide();
        this.customers = [];
        this.updateCustomerIdDisabledState();
        if (!this.isEditMode) {
          this.clearCreateMergeEligibilityScheduleAndState();
        }
        this.toastr.warning(
          this.curentLang === 'ar'
            ? 'لا توجد عملاء لهذه المصلحة التجارية'
            : this.curentLang === 'he'
              ? 'אין לקוחות לחברה זו'
              : 'No customers found for this company',
        );
      },
    });
  }

  /**
   * Load invoice for edit
   */
  loadInvoiceForEdit(id: number): void {
    this.spinner.show();
    this.invoiceV2Service.getInvoiceById(id).subscribe({
      next: (response: any) => {
        // API may return { data: invoice } or the invoice at top level
        const invoice = (response && response.data) ? response.data : response;
        this.populateForm(invoice);
        this.spinner.hide();
      },
      error: (error) => {
        this.spinner.hide();
        this.toastr.error(this.getApiErrorMessage(error, 'Error loading invoice'));
        this.router.navigate(['/dashboard/invoices-v2']);
      },
    });
  }

  /**
   * Populate form with invoice data
   */
  populateForm(invoice: any): void {
    const confirmValue = invoice.confirm === 1 || invoice.confirm === true || Number(invoice.confirm) === 1;
    this.invoiceConfirmFromServer = confirmValue ? 1 : 0;
    // Set flag to prevent product selection during population
    this.isPopulatingForm = true;

    // Convert invoice_date to Date object for p-calendar
    let invoiceDate: Date | string = invoice.invoice_date;
    if (typeof invoiceDate === 'string') {
      // Try to parse the date string
      const parsedDate = new Date(invoiceDate);
      invoiceDate = isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
    } else if (invoiceDate instanceof Date) {
      // Already a Date object
      invoiceDate = invoiceDate;
    } else {
      // Fallback to today
      invoiceDate = new Date();
    }

    this.invoiceForm.patchValue(
      {
        company_id: this.parseCompanyId(invoice.company_id),
        customer_id: invoice.customer_id != null ? +invoice.customer_id : null,
        invoice_type: invoice.invoice_type,
        invoice_date: invoiceDate,
        due_date: parseDueDateFromApi(invoice.due_date),
        payment_status: invoice.payment_status === 'unpaid' ? 'unpaid' : 'paid',
        document_language: invoice.document_language,
        allocation_number: invoice.allocation_number ?? 0,
        tax_status: invoice.tax_status ?? 1, // UI-only: 0 = exempt, 1 = subject (backend may not have it)
        confirm: confirmValue,
        invoice_note: invoice.invoice_note || '',
        deduction_from_source: invoice.deduction_from_source || 0,
      },
      { emitEvent: false },
    );

    const invoiceCurrency = resolveDocumentCurrency(invoice);
    const foreignCurrency = resolveForeignCurrencyFromInvoice(invoice);
    this.skipExchangeRateFetch = true;
    this.previousCurrencyCode = invoiceCurrency;
    this.invoiceForm.patchValue(
      {
        currency: invoiceCurrency,
        exchange_rate: foreignCurrency?.exchange_rate ?? 1,
        exchange_rate_date: foreignCurrency?.exchange_rate_date ?? '',
        exchange_rate_source: foreignCurrency?.exchange_rate_source ?? 'BOI',
        exchange_rate_manually_changed: !!foreignCurrency?.exchange_rate_manually_changed,
      },
      { emitEvent: false },
    );
    this.skipExchangeRateFetch = false;

    this.applyInvoiceTypesFilterForCompany(invoice.company_id ?? null, { silent: true });
    const it = invoice.invoice_type;
    if (it != null) {
      this.onInvoiceTypeChange(+it, { preserveAllocationAndTax: true });
    }

    if (invoice.company_id) {
      const invoiceCustomer = Array.isArray(invoice.customer) ? invoice.customer[0] : invoice.customer;
      const fallbackCustomer =
        invoiceCustomer ||
        (invoice.customer_id
          ? {
              customer_id: invoice.customer_id,
              customer_name: invoice.customer_name,
              customer_name_en: invoice.customer_name_en,
              customer_name_ar: invoice.customer_name_ar,
              customer_name_he: invoice.customer_name_he,
            }
          : undefined);
      this.loadCustomersByCompany(invoice.company_id, invoice.customer_id, fallbackCustomer);
    }

    const soldItemsRaw: any[] = invoice.soldItems || invoice.sold_items || [];
    this.items.clear();
    if (soldItemsRaw.length > 0) {
      soldItemsRaw.forEach((raw: any, index: number) => {
        this.addItem(this.mapApiSoldItemToDto(raw, index), { skipInvoiceTotals: true });
      });
    }

    this.loadProducts(this.parseCompanyId(invoice.company_id));

    // Reset flag after form is populated
    setTimeout(() => {
      this.isPopulatingForm = false;
      this.loadProducts(this.parseCompanyId(invoice.company_id));
      this.initYearlyCapThresholdTracking();
    }, 500);

    // Populate payment methods
    if (invoice.receiptsDetails && invoice.receiptsDetails.length > 0) {
      this.paymentMethods.clear();
      // Convert receiptsDetails to payment_methods format
      invoice.receiptsDetails.forEach((receipt: any, index: number) => {
        const payment: PaymentMethodDto = {
          type: receipt.payment_type || PaymentType.CASH,
          amount: receipt.amount || 0,
          details: {
            date: receipt.date,
            reference: receipt.reference,
            bank: receipt.bank,
            branch: receipt.branch,
            account: receipt.account,
            details: receipt.details,
            credit_card_code: receipt.credit_card_code,
            credit_card_name: receipt.credit_card_name,
            credit_type: receipt.credit_type,
            account_number: receipt.account_number,
          },
        };
        const paymentForm = this.createPaymentMethodFormGroup(payment);
        this.paymentMethods.push(paymentForm);
        // Setup listeners for each payment method
        this.setupPaymentMethodListeners(index);
      });
    }

    this.applyPaymentValidationForConfirmState();
    this.recalculateAllItemsAndTotals();
    this.refreshYearlyMaxAmount();
  }

  /**
   * Finalize / draft intent from footer buttons — updates `confirm` then runs the same submit pipeline.
   */
  submitWithConfirmIntent(confirmed: boolean): void {
    if (!this.beginSubmitting(confirmed)) {
      return;
    }
    this.invoiceForm.patchValue({ confirm: confirmed }, { emitEvent: false });
    this.applyPaymentValidationForConfirmState();
    this.invoiceForm.updateValueAndValidity({ emitEvent: false });
    this.onSubmit();
  }

  private beginSubmitting(confirmed: boolean): boolean {
    if (this.isSubmitting) {
      return false;
    }
    this.isSubmitting = true;
    this.submittingAsConfirm = confirmed;
    return true;
  }

  private finishSubmitting(): void {
    this.isSubmitting = false;
    this.submittingAsConfirm = null;
  }

  /**
   * Submit form
   */
  onSubmit(): void {
    this.recalculateAllItemsAndTotals();

    const formValue = this.invoiceForm.getRawValue();
    if (this.needsTaxAuthorityLinkPrompt(formValue)) {
      const companyId = this.parseCompanyId(formValue.company_id);
      if (!companyId) {
        this.runSubmitPipeline();
        return;
      }
      this.resolveCompanyTaxLinkActive(companyId).subscribe((isLinked) => {
        if (isLinked) {
          this.runSubmitPipeline();
          return;
        }
        openTaxAuthorityLinkSwal(this.curentLang).then((action) => {
          if (action === 'issue_without') {
            this.invoiceForm.patchValue({ allocation_number: 0 }, { emitEvent: false });
            this.runSubmitPipeline();
          } else if (action === 'link') {
            this.finishSubmitting();
            this.router.navigate(['/dashboard/companies']);
          } else {
            this.finishSubmitting();
          }
        });
      });
      return;
    }

    this.runSubmitPipeline();
  }

  /** Issue with send-to-tax (305/320) while company OAuth token is missing or expired. */
  private needsTaxAuthorityLinkPrompt(formValue: any): boolean {
    if (!formValue?.confirm) {
      return false;
    }
    if (+formValue.allocation_number !== 1) {
      return false;
    }
    const type = +formValue.invoice_type;
    return type === InvoiceType.TAX_INVOICE || type === InvoiceType.INVOICE_WITH_RECEIPT;
  }

  private isRefreshTokenActive(expiration: string | null | undefined): boolean {
    if (expiration == null || expiration === '') {
      return false;
    }
    const exp = new Date(expiration);
    return !Number.isNaN(exp.getTime()) && exp >= new Date();
  }

  /** Uses cached company row when possible; otherwise fetches company info once. */
  private resolveCompanyTaxLinkActive(companyId: number) {
    const company = this.companies.find((c) => +c.company_id === +companyId);
    const cached = company?.fullData?.refresh_token_expiration;
    if (cached !== undefined) {
      return of(this.isRefreshTokenActive(cached));
    }
    return this.httpHelpService.getCompanyInfoById(companyId).pipe(
      map((res) => {
        const exp = res?.refresh_token_expiration;
        if (company?.fullData) {
          company.fullData.refresh_token_expiration = exp ?? null;
        }
        return this.isRefreshTokenActive(exp);
      }),
      catchError(() => of(false)),
    );
  }

  private runSubmitPipeline(): void {
    const correlationId = this.newValidationCorrelationId();
    const validationErrors = this.runFrontendValidation(correlationId);
    if (validationErrors.length > 0) {
      this.markFormGroupTouched(this.invoiceForm);
      this.setActiveValidationErrors(validationErrors, correlationId);
      this.presentValidationErrors(validationErrors, correlationId);
      this.logValidationErrors(validationErrors, correlationId);
      this.finishSubmitting();
      return;
    }

    this.clearValidationHighlight();

    const formValue = this.invoiceForm.value;

    // Eligibility check — only for new invoices, not edits
    if (!this.isEditMode) {
      this.invoiceSubscriptionsV2.canIssueDocument().subscribe({
        next: (eligibility) => {
          if (!eligibility.allowed) {
            Swal.fire({
              icon: 'warning',
              title: this.curentLang === 'ar' ? 'لا يمكن إصدار الفاتورة' : (this.curentLang === 'he' ? 'לא ניתן להנפיק חשבונית' : 'Cannot Issue Invoice'),
              html: this.curentLang === 'ar'
                ? `وصلت للحد الأقصى للفواتير في اشتراكك الحالي (<b>${eligibility.documents_used}/${eligibility.limit}</b>).<br>يرجى شراء إضافة مستندات أو الترقية.`
                : this.curentLang === 'he'
                  ? `הגעת למגבלת המסמכים במנוי הנוכחי שלך (<b>${eligibility.documents_used}/${eligibility.limit}</b>).<br>אנא רכוש הרחבת מסמכים או שדרג.`
                  : `You have reached the document limit of your current subscription (<b>${eligibility.documents_used}/${eligibility.limit}</b>).<br>Please purchase a document extension or upgrade.`,
              confirmButtonText: this.curentLang === 'ar' ? 'إدارة الاشتراك' : (this.curentLang === 'he' ? 'ניהול מנוי' : 'Manage Subscription'),
              showCancelButton: true,
              cancelButtonText: this.curentLang === 'ar' ? 'إلغاء' : (this.curentLang === 'he' ? 'ביטול' : 'Cancel'),
              confirmButtonColor: '#E62E2D',
            }).then((result) => {
              if (result.isConfirmed) {
                this.router.navigate(['/dashboard/invoice-subscriptions']);
              }
              this.finishSubmitting();
            });
          } else {
            this.proceedWithSubmit(formValue);
          }
        },
        error: () => {
          // If the check fails (e.g. network error), proceed anyway — server will enforce limits
          this.proceedWithSubmit(formValue);
        },
      });
      return;
    }

    this.proceedWithSubmit(formValue);
  }

  private proceedWithSubmit(formValue: any): void {
    // Format date to YYYY-MM-DD string (backend expects this format)
    let invoiceDate = formValue.invoice_date;
    if (invoiceDate instanceof Date) {
      invoiceDate = this.datePipe.transform(invoiceDate, 'yyyy-MM-dd');
    } else if (typeof invoiceDate === 'string' && invoiceDate.includes('/')) {
      // Handle DD/MM/YYYY format from PrimeNG calendar display
      const parts = invoiceDate.split('/');
      if (parts.length === 3) {
        invoiceDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

    // Prepare DTO
    const currency = formValue.currency || DEFAULT_CURRENCY_CODE;
    const dto: CreateInvoiceV2Dto | UpdateInvoiceV2Dto = {
      company_id: formValue.company_id,
      customer_id: formValue.customer_id,
      invoice_type: formValue.invoice_type,
      invoice_date: invoiceDate,
      document_language: formValue.document_language,
      currency,
      allocation_number: this.computeAllocationNumberForSubmit(formValue),
      ...resolvePaymentFieldsForSubmit(
        formValue.invoice_type,
        formValue.due_date,
        formValue.payment_status,
      ),
      confirm: formValue.confirm,
      invoice_note: formValue.invoice_note,
      payment_amount: this.calculatedAmounts.payment_amount,
      all_discount: this.calculatedAmounts.all_discount,
      all_vat_amount: this.calculatedAmounts.all_vat_amount,
      payment_amount_including_vat: this.calculatedAmounts.payment_amount_including_vat,
      total_amount: this.calculatedAmounts.total_amount,
      items: this.hasItemsForSelectedType && formValue.items.length > 0
        ? formValue.items.map((item: any) =>
            mapItemForSubmitWithCurrency(
              { ...item, index: undefined },
              currency,
            ),
          )
        : undefined,
      payment_methods:
        formValue.payment_methods.length > 0
          ? formValue.payment_methods.map((pm: any) => ({
              type: +pm.type,
              amount: +pm.amount,
              details: pm.details,
            }))
          : undefined,
    };

    const foreignPayload = buildForeignCurrencyPayload(
      {
        currency,
        exchange_rate: formValue.exchange_rate,
        exchange_rate_date:
          formValue.exchange_rate_date || this.formatInvoiceDateForApi(formValue.invoice_date),
        exchange_rate_source: formValue.exchange_rate_source,
        exchange_rate_manually_changed: !!formValue.exchange_rate_manually_changed,
      },
      {
        subtotal_original: this.foreignCalculatedAmounts.amount_after_discount,
        discount_original: this.foreignCalculatedAmounts.all_discount,
        vat_original: this.foreignCalculatedAmounts.all_vat_amount,
        total_original: this.foreignCalculatedAmounts.payment_amount_including_vat,
      },
    );
    if (foreignPayload) {
      dto.foreign_currency = foreignPayload;
    }

    this.spinner.show();

    if (this.isEditMode && this.invoiceId) {
      // Update
      this.invoiceV2Service.updateInvoice(this.invoiceId, dto as UpdateInvoiceV2Dto).subscribe({
        next: (response: MultiLangMessagePayload | any) => {
          this.spinner.hide();
          if (response && response.success === false) {
            this.handleInvoiceErrorResponse(response);
            return;
          }

          const msg = this.getApiMessage(response);
          this.toastr.success(msg);
          // If confirmed, navigate to view page
          if (formValue.confirm) {
            const navState: { invoiceId: number; openProcedureModal?: boolean } = {
              invoiceId: this.invoiceId,
            };
            if (isProcedureCycleConfirmResponse(response)) {
              navState.openProcedureModal = true;
            }
            this.localDraftBypassNavigation = true;
            this.clearLocalDraft();
            this.router.navigate(['/dashboard/invoices-v2/view'], { state: navState });
          } else {
            this.localDraftBypassNavigation = true;
            this.clearLocalDraft();
            this.router.navigate(['/dashboard/invoices-v2']);
          }
        },
        error: (error) => {
          this.spinner.hide();
          this.handleInvoiceErrorResponse(error?.error);
        },
      });
    } else {
      const companyId = formValue.company_id;

      // Prefer calculated payment_amount_including_vat; fallback to sum of payment methods
      const invoiceAmount =
        (this.calculatedAmounts.payment_amount_including_vat || 0) > 0
          ? this.calculatedAmounts.payment_amount_including_vat
          : this.getTotalPayments();

      // OTP only when the type supports it (e.g. 305, 320) AND the document is sent to tax authority.
      const selectedType = this.allInvoiceTypes.find((t) => String(t.id) === String(formValue.invoice_type));
      const sendsToTaxAuthority = this.computeAllocationNumberForSubmit(formValue) === 1;
      const requiresOtpCheck =
        selectedType?.otp_on_exceed_average === true && sendsToTaxAuthority;

      if (!requiresOtpCheck) {
        this.doCreateInvoice(dto as CreateInvoiceV2Dto);
        return;
      }

      this.invoiceV2Service.getCompanyAverageAmount(companyId).subscribe({
        next: (res) => {
          // العتبة = المتوسط + 10,000 ₪ (threshold محسوب في Backend)
          const threshold = res?.threshold ?? res?.averageAmount ?? 0;
          if (invoiceAmount > threshold) {
            this.invoiceV2Service.sendOtp(companyId, invoiceAmount).subscribe({
              next: () => {
                this.spinner.hide();
                this.showOtpModalAndCreate(companyId, dto as CreateInvoiceV2Dto);
              },
              error: (err) => {
                this.spinner.hide();
                const msg =
                  err?.error?.message ||
                  (this.curentLang === 'ar'
                    ? 'فشل إرسال رمز التحقق'
                    : this.curentLang === 'he'
                      ? 'שליחת קוד האימות נכשלה'
                      : 'Failed to send verification code');
                this.toastr.error(msg);
                this.finishSubmitting();
              },
            });
          } else {
            this.doCreateInvoice(dto as CreateInvoiceV2Dto);
          }
        },
        error: () => {
          // If average API fails, proceed without OTP to avoid blocking creation
          this.doCreateInvoice(dto as CreateInvoiceV2Dto);
        },
      });
    }
  }

  /**
   * Perform the actual create invoice API call (used after OTP verify or when OTP not required)
   */
  private doCreateInvoice(dto: CreateInvoiceV2Dto): void {
    this.spinner.show();
    const formValue = this.invoiceForm.value;
    this.invoiceV2Service.createInvoice(dto).subscribe({
      next: (response: MultiLangMessagePayload | any) => {
        this.spinner.hide();
        if (response && response.success === false) {
          this.handleInvoiceErrorResponse(response);
          return;
        }

        const msg = this.stripAmountValidationNotice(this.getApiMessage(response));
        this.toastr.success(msg);

        this.localDraftBypassNavigation = true;
        this.clearLocalDraft();

        const invoiceId =
          response?.invoice_id ||
          response?.data?.invoice_id ||
          response?.invoiceId ||
          response?.data?.invoiceId ||
          response?.id ||
          response?.data?.id;

        if (formValue.confirm && invoiceId) {
          const inProcedure = isProcedureCycleConfirmResponse(response);
          const navigate = (openModal: boolean) => {
            this.router.navigate(['/dashboard/invoices-v2/view'], {
              state: { invoiceId, openProcedureModal: openModal },
            });
          };
          if (inProcedure) {
            Swal.fire({
              icon: 'warning',
              title: msg,
              text: this.translate.instant('invoices_v2.procedure.confirm_405_hint'),
              confirmButtonColor: '#E62E2D',
            }).then(() => navigate(true));
          } else {
            navigate(false);
          }
        } else {
          this.router.navigate(['/dashboard/invoices-v2']);
        }
      },
      error: (error) => {
        this.spinner.hide();
        this.handleInvoiceErrorResponse(error?.error);
      },
    });
  }

  /**
   * Show OTP modal, verify OTP, then create invoice on success
   */
  private showOtpModalAndCreate(companyId: number, dto: CreateInvoiceV2Dto): void {
    const isAr = this.curentLang === 'ar';
    const isHe = this.curentLang === 'he';

    Swal.fire({
      title: isAr ? 'تحقق من الهوية' : isHe ? 'אימות זהות' : 'Verification Required',
      html:
        isAr
          ? 'مبلغ الفاتورة يتجاوز المتوسط. تم إرسال رمز تحقق (OTP) إلى هاتفك. أدخل الرمز أدناه.'
          : isHe
            ? 'סכום החשבונית חורג מהממוצע. נשלח קוד אימות (OTP) לטלפון שלך. הזן את הקוד למטה.'
            : 'Invoice amount exceeds average. A verification code (OTP) was sent to your phone. Enter the code below.',
      input: 'text',
      inputPlaceholder: '123456',
      inputAttributes: { maxlength: '6', inputmode: 'numeric', pattern: '[0-9]*' },
      showCancelButton: true,
      confirmButtonText: isAr ? 'تحقق' : isHe ? 'אמת' : 'Verify',
      cancelButtonText: isAr ? 'إلغاء' : isHe ? 'ביטול' : 'Cancel',
      confirmButtonColor: '#dc3545',
      customClass: { popup: isAr || isHe ? 'swal2-rtl' : '' },
    }).then((result) => {
      if (!result.isConfirmed) {
        this.finishSubmitting();
        return;
      }

      const otpStr = (result.value || '').trim();
      const otp = parseInt(otpStr, 10);
      if (!otpStr || isNaN(otp) || otpStr.length !== 6) {
        this.toastr.error(
          isAr ? 'أدخل رمز التحقق الصحيح (6 أرقام)' : isHe ? 'הזן קוד אימות תקין (6 ספרות)' : 'Enter a valid 6-digit verification code'
        );
        this.showOtpModalAndCreate(companyId, dto);
        return;
      }

      this.spinner.show();
      this.invoiceV2Service.verifyOtp(companyId, otp).subscribe({
        next: (verifyRes) => {
          if (verifyRes?.verified) {
            this.doCreateInvoice(dto);
          } else {
            this.spinner.hide();
            const msg = verifyRes?.message || (isAr ? 'فشل التحقق' : isHe ? 'האימות נכשל' : 'Verification failed');
            this.toastr.error(msg);
            // If max attempts exceeded, don't re-open modal; user must submit again to request new OTP
            const maxAttemptsMsg = (verifyRes?.message || '').toLowerCase();
            const isMaxAttempts = maxAttemptsMsg.indexOf('maximum') !== -1 || maxAttemptsMsg.indexOf('attempts exceeded') !== -1;
            if (!isMaxAttempts) {
              this.showOtpModalAndCreate(companyId, dto);
            } else {
              this.finishSubmitting();
            }
          }
        },
        error: (err) => {
          this.spinner.hide();
          const msg =
            err?.error?.message ||
            (isAr ? 'فشل التحقق من الرمز' : isHe ? 'אימות הקוד נכשל' : 'Verification failed');
          this.toastr.error(msg);
          this.showOtpModalAndCreate(companyId, dto);
        },
      });
    });
  }

  /**
   * Backend may append amount auto-correction text; keep success toast user-facing only.
   */
  private stripAmountValidationNotice(message: string): string {
    if (!message) return message;
    return message
      .replace(/\s*\(تم تصحيح المبالغ تلقائياً بناءً على العناصر\)\s*$/, '')
      .trim();
  }

  /**
   * Get localized message from API response, respecting statusCode and language
   */
  private getApiMessage(response: any): string {
    if (!response) {
      return this.curentLang === 'ar'
        ? 'تمت العملية بنجاح'
        : this.curentLang === 'he'
          ? 'הפעולה בוצעה בהצלחה'
          : 'Operation completed successfully';
    }

    // Prefer localized fields when available
    const msgAr = response.messageAr || response.messageAR;
    const msgEn = response.messageEn || response.messageEN;
    const msgHe = response.messageHe || response.messageHE;

    if (this.curentLang === 'ar' && msgAr) return msgAr;
    if (this.curentLang === 'he' && msgHe) return msgHe;
    if (msgEn) return msgEn;

    // Fallback to generic message field or a default
    return (
      response.message ||
      (this.curentLang === 'ar'
        ? 'تمت العملية بنجاح'
        : this.curentLang === 'he'
          ? 'הפעולה בוצעה בהצלחה'
          : 'Operation completed successfully')
    );
  }

  /**
   * Get error message from API error response. Prefer the localized field for current
   * language (messageAr/messageHe/messageEn) so the correct text is shown.
   */
  private getApiErrorMessage(error: any, fallback: string): string {
    const e = error?.error;
    if (!e) return fallback;
    if (Array.isArray(e.message)) return e.message.join(' | ');
    const nestedObj =
      e.message != null &&
      typeof e.message === 'object' &&
      !Array.isArray(e.message)
        ? e.message
        : null;
    const src = nestedObj ?? e;
    const msgAr = src.messageAr ?? src.messageAR;
    const msgEn = src.messageEn ?? src.messageEN;
    const msgHe = src.messageHe ?? src.messageHE;
    if (this.curentLang === 'ar' && msgAr) return String(msgAr);
    if (this.curentLang === 'he' && msgHe) return String(msgHe);
    if (msgEn) return String(msgEn);
    if (msgAr) return String(msgAr);
    if (msgHe) return String(msgHe);
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
    return fallback;
  }

  /**
   * Centralized handler for invoice V2 error responses
   * - Shows localized main message
   * - Shows validation errors (if any)
   * - For CUSTOMER_VAT_REQUIRED / CUSTOMER_VAT_INVALID opens VAT update modal
   */
  private handleInvoiceErrorResponse(response: MultiLangMessagePayload | any): void {
    const correlationId = this.newValidationCorrelationId();
    const parsedErrors = this.invoiceV2Validation.parseBackendResponse(response, correlationId);

    if (parsedErrors.length > 0) {
      this.setActiveValidationErrors(parsedErrors, correlationId);
      this.presentValidationErrors(parsedErrors, correlationId);
      this.logValidationErrors(parsedErrors, correlationId);
    } else {
      const msg = this.getApiMessage(response);
      this.toastr.error(msg);
    }

    const statusCode: InvoiceStatusCode | undefined = response?.statusCode;
    if (
      statusCode === InvoiceStatusCode.CUSTOMER_VAT_REQUIRED ||
      statusCode === InvoiceStatusCode.CUSTOMER_VAT_INVALID ||
      statusCode === InvoiceStatusCode.TAX_AUTHORITY_ERROR
    ) {
      const customerId = this.invoiceForm.get('customer_id')?.value;
      if (customerId) {
        this.openUpdateCustomerVatModal(customerId);
      }
    }

    this.finishSubmitting();
  }

  /**
   * Open modal to update customer's worker number (مشتغل رقم) when backend reports it is missing/invalid.
   * During invoice creation only رقم المشتغل is requested (not tax number or ID number).
   */
  private openUpdateCustomerVatModal(customerId: number): void {
    this.httpHelpService.getCustomerById(customerId).subscribe({
      next: (customer: any) => {
        const currentWorkerNumber =
          customer?.customer_vat_number ||
          customer?.vat_number ||
          customer?.customer?.customer_vat_number ||
          '';

        openCustomerWorkerNumberSwal(this.curentLang, currentWorkerNumber || '').then((result) => {
          if (result.isConfirmed) {
            const newWorkerNumber = (result.value as any).workerNumber;
            const userId = JSON.parse(localStorage.getItem('user_id') || '0');
            const payload = {
              ...customer,
              customer_vat_number: newWorkerNumber,
              customer_vat_tax: newWorkerNumber,
              customer_address_zip_code: newWorkerNumber,
              id_number: newWorkerNumber,
              updated_by: userId || customer.created_by,
            };
            this.httpHelpService.updateCustomersById(customerId, payload).subscribe({
              next: () => {
                this.toastr.success(
                  this.curentLang === 'ar'
                    ? 'تم تحديث رقم المشتغل بنجاح'
                    : this.curentLang === 'he'
                      ? 'מספר העוסק עודכן בהצלחה'
                      : 'Customer worker number updated successfully',
                );
              },
              error: () => {
                this.toastr.error(
                  this.curentLang === 'ar'
                    ? 'فشل تحديث رقم المشتغل'
                    : this.curentLang === 'he'
                      ? 'עדכון מספר העוסק נכשל'
                      : 'Failed to update customer worker number',
                );
              },
            });
          }
        });
      },
      error: () => {
        this.toastr.error(
          this.curentLang === 'ar'
            ? 'فشل تحميل بيانات الزبون لتحديث مشتغل رقم'
            : this.curentLang === 'he'
              ? 'טעינת נתוני הלקוח נכשלה'
              : 'Failed to load customer data for update',
        );
      },
    });
  }

  /**
   * Mark form group as touched
   */
  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach((arrayControl) => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          } else {
            arrayControl.markAsTouched();
          }
        });
      }
    });
  }

  private newValidationCorrelationId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `inv-val-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private getSelectedCompany(): any | null {
    const companyId = this.invoiceForm.get('company_id')?.value;
    if (companyId == null) {
      return null;
    }
    return this.companies.find((c) => +c.company_id === +companyId) || null;
  }

  private getSelectedCustomer(): any | null {
    const customerId = this.invoiceForm.get('customer_id')?.value;
    if (customerId == null) {
      return null;
    }
    return this.customers.find((c) => +c.customer_id === +customerId) || null;
  }

  private runFrontendValidation(correlationId: string): InvoiceFieldError[] {
    const formValue = this.invoiceForm.getRawValue();
    return this.invoiceV2Validation.validate({
      formValue,
      hasItems: this.hasItemsForSelectedType,
      hasPayments: this.paymentMethodsRequired,
      confirm: !!formValue.confirm,
      allocationNumber: this.computeAllocationNumberForSubmit(formValue),
      paymentMethodsList: this.paymentMethodsList,
      selectedCustomer: this.getSelectedCustomer(),
      selectedCompany: this.getSelectedCompany(),
      allowedInvoiceTypes: this.getAllowedInvoiceTypeCodesForCompany(formValue.company_id),
      correlationId,
    });
  }

  private setActiveValidationErrors(errors: InvoiceFieldError[], correlationId: string): void {
    this.activeValidationErrors = errors;
    this.activeValidationFieldPaths = new Set(errors.map((e) => e.fieldPath));
    this.lastValidationCorrelationId = correlationId;
  }

  private clearValidationHighlight(): void {
    this.activeValidationErrors = [];
    this.activeValidationFieldPaths.clear();
    this.lastValidationCorrelationId = null;
  }

  /** Drop submit-time highlight for a field once the user fixes it. */
  private clearFieldValidationError(fieldPath: string): void {
    if (!this.activeValidationFieldPaths.has(fieldPath)) {
      return;
    }
    this.activeValidationErrors = this.activeValidationErrors.filter(
      (e) => e.fieldPath !== fieldPath,
    );
    this.activeValidationFieldPaths.delete(fieldPath);
  }

  private parseCompanyId(value: unknown): number | null {
    if (value == null || value === '') {
      return null;
    }
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  hasFieldError(fieldPath: string): boolean {
    if (this.activeValidationFieldPaths.has(fieldPath)) {
      return true;
    }
    for (const path of this.activeValidationFieldPaths) {
      if (this.resolveValidationDomFieldPath(path) === fieldPath) {
        return true;
      }
    }
    return false;
  }

  hasRowError(index: number): boolean {
    const prefix = `items.${index}.`;
    for (const path of this.activeValidationFieldPaths) {
      if (path.startsWith(prefix)) {
        return true;
      }
    }
    return false;
  }

  hasPaymentError(index: number): boolean {
    const prefix = `payment_methods.${index}.`;
    for (const path of this.activeValidationFieldPaths) {
      if (path.startsWith(prefix)) {
        return true;
      }
    }
    return false;
  }

  presentValidationErrors(errors: InvoiceFieldError[], correlationId: string): void {
    this.validationErrorsForModal = errors;
    const isRtl = this.curentLang === 'ar' || this.curentLang === 'he';

    const goToFirstLabel =
      this.curentLang === 'ar'
        ? 'الانتقال لأول خطأ'
        : this.curentLang === 'he'
          ? 'עבור לשגיאה הראשונה'
          : 'Go to first error';

    const closeLabel =
      this.curentLang === 'ar' ? 'إغلاق' : this.curentLang === 'he' ? 'סגור' : 'Close';

    Swal.fire({
      title: ' ',
      html: this.buildValidationErrorsPanelHtml(errors, correlationId, isRtl),
      icon: undefined,
      showCancelButton: true,
      confirmButtonText: goToFirstLabel,
      cancelButtonText: closeLabel,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#64748b',
      reverseButtons: isRtl,
      focusConfirm: false,
      returnFocus: false,
      width: 560,
      customClass: {
        popup: `inv-v2-validation-modal${isRtl ? ' inv-v2-validation-modal--rtl' : ''}`,
        htmlContainer: 'inv-v2-validation-modal__html',
        actions: 'inv-v2-validation-modal__actions',
        confirmButton: 'inv-v2-validation-modal__btn-primary',
        cancelButton: 'inv-v2-validation-modal__btn-secondary',
        title: 'inv-v2-validation-modal__title-hidden',
      },
      didOpen: () => {
        this.bindValidationModalInteractions(errors, correlationId);
      },
    }).then((result) => {
      if (result.isConfirmed && errors.length > 0) {
        this.navigateToValidationError(errors[0], errors, correlationId);
      }
      this.validationErrorsForModal = [];
    });
  }

  /** Errors kept for modal row click handlers while Swal is open */
  private validationErrorsForModal: InvoiceFieldError[] = [];

  private escapeHtml(text: string): string {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private buildValidationErrorsPanelHtml(
    errors: InvoiceFieldError[],
    correlationId: string,
    isRtl: boolean,
  ): string {
    const count = errors.length;
    const dir = isRtl ? 'rtl' : 'ltr';

    const title =
      this.curentLang === 'ar'
        ? count === 1
          ? 'خطأ واحد يمنع حفظ المستند'
          : `${count} أخطاء تمنع حفظ المستند`
        : this.curentLang === 'he'
          ? count === 1
            ? 'שגיאה אחת מונעת שמירה'
            : `${count} שגיאות מונעות שמירה`
          : count === 1
            ? '1 issue prevents saving'
            : `${count} issues prevent saving`;

    const subtitle =
      this.curentLang === 'ar'
        ? 'صحّح الحقول أدناه ثم أعد المحاولة. انقر على أي خطأ للانتقال إليه مباشرة.'
        : this.curentLang === 'he'
          ? 'תקן את השדות למטה ונסה שוב. לחץ על שגיאה כדי לעבור אליה.'
          : 'Fix the fields below and try again. Click any error to jump to it.';

    const sourceLabel = (source: 'frontend' | 'backend') =>
      source === 'frontend'
        ? this.curentLang === 'ar'
          ? 'الواجهة'
          : this.curentLang === 'he'
            ? 'ממשק'
            : 'Frontend'
        : this.curentLang === 'ar'
          ? 'الخادم'
          : this.curentLang === 'he'
            ? 'שרת'
            : 'Backend';

    const grouped = this.groupValidationErrorsForDisplay(errors);
    const sectionsHtml = grouped
      .map((group) => {
        const cards = group.errors
          .map((err) => {
            const msg = this.escapeHtml(this.invoiceV2Validation.getMessage(err, this.curentLang));
            const rowBadge =
              err.row != null
                ? `<span class="inv-val-row-badge">${this.curentLang === 'ar' ? 'س' : this.curentLang === 'he' ? 'ש' : 'R'}${err.row}</span>`
                : err.paymentIndex != null
                  ? `<span class="inv-val-row-badge">${this.curentLang === 'ar' ? 'د' : this.curentLang === 'he' ? 'ת' : 'P'}${err.paymentIndex}</span>`
                  : '';

            const gotoLabel =
              this.curentLang === 'ar'
                ? 'انتقل للحقل'
                : this.curentLang === 'he'
                  ? 'עבור לשדה'
                  : 'Go to field';

            const rowAttr =
              err.row != null ? ` data-inv-val-row="${err.row}"` : '';
            const paymentAttr =
              err.paymentIndex != null ? ` data-inv-val-payment-index="${err.paymentIndex}"` : '';

            return `
              <button type="button" class="inv-val-card" data-inv-val-field-path="${this.escapeHtml(err.fieldPath)}"${rowAttr}${paymentAttr} aria-label="${msg}">
                <div class="inv-val-card__accent"></div>
                <div class="inv-val-card__body">
                  <div class="inv-val-card__top">
                    ${rowBadge}
                    <p class="inv-val-card__message">${msg}</p>
                  </div>
                  <div class="inv-val-card__meta">
                    <span class="inv-val-pill inv-val-pill--code">${this.escapeHtml(err.errorCode)}</span>
                    <span class="inv-val-pill inv-val-pill--source">${sourceLabel(err.source)}</span>
                  </div>
                  <span class="inv-val-card__goto">${gotoLabel} <i class="fa fa-arrow-${isRtl ? 'left' : 'right'}"></i></span>
                </div>
              </button>`;
          })
          .join('');

        return `
          <section class="inv-val-section">
            <header class="inv-val-section__head">
              <span class="inv-val-section__icon"><i class="fa ${group.icon}"></i></span>
              <span class="inv-val-section__title">${this.escapeHtml(group.label)}</span>
              <span class="inv-val-section__count">${group.errors.length}</span>
            </header>
            <div class="inv-val-section__list">${cards}</div>
          </section>`;
      })
      .join('');

    const refLabel =
      this.curentLang === 'ar' ? 'مرجع الدعم' : this.curentLang === 'he' ? 'אסמכתא לתמיכה' : 'Support reference';
    const copyLabel =
      this.curentLang === 'ar' ? 'نسخ' : this.curentLang === 'he' ? 'העתק' : 'Copy';
    const copyHint =
      this.curentLang === 'ar'
        ? 'أرسل هذا المرجع لفريق الدعم عند طلب المساعدة'
        : this.curentLang === 'he'
          ? 'שלח אסמכתא זו לתמיכה בעת פנייה'
          : 'Share this reference with support if you need help';

    return `
      <div class="inv-val-panel" dir="${dir}">
        <header class="inv-val-hero">
          <div class="inv-val-hero__icon" aria-hidden="true">
            <i class="fa fa-exclamation-triangle"></i>
          </div>
          <div class="inv-val-hero__text">
            <h2 class="inv-val-hero__title">${this.escapeHtml(title)}</h2>
            <p class="inv-val-hero__subtitle">${this.escapeHtml(subtitle)}</p>
          </div>
          <div class="inv-val-hero__count" aria-hidden="true">${count}</div>
        </header>

        <div class="inv-val-scroll">${sectionsHtml}</div>

        <footer class="inv-val-footer">
          <div class="inv-val-ref">
            <div class="inv-val-ref__label">${this.escapeHtml(refLabel)}</div>
            <div class="inv-val-ref__row">
              <code class="inv-val-ref__code" id="inv-val-correlation-id">${this.escapeHtml(correlationId)}</code>
              <button type="button" class="inv-val-ref__copy" id="inv-val-copy-ref" title="${this.escapeHtml(copyLabel)}">
                <i class="fa fa-copy"></i>
                <span>${this.escapeHtml(copyLabel)}</span>
              </button>
            </div>
            <p class="inv-val-ref__hint">${this.escapeHtml(copyHint)}</p>
          </div>
        </footer>
      </div>`;
  }

  private groupValidationErrorsForDisplay(errors: InvoiceFieldError[]): Array<{
    key: string;
    label: string;
    icon: string;
    errors: InvoiceFieldError[];
  }> {
    const documentSections = new Set(['header', 'customer', 'tax', 'business']);
    const buckets: Record<string, InvoiceFieldError[]> = {
      document: [],
      item: [],
      payment: [],
    };

    for (const err of errors) {
      if (err.section === 'item') {
        buckets.item.push(err);
      } else if (err.section === 'payment') {
        buckets.payment.push(err);
      } else if (documentSections.has(err.section)) {
        buckets.document.push(err);
      } else {
        buckets.document.push(err);
      }
    }

    const labels = {
      document:
        this.curentLang === 'ar'
          ? 'بيانات المستند'
          : this.curentLang === 'he'
            ? 'פרטי מסמך'
            : 'Document details',
      item:
        this.curentLang === 'ar'
          ? 'الأصناف'
          : this.curentLang === 'he'
            ? 'פריטים'
            : 'Line items',
      payment:
        this.curentLang === 'ar'
          ? 'طرق الدفع'
          : this.curentLang === 'he'
            ? 'אמצעי תשלום'
            : 'Payments',
    };

    const icons = {
      document: 'fa-file-text-o',
      item: 'fa-list-ul',
      payment: 'fa-credit-card',
    };

    return (['document', 'item', 'payment'] as const)
      .filter((key) => buckets[key].length > 0)
      .map((key) => ({
        key,
        label: labels[key],
        icon: icons[key],
        errors: buckets[key],
      }));
  }

  private bindValidationModalInteractions(errors: InvoiceFieldError[], correlationId: string): void {
    document.getElementById('inv-val-copy-ref')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(correlationId).then(() => {
          this.toastr.success(
            this.curentLang === 'ar'
              ? 'تم نسخ المرجع'
              : this.curentLang === 'he'
                ? 'האסמכתא הועתקה'
                : 'Reference copied',
          );
        });
      }
    });

    document.querySelectorAll<HTMLButtonElement>('[data-inv-val-field-path]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const err = this.resolveValidationErrorFromButton(btn, errors);
        if (!err) {
          return;
        }
        this.navigateToValidationError(err, errors, correlationId);
      });
    });
  }

  private resolveValidationErrorFromButton(
    btn: HTMLButtonElement,
    errors: InvoiceFieldError[],
  ): InvoiceFieldError | undefined {
    const fieldPath = btn.getAttribute('data-inv-val-field-path');
    if (!fieldPath) {
      return undefined;
    }
    const rowAttr = btn.getAttribute('data-inv-val-row');
    const paymentAttr = btn.getAttribute('data-inv-val-payment-index');

    return errors.find((err) => {
      if (err.fieldPath !== fieldPath) {
        return false;
      }
      if (rowAttr != null && rowAttr !== '' && err.row !== +rowAttr) {
        return false;
      }
      if (paymentAttr != null && paymentAttr !== '' && err.paymentIndex !== +paymentAttr) {
        return false;
      }
      return true;
    });
  }

  /** Close validation modal (if open) then scroll/focus the target field without Swal stealing focus back. */
  private navigateToValidationError(
    error: InvoiceFieldError,
    errors: InvoiceFieldError[],
    correlationId: string,
  ): void {
    this.setActiveValidationErrors(errors, correlationId);
    const run = () => this.focusFirstError(error);
    if (Swal.isVisible()) {
      Swal.close();
      // Wait for Swal teardown so it does not restore focus to the submit button at page bottom.
      setTimeout(run, 100);
    } else {
      run();
    }
  }

  /** Map validation field paths to DOM targets (some errors reference computed/non-input fields). */
  private resolveValidationDomFieldPath(fieldPath: string): string {
    const itemFieldAliases: Record<string, string> = {
      item_name: 'product_id',
      sub_total: 'price_per_unit',
      total_amount: 'price_per_unit',
      vat_amount: 'price_per_unit',
      vat_rate: 'price_per_unit',
      catalog_id: 'product_id',
    };
    const itemMatch = fieldPath.match(/^items\.(\d+)\.(.+)$/);
    if (itemMatch) {
      const index = itemMatch[1];
      const field = itemMatch[2];
      const mapped = itemFieldAliases[field] ?? field;
      return `items.${index}.${mapped}`;
    }
    return fieldPath;
  }

  private prepareValidationNavigation(error: InvoiceFieldError): void {
    if (error.section === 'item') {
      if (error.fieldPath === 'items') {
        return;
      }
      if (error.row != null) {
        this.expandedItemIndex = error.row - 1;
      }
      return;
    }

    if (error.section === 'payment' && error.fieldPath === 'payment_methods') {
      return;
    }

    if (
      error.section === 'header' ||
      error.section === 'customer' ||
      error.section === 'tax' ||
      error.section === 'business'
    ) {
      if (error.fieldPath === 'document_language') {
        this.showTaxDetails = true;
      }
    }
  }

  private getValidationControlElement(container: HTMLElement): HTMLElement {
    return (
      container.querySelector<HTMLElement>('.ng-select-container') ??
      container.querySelector<HTMLElement>('input.p-inputtext:not([type="hidden"])') ??
      container.querySelector<HTMLElement>('input:not([type="hidden"])') ??
      container.querySelector<HTMLElement>('select') ??
      container.querySelector<HTMLElement>('textarea') ??
      container
    );
  }

  private validationFieldDomId(domFieldPath: string): string {
    return `validation-field-${domFieldPath.replace(/\./g, '-').replace(/_/g, '-')}`;
  }

  private findValidationTargetElement(error: InvoiceFieldError): HTMLElement | null {
    const domFieldPath = this.resolveValidationDomFieldPath(error.fieldPath);
    const wrapper =
      document.querySelector<HTMLElement>(`[data-validation-field="${domFieldPath}"]`) ??
      document.getElementById(this.validationFieldDomId(domFieldPath));

    if (wrapper) {
      return this.getValidationControlElement(wrapper);
    }

    if (error.row != null) {
      const rowEl = document.querySelector<HTMLElement>(`[data-item-row="${error.row - 1}"]`);
      if (rowEl) {
        const fieldSuffix = domFieldPath.split('.').pop() ?? '';
        const nested = rowEl.querySelector<HTMLElement>(`[data-validation-field$=".${fieldSuffix}"]`);
        if (nested) {
          return this.getValidationControlElement(nested);
        }
        return rowEl;
      }
    }

    if (error.paymentIndex != null) {
      const rowEl = document.querySelector<HTMLElement>(
        `[data-payment-row="${error.paymentIndex - 1}"]`,
      );
      if (rowEl) {
        const nested = rowEl.querySelector<HTMLElement>(`[data-validation-field="${domFieldPath}"]`);
        if (nested) {
          return this.getValidationControlElement(nested);
        }
        return rowEl;
      }
    }

    return null;
  }

  private focusValidationControl(el: HTMLElement): void {
    const focusEl = el.classList.contains('ng-select-container')
      ? el
      : this.getValidationControlElement(el.closest('[data-validation-field]') ?? el);

    if (!focusEl?.focus) {
      return;
    }

    try {
      focusEl.focus({ preventScroll: true });
    } catch {
      focusEl.focus();
    }
  }

  private scrollToValidationTarget(error: InvoiceFieldError): void {
    if (error.section === 'item' && error.fieldPath === 'items') {
      document.getElementById('invoice-v2-section-items')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }

    if (error.section === 'payment' && error.fieldPath === 'payment_methods') {
      document.getElementById('invoice-v2-section-payments')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }

    if (
      error.section === 'header' ||
      error.section === 'customer' ||
      error.section === 'tax' ||
      error.section === 'business'
    ) {
      if (error.fieldPath === 'header') {
        document.getElementById('invoice-v2-section-header')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        return;
      }
    }

    const target = this.findValidationTargetElement(error);
    if (!target) {
      if (error.section === 'item') {
        document.getElementById('invoice-v2-section-items')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      } else if (error.section === 'payment') {
        document.getElementById('invoice-v2-section-payments')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      } else {
        document.getElementById('invoice-v2-section-header')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      return;
    }

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.focusValidationControl(target);
  }

  focusFirstError(error: InvoiceFieldError): void {
    this.prepareValidationNavigation(error);
    this.cdr.detectChanges();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.scrollToValidationTarget(error);
      });
    });
  }

  private logValidationErrors(errors: InvoiceFieldError[], correlationId: string): void {
    const formValue = this.invoiceForm.getRawValue();
    console.warn('[invoice-v2][validation]', {
      correlationId,
      source: errors[0]?.source ?? 'frontend',
      errorCodes: errors.map((e) => e.errorCode),
      merchantId: this.merchantId,
      companyId: formValue.company_id,
      customerId: formValue.customer_id,
      invoiceType: formValue.invoice_type,
      itemsCount: Array.isArray(formValue.items) ? formValue.items.length : 0,
      confirm: !!formValue.confirm,
      timestamp: new Date().toISOString(),
      fields: errors.map((e) => ({ field: e.fieldPath, row: e.row, code: e.errorCode })),
    });
  }

  /**
   * @deprecated Replaced by InvoiceV2ValidationService + presentValidationErrors
   */
  showValidationErrors(): void {
    const missingFields: string[] = [];
    
    // Field labels in all languages
    const fieldLabels: { [key: string]: { ar: string; en: string; he: string } } = {
      company_id: { ar: 'المصلحة التجارية', en: 'Company', he: 'חברה' },
      customer_id: { ar: 'الزبون', en: 'Customer', he: 'לקוח' },
      invoice_type: { ar: 'نوع الفاتورة', en: 'Invoice Type', he: 'סוג חשבונית' },
      invoice_date: { ar: 'تاريخ الفاتورة', en: 'Invoice Date', he: 'תאריך חשבונית' },
      document_language: { ar: 'لغة المستند', en: 'Document Language', he: 'שפת מסמך' },
      items: { ar: 'المنتجات', en: 'Products', he: 'מוצרים' },
      payment_methods: { ar: 'طرق الدفع', en: 'Payment Methods', he: 'אמצעי תשלום' },
    };

    // Check main form controls
    Object.keys(this.invoiceForm.controls).forEach(key => {
      if (key === 'payment_methods' && !this.invoiceForm.get('confirm')?.value) {
        return;
      }
      const control = this.invoiceForm.get(key);
      if (control?.invalid && fieldLabels[key]) {
        const label = fieldLabels[key];
        missingFields.push(
          this.curentLang === 'ar' ? label.ar : this.curentLang === 'he' ? label.he : label.en
        );
      }
    });

    // Check items if required
    if (this.hasItemsForSelectedType && this.items.length === 0) {
      const label = fieldLabels['items'];
      if (!missingFields.includes(this.curentLang === 'ar' ? label.ar : this.curentLang === 'he' ? label.he : label.en)) {
        missingFields.push(
          this.curentLang === 'ar' ? label.ar : this.curentLang === 'he' ? label.he : label.en
        );
      }
    }

    // Check items details (product, quantity, price)
    if (this.hasItemsForSelectedType && this.items.length > 0) {
      this.items.controls.forEach((item, index) => {
        const itemGroup = item as FormGroup;
        if (itemGroup.get('product_id')?.invalid) {
          missingFields.push(
            this.curentLang === 'ar' ? `المنتج (صف ${index + 1})` : 
            this.curentLang === 'he' ? `מוצר (שורה ${index + 1})` : 
            `Product (row ${index + 1})`
          );
        }
        if (itemGroup.get('quantity')?.invalid) {
          missingFields.push(
            this.curentLang === 'ar' ? `الكمية (صف ${index + 1})` : 
            this.curentLang === 'he' ? `כמות (שורה ${index + 1})` : 
            `Quantity (row ${index + 1})`
          );
        }
        if (itemGroup.get('price_per_unit')?.invalid) {
          missingFields.push(
            this.curentLang === 'ar' ? `السعر (صف ${index + 1})` : 
            this.curentLang === 'he' ? `מחיר (שורה ${index + 1})` : 
            `Price (row ${index + 1})`
          );
        }
      });
    }

    // Check payment methods details (only when finalizing / confirm=true)
    if (
      this.invoiceForm.get('confirm')?.value &&
      this.paymentMethodsRequired &&
      this.paymentMethods.length > 0
    ) {
      this.paymentMethods.controls.forEach((pm, index) => {
        const pmGroup = pm as FormGroup;
        if (pmGroup.get('amount')?.invalid) {
          missingFields.push(
            this.curentLang === 'ar' ? `مبلغ الدفع (دفعة ${index + 1})` : 
            this.curentLang === 'he' ? `סכום תשלום (תשלום ${index + 1})` : 
            `Payment Amount (payment ${index + 1})`
          );
        }
        // Check required details fields
        const details = pmGroup.get('details') as FormGroup;
        if (details) {
          ['reference', 'bank', 'branch', 'account', 'credit_card_code', 'credit_card_name', 'credit_type'].forEach(field => {
            if (details.get(field)?.invalid) {
              const fieldNames: { [k: string]: { ar: string; en: string; he: string } } = {
                reference: { ar: 'رقم الشيك', en: 'Check number', he: "מס' שיק" },
                bank: { ar: 'البنك', en: 'Bank', he: 'בנק' },
                branch: { ar: 'الفرع', en: 'Branch', he: 'סניף' },
                account: { ar: 'رقم الحساب', en: 'Account', he: 'חשבון' },
                credit_card_code: { ar: '4 أرقام أخيرة', en: 'Last 4 digits', he: '4 ספרות אחרונות' },
                credit_card_name: { ar: 'اسم البطاقة', en: 'Card Name', he: 'שם כרטיס' },
                credit_type: { ar: 'نوع الائتمان', en: 'Credit Type', he: 'סוג אשראי' },
              };
              const fn = fieldNames[field];
              if (fn) {
                missingFields.push(
                  this.curentLang === 'ar' ? `${fn.ar} (دفعة ${index + 1})` : 
                  this.curentLang === 'he' ? `${fn.he} (תשלום ${index + 1})` : 
                  `${fn.en} (payment ${index + 1})`
                );
              }
            }
          });
        }
      });
    }

    // Build message
    const title = this.curentLang === 'ar' ? 'الحقول المطلوبة' : 
                  this.curentLang === 'he' ? 'שדות נדרשים' : 'Required Fields';
    
    const message = this.curentLang === 'ar' ? 'يرجى ملء الحقول التالية:' : 
                    this.curentLang === 'he' ? 'אנא מלא את השדות הבאים:' : 
                    'Please fill the following fields:';

    // Show SweetAlert with the list
    Swal.fire({
      title: title,
      html: `<p>${message}</p><ul style="text-align: ${this.curentLang === 'en' ? 'left' : 'right'}; direction: ${this.curentLang === 'en' ? 'ltr' : 'rtl'}; list-style: disc; padding: 0 20px;">${missingFields.map(f => `<li style="margin: 5px 0; color: #dc3545;">${f}</li>`).join('')}</ul>`,
      icon: 'warning',
      confirmButtonText: this.curentLang === 'ar' ? 'حسناً' : this.curentLang === 'he' ? 'בסדר' : 'OK',
      confirmButtonColor: '#dc3545',
    });
  }

  /**
   * Debug: Log invalid form controls (call from browser console or template)
   */
  debugFormValidity(): void {
    Object.keys(this.invoiceForm.controls).forEach(key => {
      const control = this.invoiceForm.get(key);
});
    }

  /**
   * Cancel and go back.
   */
  cancel(): void {
    this.router.navigate(['/dashboard/invoices-v2']);
  }

  /**
   * Get invoice type label
   */
  getInvoiceTypeLabel(type: number): string {
    const invoiceType =
      this.invoiceTypes.find((t) => t.id === type.toString()) ||
      this.allInvoiceTypes.find((t) => String(t.id) === String(type));
    if (!invoiceType) return type.toString();
    if (this.curentLang === 'ar') return invoiceType.name_ar;
    if (this.curentLang === 'he') return invoiceType.name_he;
    return invoiceType.name_en;
  }

  /**
   * Show «merge documents» button only when:
   * 1. The current document is Category B (types 305 / 320) — either new or unconfirmed draft.
   * 2. A customer and company are selected.
   * 3. At least one Category A document exists for that customer.
   */
  get showCreateMergeDocumentsButton(): boolean {
    const type = this.invoiceForm?.get('invoice_type')?.value;
    if (type == null || !CREATE_MERGE_ALLOWED_TARGET_INVOICE_TYPES.has(+type)) {
      return false;
    }
    // In edit mode only allow on unconfirmed drafts (confirmed docs can't be changed)
    if (this.isEditMode && this.invoiceConfirmFromServer !== 0) {
      return false;
    }
    const companyId = this.invoiceForm?.get('company_id')?.value;
    const customerId = this.invoiceForm?.get('customer_id')?.value;
    if (!companyId || customerId == null || customerId === '') {
      return false;
    }
    if (this.createMergeEligibilityLoading) {
      return false;
    }
    return this.createMergeEligibilityChecked && this.createMergeEligibleCount > 0;
  }

  // ─── Create: merge existing documents for selected customer ─────────────────

  /**
   * All selected source types must map to the same `convert_to_invoice_type_code`
   * (e.g. delivery 200 + financial 332 → 305). Frontend-only merge.
   */
  private resolveMergeTargetTypeFromSourceTypes(sourceTypeCodes: number[]): number | null {
    if (sourceTypeCodes.length === 0) {
      return null;
    }
    const targets = new Set<number>();
    for (const code of sourceTypeCodes) {
      const meta =
        this.invoiceTypes.find((t) => +t.id === +code) ||
        this.allInvoiceTypes.find((t) => +t.id === +code);
      const conv = meta?.convert_to_invoice_type_code;
      if (conv == null || conv === '') {
        return null;
      }
      targets.add(+conv);
    }
    return targets.size === 1 ? [...targets][0] : null;
  }

  /** Same filters as merge modal list (`loadCreateMergeInvoices`). */
  private filterRawInvoicesForCreateMerge(raw: any[]): any[] {
    return raw.filter((inv: any) => {
      const t = +inv.invoice_type;
      if (CREATE_MERGE_EXCLUDED_INVOICE_TYPES.has(t)) {
        return false;
      }
      if (!CREATE_MERGE_ALLOWED_SOURCE_INVOICE_TYPES.has(t)) {
        return false;
      }
      if (+inv.confirm !== 1 || inv.is_deleted === 1) {
        return false;
      }
      if (this.hasMergedInvoiceReference(inv)) {
        return false;
      }
      if (this.isInvoiceMarkedMergedDb(inv)) {
        return false;
      }
      const rid = this.getCreateMergeRowId(inv);
      return rid > 0;
    });
  }

  private clearCreateMergeEligibilityScheduleAndState(): void {
    if (this.createMergeEligibilityTimer) {
      clearTimeout(this.createMergeEligibilityTimer);
      this.createMergeEligibilityTimer = null;
    }
    this.createMergeEligibilityLoading = false;
    this.createMergeEligibilityChecked = false;
    this.createMergeEligibleCount = 0;
  }

  private scheduleRefreshCreateMergeEligibility(): void {
    // Allow in edit mode only for unconfirmed Category B drafts (305 / 320)
    if (this.isEditMode) {
      const type = this.invoiceForm?.get('invoice_type')?.value;
      const isTargetType = type != null && CREATE_MERGE_ALLOWED_TARGET_INVOICE_TYPES.has(+type);
      if (!isTargetType || this.invoiceConfirmFromServer !== 0) {
        return;
      }
    }
    const companyId = this.invoiceForm.get('company_id')?.value;
    const cid = this.invoiceForm.get('customer_id')?.value;
    if (!companyId || cid == null || cid === '') {
      this.clearCreateMergeEligibilityScheduleAndState();
      return;
    }
    if (this.createMergeEligibilityTimer) {
      clearTimeout(this.createMergeEligibilityTimer);
    }
    this.createMergeEligibilityTimer = setTimeout(() => {
      this.createMergeEligibilityTimer = null;
      this.refreshCreateMergeEligibilityNow();
    }, 300);
  }

  private refreshCreateMergeEligibilityNow(): void {
    if (this.isEditMode) {
      const type = this.invoiceForm?.get('invoice_type')?.value;
      const isTargetType = type != null && CREATE_MERGE_ALLOWED_TARGET_INVOICE_TYPES.has(+type);
      if (!isTargetType || this.invoiceConfirmFromServer !== 0) {
        return;
      }
    }
    const companyId = this.invoiceForm.get('company_id')?.value;
    const cid = this.invoiceForm.get('customer_id')?.value;
    if (!companyId || cid == null || cid === '') {
      this.clearCreateMergeEligibilityScheduleAndState();
      return;
    }

    this.createMergeEligibilityLoading = true;
    this.createMergeEligibilityChecked = false;

    this.invoiceV2Service
      .getFilteredInvoices({
        customer_id: +cid,
        companyId: +companyId,
        confirm: true,
        limit: 100,
        offset: 0,
      })
      .subscribe({
        next: (response: any) => {
          const raw: any[] = response?.data || [];
          const filtered = this.filterRawInvoicesForCreateMerge(raw);
          this.createMergeEligibleCount = filtered.length;
          this.createMergeEligibilityLoading = false;
          this.createMergeEligibilityChecked = true;
        },
        error: () => {
          this.createMergeEligibleCount = 0;
          this.createMergeEligibilityLoading = false;
          this.createMergeEligibilityChecked = true;
        },
      });
  }

  private hasMergedInvoiceReference(inv: any): boolean {
    const ref = inv?.invoice_reference_number;
    return ref != null && String(ref).trim() !== '';
  }

  /** DB flag from PATCH /invoices-v2/bulk/mark-merged */
  private isInvoiceMarkedMergedDb(inv: any): boolean {
    if (!inv) return false;
    return inv.is_merged === true || inv.is_merged === 1 || inv.is_merged === '1';
  }

  getCreateMergeRowId(inv: any): number {
    const v = inv?.invoice_id ?? inv?.id;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  getCreateMergeDisplayAmount(inv: any): number {
    if (!inv) return 0;
    if (+inv.invoice_type === 400) {
      return +inv.total_amount || 0;
    }
    return +inv.payment_amount_including_vat || 0;
  }

  /** Only order (100), mission (200), financial request (332) may be selected as merge sources. */
  isCreateMergeRowSelectable(inv: any): boolean {
    const t = inv != null ? +inv.invoice_type : NaN;
    return CREATE_MERGE_ALLOWED_SOURCE_INVOICE_TYPES.has(t);
  }

  openCreateMergeFromCustomerButton(): void {
    const cid = this.invoiceForm.get('customer_id')?.value;
    if (cid == null || cid === '') {
      return;
    }
    this.openCreateMergeDocumentsModal(+cid);
  }

  openCreateMergeDocumentsModal(customerId: number): void {
    if (!customerId || customerId < 1) {
      return;
    }
    const companyId = this.invoiceForm.get('company_id')?.value;
    if (!companyId) {
      return;
    }
    this.createMergeSelectedIds = [];
    this.createMergeInvoices = [];
    this.showCreateMergeModal = true;
    this.loadCreateMergeInvoices(customerId, +companyId);
  }

  closeCreateMergeModal(): void {
    this.closeCreateMergeInvoicePreview();
    this.showCreateMergeModal = false;
    this.createMergeLoading = false;
    this.createMergeSelectedIds = [];
    this.createMergeInvoices = [];
  }

  private loadCreateMergeInvoices(customerId: number, companyId: number): void {
    this.createMergeLoading = true;
    this.invoiceV2Service
      .getFilteredInvoices({
        customer_id: customerId,
        companyId,
        confirm: true,
        limit: 100,
        offset: 0,
      })
      .subscribe({
        next: (response: any) => {
          const raw: any[] = response?.data || [];
          this.createMergeInvoices = this.filterRawInvoicesForCreateMerge(raw);
          this.createMergeLoading = false;
        },
        error: () => {
          this.createMergeLoading = false;
          this.toastr.error(
            this.curentLang === 'ar'
              ? 'خطأ في تحميل المستندات'
              : this.curentLang === 'he'
                ? 'שגיאה בטעינת מסמכים'
                : 'Error loading documents',
          );
        },
      });
  }

  isCreateMergeSelected(rowId: number): boolean {
    return this.createMergeSelectedIds.includes(rowId);
  }

  toggleCreateMergeSelection(rowId: number): void {
    const inv = this.createMergeInvoices.find((i) => this.getCreateMergeRowId(i) === rowId);
    if (!inv || !this.isCreateMergeRowSelectable(inv)) {
      return;
    }
    const idx = this.createMergeSelectedIds.indexOf(rowId);
    if (idx === -1) {
      this.createMergeSelectedIds = [...this.createMergeSelectedIds, rowId];
    } else {
      const next = [...this.createMergeSelectedIds];
      next.splice(idx, 1);
      this.createMergeSelectedIds = next;
    }
  }

  /**
   * Hide a confirmed source document from future merge pickers without deleting it
   * (PATCH /invoices-v2/bulk/mark-merged → is_merged).
   */
  confirmExcludeFromCreateMerge(inv: any): void {
    const id = this.getCreateMergeRowId(inv);
    if (id < 1 || this.createMergeExcludingId != null) {
      return;
    }
    const docLabel = inv?.document_number || `#${id}`;
    this.openCreateMergeExcludeConfirm(docLabel, () => this.excludeInvoiceFromCreateMerge(id));
  }

  confirmExcludeFromCreateMergeById(invoiceId: number): void {
    const inv = this.createMergeInvoices.find((row) => this.getCreateMergeRowId(row) === invoiceId);
    if (inv) {
      this.confirmExcludeFromCreateMerge(inv);
    }
  }

  /** Close preview/focus before Swal so aria-hidden on app-root does not trap focus in a hidden layer. */
  private openCreateMergeExcludeConfirm(docLabel: string, onConfirm: () => void): void {
    if (this.showCreateMergePreviewModal) {
      this.closeCreateMergeInvoicePreview();
    }
    (document.activeElement as HTMLElement | null)?.blur?.();
    setTimeout(() => {
      Swal.fire({
        icon: 'warning',
        title: this.translate.instant('invoices_v2.form.create_merge_exclude_confirm_title'),
        text: this.translate.instant('invoices_v2.form.create_merge_exclude_confirm_text', { doc: docLabel }),
        showCancelButton: true,
        confirmButtonText: this.translate.instant('invoices_v2.form.create_merge_exclude_confirm_ok'),
        cancelButtonText: this.translate.instant('invoices_v2.common.cancel'),
        returnFocus: false,
        focusConfirm: true,
      }).then((result) => {
        if (result.isConfirmed) {
          onConfirm();
        }
      });
    }, 0);
  }

  excludeInvoiceFromCreateMerge(invoiceId: number): void {
    if (invoiceId < 1) {
      return;
    }
    this.createMergeExcludingId = invoiceId;
    this.invoiceV2Service.markInvoicesMerged([invoiceId], true).subscribe({
      next: () => {
        this.createMergeSelectedIds = this.createMergeSelectedIds.filter((x) => x !== invoiceId);
        this.createMergeInvoices = this.createMergeInvoices.filter(
          (row) => this.getCreateMergeRowId(row) !== invoiceId,
        );
        if (this.createMergePreviewInvoiceId === invoiceId) {
          this.closeCreateMergeInvoicePreview();
        }
        this.refreshCreateMergeEligibilityNow();
        this.createMergeExcludingId = null;
        this.toastr.success(this.translate.instant('invoices_v2.form.create_merge_exclude_success'));
      },
      error: () => {
        this.createMergeExcludingId = null;
        this.toastr.error(this.translate.instant('invoices_v2.form.create_merge_exclude_error'));
      },
    });
  }

  isCreateMergeExcluding(invoiceId: number): boolean {
    return this.createMergeExcludingId === invoiceId;
  }

  /** Row in preview popup can toggle merge checkbox only if same rules as list selection */
  isCreateMergePreviewSelectable(): boolean {
    if (this.createMergePreviewInvoiceId == null) {
      return false;
    }
    const inv = this.createMergeInvoices.find(
      (i) => this.getCreateMergeRowId(i) === this.createMergePreviewInvoiceId,
    );
    return inv ? this.isCreateMergeRowSelectable(inv) : false;
  }

  openCreateMergeInvoicePreview(inv: any): void {
    const invoiceId = this.getCreateMergeRowId(inv);
    if (invoiceId < 1) {
      return;
    }
    this.createMergePreviewInvoiceId = invoiceId;
    this.createMergePreviewInvoiceLabel = inv?.document_number || `#${invoiceId}`;
    this.createMergePreviewUrl = null;
    this.createMergePreviewLoading = true;
    this.showCreateMergePreviewModal = true;

    const docLang =
      inv?.document_language === 'ar' ||
      inv?.document_language === 'he' ||
      inv?.document_language === 'en'
        ? inv.document_language
        : this.curentLang;
    this.invoiceV2Service.getInvoiceHtml(invoiceId, docLang).subscribe({
      next: (html: string) => {
        const fullHtml = `<!DOCTYPE html><html lang="${docLang}"
          dir="${docLang === 'ar' || docLang === 'he' ? 'rtl' : 'ltr'}">
          <head><meta charset="UTF-8">
          <style>body{margin:0;padding:20px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:#fff;color:#1a1a1a;}*{box-sizing:border-box;}</style>
          </head><body>${html}</body></html>`;
        if (this.createMergePreviewBlobUrl) {
          URL.revokeObjectURL(this.createMergePreviewBlobUrl);
        }
        const blob = new Blob([fullHtml], { type: 'text/html' });
        this.createMergePreviewBlobUrl = URL.createObjectURL(blob);
        this.createMergePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.createMergePreviewBlobUrl,
        );
        this.createMergePreviewLoading = false;
      },
      error: () => {
        this.createMergePreviewLoading = false;
        this.toastr.error(
          this.curentLang === 'ar'
            ? 'تعذر تحميل معاينة الفاتورة'
            : this.curentLang === 'he'
              ? 'לא ניתן לטעון תצוגה מקדימה'
              : 'Could not load invoice preview',
        );
        this.showCreateMergePreviewModal = false;
        this.createMergePreviewInvoiceId = null;
        this.createMergePreviewInvoiceLabel = '';
      },
    });
  }

  closeCreateMergeInvoicePreview(): void {
    this.showCreateMergePreviewModal = false;
    this.createMergePreviewInvoiceId = null;
    this.createMergePreviewInvoiceLabel = '';
    this.createMergePreviewUrl = null;
    this.createMergePreviewLoading = false;
    if (this.createMergePreviewBlobUrl) {
      URL.revokeObjectURL(this.createMergePreviewBlobUrl);
      this.createMergePreviewBlobUrl = null;
    }
  }

  private unwrapInvoiceResponse(res: any): any {
    return res?.data !== undefined ? res.data : res;
  }

  /** Map GET invoice `soldItems` row into the DTO shape used by the form. */
  private mapApiSoldItemToDto(raw: any, index: number): CreateSoldItemV2Dto {
    const qty = Number(raw.quantity ?? 1) || 1;
    const discount = Number(raw.discount ?? 0);
    const subTotal = Number(raw.sub_total ?? 0);
    let price = Number(raw.price_per_unit ?? raw.price ?? 0);

    // Backend sometimes returns header/line price 100× off while sub_total is correct — trust sub_total + discount
    const impliedGross = subTotal + discount;
    if (impliedGross > 0 && qty > 0) {
      const priceGross = qty * price;
      if (priceGross > 0) {
        const ratio = priceGross / impliedGross;
        if (ratio >= 10 || ratio <= 0.1) {
          price = impliedGross / qty;
        }
      } else if (price <= 0) {
        price = impliedGross / qty;
      }
    }

    const pid = raw.product_id != null ? Number(raw.product_id) : NaN;
    const foreignAmounts = readSoldItemForeignFromApi(raw);
    const hasForeign = hasSoldItemForeignAmounts(raw);

    return {
      index: index + 1,
      catalog_id: raw.catalog_id != null ? String(raw.catalog_id) : undefined,
      category: raw.category ?? undefined,
      measurement_unit_id:
        raw.measurement_unit_id != null ? Number(raw.measurement_unit_id) : undefined,
      description: raw.description ?? '',
      item_name: (raw.item_name || raw.description || '').toString(),
      measure_unit_description: raw.measure_unit_description ?? '',
      quantity: qty > 0 ? qty : 1,
      price_per_unit: Math.round(price * 100) / 100,
      sub_total: subTotal,
      discount,
      total_amount: Number(raw.total_amount ?? 0),
      vat_rate: Number(raw.vat_rate ?? this.vatPercentage ?? 17),
      vat_amount: Number(raw.vat_amount ?? 0),
      product_id: Number.isFinite(pid) ? pid : 0,
      isPresenting: !!raw.isPresenting,
      isVatIncluded: !!(raw.isVatIncluded ?? raw.is_vat_included),
      ...(hasForeign
        ? {
            price_per_unit_foreign: foreignAmounts.price_per_unit_foreign ?? price,
            discount_foreign: foreignAmounts.discount_foreign ?? discount,
            sub_total_foreign: foreignAmounts.sub_total_foreign ?? subTotal,
            vat_amount_foreign: foreignAmounts.vat_amount_foreign ?? Number(raw.vat_amount ?? 0),
            total_amount_foreign: foreignAmounts.total_amount_foreign ?? Number(raw.total_amount ?? 0),
          }
        : {}),
    };
  }

  /** Human-readable label for merge audit note: localized type name + document # (or #id). */
  private getMergeSourceDocumentDisplayLabel(inv: any): string {
    const typeCode = inv?.invoice_type != null ? +inv.invoice_type : NaN;
    const typeLabel = Number.isFinite(typeCode) ? this.getInvoiceTypeLabel(typeCode) : '';
    const dn = inv?.document_number != null ? String(inv.document_number).trim() : '';
    const idPart = dn || (inv?.invoice_id != null ? `#${inv.invoice_id}` : '?');
    return typeLabel ? `${typeLabel} ${idPart}` : idPart;
  }

  private buildFrontendMergeInvoiceNote(sourceInvoices: any[]): string {
    const labels = sourceInvoices.map((inv) => this.getMergeSourceDocumentDisplayLabel(inv));
    const joined = labels.join(', ');
    const prev = (this.invoiceForm.get('invoice_note')?.value || '').trim();
    let suffix: string;
    if (this.curentLang === 'ar') {
      suffix = `[دمج من المستندات: ${joined}]`;
    } else if (this.curentLang === 'he') {
      suffix = `[מוזג מהמסמכים: ${joined}]`;
    } else {
      suffix = `[Merged from documents: ${joined}]`;
    }
    return prev ? `${prev}\n${suffix}` : suffix;
  }

  /**
   * Compose create-form draft from merged invoices (same idea as bulk-convert: concat lines + totals),
   * without calling POST /bulk-convert — user saves via normal create.
   */
  private applyFrontendMergeFromInvoices(invoices: any[], targetType: number): void {
    const sorted = [...invoices].sort((a, b) => {
      const da = String(a.invoice_date || '');
      const db = String(b.invoice_date || '');
      const cmp = da.localeCompare(db);
      if (cmp !== 0) {
        return cmp;
      }
      return (+a.invoice_id || 0) - (+b.invoice_id || 0);
    });

    const targetMeta =
      this.invoiceTypes.find((t) => +t.id === +targetType) ||
      this.allInvoiceTypes.find((t) => +t.id === +targetType);
    const targetHasItems = targetMeta
      ? !(targetMeta.has_items === false || targetMeta.has_items === 0)
      : true;
    const targetNeedsPayments = targetMeta
      ? targetMeta.has_payments === true || targetMeta.has_payments === 1
      : false;

    const mergedItemsRaw = sorted.flatMap((inv) => inv.soldItems || []);
    if (targetHasItems && mergedItemsRaw.length === 0) {
      throw new Error('NO_MERGE_ITEMS');
    }

    let latestDate = new Date();
    const formDateVal = this.invoiceForm.get('invoice_date')?.value;
    if (formDateVal) {
      const parsed = formDateVal instanceof Date ? formDateVal : new Date(formDateVal);
      if (!isNaN(parsed.getTime())) {
        latestDate = parsed;
      }
    }
    for (const inv of sorted) {
      if (!inv.invoice_date) {
        continue;
      }
      const d = new Date(inv.invoice_date);
      if (!isNaN(d.getTime()) && d > latestDate) {
        latestDate = d;
      }
    }

    const dtoRows: CreateSoldItemV2Dto[] = mergedItemsRaw.map((raw, idx) =>
      this.mapApiSoldItemToDto(raw, idx),
    );

    this.isPopulatingForm = true;
    this.invoiceForm.patchValue(
      { invoice_type: targetType, invoice_date: latestDate },
      { emitEvent: false },
    );
    this.onInvoiceTypeChange(targetType);

    while (this.items.length > 0) {
      this.items.removeAt(0);
    }
    dtoRows.forEach((row) => this.addItem(row));

    this.paymentMethods.clear();
    if (targetNeedsPayments) {
      let lineId = 1;
      sorted.forEach((inv) => {
        const receipts = inv.receiptsDetails || [];
        receipts.forEach((receipt: any) => {
          const payment: PaymentMethodDto = {
            type: receipt.payment_type || PaymentType.CASH,
            amount: Number(receipt.amount ?? 0),
            details: {
              date:
                receipt.date ||
                inv.invoice_date ||
                latestDate,
              reference: receipt.reference,
              bank: receipt.bank,
              branch: receipt.branch,
              account: receipt.account,
              details: receipt.details,
              credit_card_code: receipt.credit_card_code,
              credit_card_name: receipt.credit_card_name,
              credit_type:
                receipt.credit_type != null ? String(receipt.credit_type) : undefined,
              account_number: receipt.account_number,
              line_id: lineId++,
            },
          };
          const idx = this.paymentMethods.length;
          const paymentForm = this.createPaymentMethodFormGroup(payment);
          this.paymentMethods.push(paymentForm);
          this.setupPaymentMethodListeners(idx);
        });
      });
      if (this.paymentMethods.length === 0) {
        this.addPaymentMethod();
      }
    }

    const note = this.buildFrontendMergeInvoiceNote(sorted);
    this.invoiceForm.patchValue({ invoice_note: note }, { emitEvent: false });

    this.syncPaymentMethodValidatorsAfterTypeOrCopy();
    this.applyPaymentValidationForConfirmState();
    this.recalculateAllItemsAndTotals();
    this.refreshYearlyMaxAmount();

    const cid = this.invoiceForm.get('company_id')?.value;
    if (cid) {
      this.predictDocumentNumber(+cid);
    }

    setTimeout(() => {
      this.isPopulatingForm = false;
      this.initYearlyCapThresholdTracking();
    }, 450);
  }

  executeCreateBulkMerge(): void {
    if (this.createMergeSelectedIds.length < 1) {
      this.toastr.warning(
        this.translate.instant('invoices_v2.form.create_merge_need_one'),
      );
      return;
    }
    const types = new Set<number>();
    for (const id of this.createMergeSelectedIds) {
      const row = this.createMergeInvoices.find((i) => this.getCreateMergeRowId(i) === id);
      if (!row) {
        this.toastr.warning(
          this.curentLang === 'ar'
            ? 'اختيار غير صالح'
            : this.curentLang === 'he'
              ? 'בחירה לא חוקית'
              : 'Invalid selection',
        );
        return;
      }
      types.add(+row.invoice_type);
    }

    const targetType = this.resolveMergeTargetTypeFromSourceTypes([...types]);
    if (targetType == null) {
      this.toastr.error(
        this.translate.instant('invoices_v2.form.create_merge_mixed_incompatible'),
      );
      return;
    }
    if (!CREATE_MERGE_ALLOWED_TARGET_INVOICE_TYPES.has(+targetType)) {
      this.toastr.error(
        this.curentLang === 'ar'
          ? 'الدمج متاح فقط نحو فاتورة ضريبية أو فاتورة وإيصال وفق إعدادات الأنواع.'
          : this.curentLang === 'he'
            ? 'מיזוג זמין רק לחשבונית מס או חשבונית מס/קבלה לפי הגדרות הסוגים.'
            : 'Merge is only allowed into a tax invoice or invoice-with-receipt per type settings.',
      );
      return;
    }

    const companyId = this.invoiceForm.get('company_id')?.value;
    if (!this.isInvoiceTypeAllowedForCompany(companyId != null ? +companyId : null, targetType)) {
      this.toastr.error(
        this.curentLang === 'ar'
          ? 'نوع المستند الناتج غير مسموح لهذا النوع من المصلحة التجارية'
          : this.curentLang === 'he'
            ? 'סוג המסמך הנוצר אינו מותר לסוג החברה'
            : 'The resulting document type is not allowed for this company type',
      );
      return;
    }

    const n = this.createMergeSelectedIds.length;
    Swal.fire({
      title: this.translate.instant('invoices_v2.form.create_merge_confirm_title'),
      text: this.translate.instant('invoices_v2.form.create_merge_confirm_text', { count: n }),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#17a2b8',
      cancelButtonColor: '#6c757d',
      confirmButtonText: this.translate.instant('invoices_v2.form.create_merge_confirm_ok'),
      cancelButtonText: this.translate.instant('invoices_v2.common.cancel'),
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      const ids = [...this.createMergeSelectedIds];
      this.closeCreateMergeModal();
      this.spinner.show();
      forkJoin(ids.map((id) => this.invoiceV2Service.getInvoiceById(id))).subscribe({
        next: (responses) => {
          const invoices = responses.map((r) => this.unwrapInvoiceResponse(r));
          const formCompany = +this.invoiceForm.get('company_id')?.value;
          const formCustomer = +this.invoiceForm.get('customer_id')?.value;

          for (const inv of invoices) {
            if (!inv || inv.invoice_id == null) {
              this.spinner.hide();
              this.toastr.error(
                this.curentLang === 'ar'
                  ? 'تعذر تحميل أحد المستندات'
                  : this.curentLang === 'he'
                    ? 'לא ניתן לטעון אחד המסמכים'
                    : 'Could not load one of the documents',
              );
              return;
            }
            if (+inv.company_id !== formCompany || +inv.customer_id !== formCustomer) {
              this.spinner.hide();
              this.toastr.error(
                this.curentLang === 'ar'
                  ? 'يجب أن تطابق كل المستندات المصلحة التجارية والزبون المحددين في النموذج'
                  : this.curentLang === 'he'
                    ? 'כל המסמכים חייבים להתאים לחברה וללקוח שנבחרו בטופס'
                    : 'All documents must match the company and customer selected on this form',
              );
              return;
            }
            if (!types.has(+inv.invoice_type)) {
              this.spinner.hide();
              this.toastr.error(
                this.curentLang === 'ar'
                  ? 'تعارض في نوع المستند'
                  : this.curentLang === 'he'
                    ? 'סוג המסמך אינו תואם'
                    : 'Document type mismatch',
              );
              return;
            }
            if (!CREATE_MERGE_ALLOWED_SOURCE_INVOICE_TYPES.has(+inv.invoice_type)) {
              this.spinner.hide();
              this.toastr.error(
                this.curentLang === 'ar'
                  ? 'يُسمح بدمج الطلبيات والإرساليات والمطالبات المالية فقط.'
                  : this.curentLang === 'he'
                    ? 'ניתן למזג רק מסמכי הזמנה, משלוח ודרישות תשלום.'
                    : 'Only orders, delivery notes, and financial requests can be merged as sources.',
              );
              return;
            }
            if (+inv.confirm !== 1 && inv.confirm !== true) {
              this.spinner.hide();
              this.toastr.error(
                this.curentLang === 'ar'
                  ? 'يجب أن تكون كل المستندات مؤكدة'
                  : this.curentLang === 'he'
                    ? 'כל המסמכים חייבים להיות מאושרים'
                    : 'All documents must be confirmed',
              );
              return;
            }
            if (this.hasMergedInvoiceReference(inv)) {
              this.spinner.hide();
              this.toastr.error(
                this.curentLang === 'ar'
                  ? 'أحد المستندات مرتبط بفاتورة أخرى (مدمج مسبقاً)'
                  : this.curentLang === 'he'
                    ? 'אחד המסמכים כבר משויך לחשבונית אחרת (מוזג)'
                    : 'One document is already linked to another invoice (merged)',
              );
              return;
            }
            if (this.isInvoiceMarkedMergedDb(inv)) {
              this.spinner.hide();
              this.toastr.error(
                this.curentLang === 'ar'
                  ? 'أحد المستندات معلّم كمدمج مسبقاً'
                  : this.curentLang === 'he'
                    ? 'אחד המסמכים מסומן כמוזג כבר'
                    : 'One document is already marked as merged',
              );
              return;
            }
          }

          try {
            this.applyFrontendMergeFromInvoices(invoices, targetType);
            this.invoiceV2Service.markInvoicesMerged(ids, true).subscribe({
              next: () => {
                this.refreshCreateMergeEligibilityNow();
                this.spinner.hide();
                this.toastr.success(
                  this.curentLang === 'ar'
                    ? 'تم دمج البنود في المسودة. راجع البيانات ثم احفظ.'
                    : this.curentLang === 'he'
                      ? 'השורות מוזגו לטיוטה. יש לבדוק ולשמור.'
                      : 'Lines merged into the draft. Review and save.',
                );
              },
              error: () => {
                this.refreshCreateMergeEligibilityNow();
                this.spinner.hide();
                this.toastr.warning(
                  this.curentLang === 'ar'
                    ? 'تم دمج البنود، لكن تعذر تحديث حالة «مدمج» على المستندات.'
                    : this.curentLang === 'he'
                      ? 'השורות מוזגו, אך לא ניתן היה לעדכן את סימון «מוזג» בשרת.'
                      : 'Lines were merged, but the merged flag could not be updated on the server.',
                );
                this.toastr.success(
                  this.curentLang === 'ar'
                    ? 'تم دمج البنود في المسودة. راجع البيانات ثم احفظ.'
                    : this.curentLang === 'he'
                      ? 'השורות מוזגו לטיוטה. יש לבדוק ולשמור.'
                      : 'Lines merged into the draft. Review and save.',
                );
              },
            });
          } catch (e: any) {
            console.error(e);
            this.spinner.hide();
            if (e?.message === 'NO_MERGE_ITEMS') {
              this.toastr.error(
                this.curentLang === 'ar'
                  ? 'لا توجد بنود لدمجها في المستندات المحددة'
                  : this.curentLang === 'he'
                    ? 'אין שורות למיזוג במסמכים שנבחרו'
                    : 'No line items to merge in the selected documents',
              );
            } else {
              this.toastr.error(
                this.curentLang === 'ar'
                  ? 'فشل دمج البنود'
                  : this.curentLang === 'he'
                    ? 'מיזוג השורות נכשל'
                    : 'Failed to merge lines',
              );
            }
          }
        },
        error: (err) => {
          this.spinner.hide();
          this.toastr.error(this.getApiErrorMessage(err, 'Merge failed'));
        },
      });
    });
  }

  /**
   * Get payment type label from API data
   */
  getPaymentTypeLabel(type: number): string {
    const paymentMethod = this.paymentMethodsList.find((pm) => +pm.payment_method_code === +type);
    if (paymentMethod) {
      if (this.curentLang === 'ar') return paymentMethod.payment_method_name_ar;
      if (this.curentLang === 'he') return paymentMethod.payment_method_name_he;
      return paymentMethod.payment_method_name_en;
    }
    // Fallback to hardcoded labels if API data not loaded yet
    const labels: { [key: number]: { ar: string; he: string; en: string } } = {
      1: { ar: 'نقد', he: 'מזומן', en: 'Cash' },
      2: { ar: 'شيك', he: 'צ׳ק', en: 'Check' },
      3: { ar: 'بطاقة ائتمان', he: 'כרטיס אשראי', en: 'Credit Card' },
      4: { ar: 'تحويل بنكي', he: 'העברה בנקאית', en: 'Bank Transfer' },
      5: { ar: 'خصم من المصدر', he: 'ניכוי במקור', en: 'Deduction from Source' },
    };
    const label = labels[type] || { ar: '', he: '', en: '' };
    return this.curentLang === 'ar' ? label.ar : this.curentLang === 'he' ? label.he : label.en;
  }

  /**
   * Get payment method icon class from API data (fallback to defaults)
   */
  getPaymentMethodIcon(type: number): string {
    const paymentMethod = this.paymentMethodsList.find((pm) => +pm.payment_method_code === +type);
    const iconKey = paymentMethod?.icon;

    switch (iconKey) {
      case 'cash':
        return 'fa-money';
      case 'check':
        return 'fa-file-text-o';
      case 'credit_card':
        return 'fa-credit-card';
      case 'bank_transfer':
        return 'fa-bank';
      case 'withholding':
        return 'fa-percent';
      default:
        // Fallback based on type code if icon not provided
        if (type === 1) return 'fa-money';
        if (type === 2) return 'fa-file-text-o';
        if (type === 3) return 'fa-credit-card';
        if (type === 4) return 'fa-bank';
        return 'fa-credit-card';
    }
  }

  /**
   * Get required fields for a payment method
   */
  getRequiredFieldsForPaymentType(type: number): string[] {
    const paymentMethod = this.paymentMethodsList.find((pm) => +pm.payment_method_code === +type);
    let fields = paymentMethod?.required_fields || [];
    if (typeof fields === 'string') {
      try {
        fields = JSON.parse(fields);
      } catch {
        fields = [];
      }
    }
    return Array.isArray(fields) ? fields : [];
  }

  /**
   * Check if a field is required for a payment method
   */
  isFieldRequired(paymentType: number, fieldName: string): boolean {
    if (['bank', 'branch', 'account'].includes(fieldName)) {
      return false;
    }
    const requiredFields = this.getRequiredFieldsForPaymentType(paymentType);
    return requiredFields.includes(fieldName);
  }

  /**
   * Show bank / branch / account when API marks them for this payment type (e.g. transfer / check),
   * even though they are not validated as mandatory.
   */
  isBankDetailFieldVisible(paymentType: number, fieldName: 'bank' | 'branch' | 'account'): boolean {
    const requiredFields = this.getRequiredFieldsForPaymentType(paymentType);
    if (requiredFields.includes(fieldName)) {
      return true;
    }
    const type = +paymentType;
    if (type === PaymentType.CHECK || type === PaymentType.BANK_TRANSFER) {
      return fieldName === 'bank' || fieldName === 'branch' || fieldName === 'account';
    }
    return false;
  }

  /**
   * Check if payment method requires details
   */
  requiresDetails(paymentType: number): boolean {
    const paymentMethod = this.paymentMethodsList.find((pm) => +pm.payment_method_code === +paymentType);
    return paymentMethod?.requires_details || false;
  }

  /**
   * Company label for the current language, with a fallback when that language is empty.
   */
  getCompanyName(company: any): string {
    if (!company) return '';
    return (
      companyDisplayName(company, this.curentLang) ||
      companyDisplayName(company.fullData, this.curentLang)
    );
  }

  /**
   * Get customer name for display
   */
  getCustomerName(customer: any): string {
    if (!customer) return '';
    if (customer.customer_name) return customer.customer_name;
    if (this.curentLang === 'ar') return customer.customer_name_ar || customer.customer_name_en || '';
    if (this.curentLang === 'he') return customer.customer_name_he || customer.customer_name_en || '';
    return customer.customer_name_en || '';
  }

  /**
   * Get customer worker number (مشتغل رقم) for display – VAT number or ID number
   */
  getCustomerWorkerNumber(customer: any): string {
    if (!customer) return '';
    return (
      customer.customer_vat_number ||
      customer.id_number ||
      customer.customer_id_number ||
      ''
    ).toString().trim();
  }

  /**
   * Debug: bank dropdown open/close (intermittent first-click issues).
   */
  onBankDropdownOpen(index: number): void {
    const payment = this.paymentMethods.at(index);
    }

  onBankDropdownClose(index: number): void {
    }

  /**
   * Custom search for bank ng-select across localized names and codes.
   */
  bankSearchFn = (term: string, item: any): boolean => {
    if (!term || !item) return true;
    const t = term.toLowerCase().trim();
    const label = String(item.__bankSelectLabel || '').toLowerCase();
    if (label.includes(t)) return true;
    const names = [
      item.bank_name_ar,
      item.bank_name_en,
      item.bank_name_he,
      item.bank_code,
      item.bank_id,
    ]
      .filter((v) => v != null && v !== '')
      .map((v) => String(v).toLowerCase());
    return names.some((name) => name.includes(t));
  };

  /**
   * Local product filter. Names, SKU, and barcode — no API call.
   */
  productSearchFn = (term: string, item: any): boolean => {
    if (!term || !item) return true;
    const t = term.toLowerCase().trim();
    const names = [
      item.product_name_ar,
      item.product_name_en,
      item.product_name_he,
      item.product_code,
      item.catalog_id,
      item.barcode,
    ]
      .filter((value) => value != null && String(value).trim() !== '')
      .map((value) => String(value).toLowerCase());
    return names.some((name) => name.includes(t));
  };

  /**
   * Custom search for customer ng-select: by name, رقم المشتغل (worker number), custom text, custom account
   */
  customerSearchFn = (term: string, item: any): boolean => {
    if (!term || !item) return true;
    const t = term.toLowerCase().trim();
    const name = (this.getCustomerName(item) || '').toLowerCase();
    const workerNumber = (this.getCustomerWorkerNumber(item) || '').toLowerCase();
    const customText = (item.custom_text || '').toString().toLowerCase();
    const customAccount = (item.custom_account || item.account_number || '').toString().toLowerCase();
    return (
      name.includes(t) ||
      workerNumber.includes(t) ||
      customText.includes(t) ||
      customAccount.includes(t)
    );
  };

  /**
   * Load branches for a given bank code and cache them
   */
  loadBranchesForBank(bankCode: string): void {
    // If already loaded, do nothing
    if (this.bankBranchesByCode[bankCode]) {
      return;
    }

    // Find bank by code to get its ID
    const bank = this.banks.find((b) => b.bank_code?.toString() === bankCode?.toString());
    if (!bank || !bank.bank_id) {
      return;
    }

    this.httpHelpService.getAllBranchById(bank.bank_id).subscribe({
      next: (response: any) => {
        let branchesData: any[] = [];
        if (Array.isArray(response)) {
          branchesData = response;
        } else if (response && Array.isArray(response.data)) {
          branchesData = response.data;
        }

        // Enrich rows for ng-select: show code like banks, searchable by name + number
        this.bankBranchesByCode[bankCode] = branchesData.map((br) =>
          this.decorateBranchForSelect(br),
        );
      },
      error: (error) => {
        console.error('Error loading bank branches:', error);
      },
    });
  }

  /**
   * Get branches list for payment row (by index)
   */
  getBranchesForPayment(index: number): any[] {
    const payment = this.paymentMethods.at(index);
    const bankRaw = (payment.get('details') as FormGroup)?.get('bank')?.value;
    if (!bankRaw && bankRaw !== 0) return [];
    const bankCode = String(bankRaw);
    let rows = this.bankBranchesByCode[bankCode] || [];
    if (rows.length > 0 && rows[0].__branchSelectLabel == null) {
      rows = rows.map((br) => this.decorateBranchForSelect(br));
      this.bankBranchesByCode[bankCode] = rows;
    }
    return rows;
  }

  /** ng-select: match `bank_code` when API uses number vs string. */
  compareBankCode = (a: unknown, b: unknown): boolean => {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    return String(a) === String(b);
  };

  /** Same as bank row: branch code may be string or number from API / form */
  compareBranchCode = (a: unknown, b: unknown): boolean => {
    return this.compareBankCode(a, b);
  };

  /**
   * Search branch by localized name(s) or branch number (aligned with bank UX).
   */
  branchSelectSearchFn = (term: string, item: any): boolean => {
    if (!term || !item) return true;
    const t = term.toLowerCase().trim();
    const code = this.getBranchCodeRaw(item).toLowerCase();
    if (code.includes(t)) return true;
    const label = String(item.__branchSelectLabel || '').toLowerCase();
    if (label.includes(t)) return true;
    const fields = [
      item.branch_name_ar,
      item.bank_branch_name_ar,
      item.branch_name_en,
      item.bank_branch_name_en,
      item.branch_name_he,
      item.bank_branch_name_he,
    ];
    return fields.some((f) => String(f || '').toLowerCase().includes(t));
  };

  /** Branch code for API row (display + form value) */
  getBranchCodeRaw(br: any): string {
    if (!br) return '';
    const c = br.branch_code ?? br.bank_branch_code ?? br.branch_id;
    return c != null && c !== '' ? String(c) : '';
  }

  /**
   * Localized branch name only (without code) — receipts / invoice+receipt payment row
   */
  getBranchDisplayName(br: any): string {
    if (!br) return '';
    if (this.curentLang === 'ar') {
      return (
        br.branch_name_ar ||
        br.bank_branch_name_ar ||
        br.branch_name_en ||
        br.bank_branch_name_en ||
        br.branch_name_he ||
        br.bank_branch_name_he ||
        this.getBranchCodeRaw(br)
      );
    }
    if (this.curentLang === 'he') {
      return (
        br.branch_name_he ||
        br.bank_branch_name_he ||
        br.branch_name_en ||
        br.bank_branch_name_en ||
        br.branch_name_ar ||
        br.bank_branch_name_ar ||
        this.getBranchCodeRaw(br)
      );
    }
    return (
      br.branch_name_en ||
      br.bank_branch_name_en ||
      br.branch_name_ar ||
      br.bank_branch_name_ar ||
      br.branch_name_he ||
      br.bank_branch_name_he ||
      this.getBranchCodeRaw(br)
    );
  }

  private decorateBranchForSelect(br: any): any {
    const code = this.getBranchCodeRaw(br);
    const name = this.getBranchDisplayName(br);
    const label = code ? `${name} (${code})` : name || code;
    return {
      ...br,
      __branchFormValue: code,
      __branchSelectLabel: label,
    };
  }

  /**
   * Get bank display name based on current language
   */
  getBankName(bank: any): string {
    if (!bank) return '';
    if (this.curentLang === 'ar') return bank.bank_name_ar || bank.bank_name_en || '';
    if (this.curentLang === 'he') return bank.bank_name_he || bank.bank_name_en || '';
    return bank.bank_name_en || '';
  }

  /** Bank label with code — aligned with receipts / branch select UX */
  getBankLabel(bank: any): string {
    if (!bank) return '';
    const name = this.getBankName(bank);
    const code =
      bank.bank_code != null && bank.bank_code !== '' ? String(bank.bank_code) : '';
    return code ? `${code} - ${name}` : name;
  }

  private decorateBankForSelect(bank: any): any {
    return {
      ...bank,
      __bankSelectLabel: this.getBankLabel(bank),
    };
  }
}

